import { useQuery, useQueryClient } from "@tanstack/react-query"
import { useVirtualizer } from "@tanstack/react-virtual"
import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react"

/** Context to share GroupNode UUID→name lookup across all pivot sub-components */
const GroupNodeNamesCtx = createContext<Map<string, string>>(new Map())

/** Context to share transitive relation counts (multi-hop) across pivot rows */
const TransitiveCountsCtx = createContext<TransitiveCounts | null>(null)

/** Context: scope id → tab display order index. Used to sort relation columns to match tab order. */
const ScopeOrderCtx = createContext<Map<string, number>>(new Map())

type RelationSlot =
  | {
      kind: "direct"
      prim: import("../../types").PrimitiveDef
      sortKey: number
    }
  | {
      kind: "transitive"
      ts: import("../../types").TransitiveScopeInfo
      sortKey: number
    }

function buildRelationSlots(
  relationPrims: import("../../types").PrimitiveDef[],
  transitiveScopes: import("../../types").TransitiveScopeInfo[] | undefined,
  scopeOrderIndex: Map<string, number>,
): RelationSlot[] {
  const slots: RelationSlot[] = []
  for (const p of relationPrims) {
    const sortKey = p.related_scope_id
      ? (scopeOrderIndex.get(p.related_scope_id) ?? Infinity)
      : Infinity
    slots.push({ kind: "direct", prim: p, sortKey })
  }
  for (const ts of transitiveScopes ?? []) {
    const sortKey = scopeOrderIndex.get(ts.scope_id) ?? Infinity
    slots.push({ kind: "transitive", ts, sortKey })
  }
  return slots.sort((a, b) => a.sortKey - b.sortKey)
}

import {
  useTranslation,
  type TranslationKeys,
} from "@/hooks/useTranslation/useTranslation"
import {
  DndContext,
  PointerSensor,
  closestCenter,
  useSensor,
  useSensors,
  type DragEndEvent,
} from "@dnd-kit/core"
import {
  SortableContext,
  arrayMove,
  horizontalListSortingStrategy,
  useSortable,
} from "@dnd-kit/sortable"
import { CSS } from "@dnd-kit/utilities"
import clsx from "clsx"
import { api } from "../../api/client"
import { useStore } from "../../store/useStore"
import type {
  DiffCell,
  DiffRecord,
  DiffState,
  ExportFormat,
  GroupNode,
  GroupTree,
  HistoryEntry,
  PrimitiveDef,
  PrimitiveType,
  PrimitiveValue,
  QmsRecord,
  RelationDiff,
  RelationPreview,
  RollupColumnDef,
  Scope,
  ScopeDiffState,
  TransitiveCounts,
} from "../../types"
import { isConformityFormat } from "../../types"
import { evaluateFormula, type TranslateFn } from "../../utils/formula"
import { getFreshnessStatus } from "../../utils/freshness"
import {
  getPrimitiveDisplayTitle,
  getScopeDisplayName,
} from "../config/scopeIcons"
import {
  generateConformityPdf,
  generateEschaKundenKonformitaetPdf,
  generateNegativeText,
  generatePositiveText,
  generateProductConformityPdf,
  generateProductNegativeText,
  generateProductPositiveText,
  generateRegulatoryNotes,
  type ConformityFormData,
  type EschaKundenTableRow,
  type SvhcTableRow,
} from "../conformity/conformityPdf"
import type { HistoryMode } from "../diff/CommitModeDialog"
import { DiffCellComponent } from "../diff/DiffCell"
import { DiffToolbar } from "../diff/DiffToolbar"
import { FreshnessDatePicker } from "../FreshnessDatePicker"
import { PivotCell } from "./PivotCell"
import { RelationTag } from "./RelationTag"

/** Format an ISO timestamp as a relative time string (e.g. "3d ago", "2w ago") */
function formatRelativeTime(
  iso: string,
  translate: (
    key: TranslationKeys,
    params?: Record<string, string | number>,
  ) => string,
): string {
  const diff = Date.now() - new Date(iso).getTime()
  const mins = Math.floor(diff / 60_000)
  if (mins < 1) return translate("QMS/COMMON/JUST_NOW")
  if (mins < 60) return translate("QMS/COMMON/MINS_AGO", { count: mins })
  const hrs = Math.floor(mins / 60)
  if (hrs < 24) return translate("QMS/COMMON/HOURS_AGO", { count: hrs })
  const days = Math.floor(hrs / 24)
  if (days < 14) return translate("QMS/COMMON/DAYS_AGO", { count: days })
  const weeks = Math.floor(days / 7)
  if (weeks < 8) return translate("QMS/COMMON/WEEKS_AGO", { count: weeks })
  const months = Math.floor(days / 30)
  return translate("QMS/COMMON/MONTHS_AGO", { count: months })
}

/**
 * Build secondary diff state from existing DB records for a related scope.
 *
 * Row inclusion + row-freshness acceptance are driven by the caller-supplied
 * ``effectiveStaleIds`` set. The set encodes the directional-cascade rule
 * (supplier → substance → component → product → customer): a record is in the set
 * iff its own updated_at OR any upstream (lower-rank) reachable record is
 * stale. Callers are expected to pre-fetch this via /scopes/effective-staleness.
 *
 * Primitives whose IDs are in ``mappedPrimitiveIds`` become primary diff cells
 * (actual value changes for export). All other cells are omitted - no per-cell
 * secondary diffs. In export mode mapped primary cells use fileValue=null.
 */
function buildSecondaryDiffState(
  scopeId: string,
  scope: Scope,
  records: QmsRecord[],
  effectiveStaleIds: Set<string>,
  freshnessFilterActive: boolean,
  mappedPrimitiveIds?: Set<string>,
  exportMode?: boolean,
): ScopeDiffState {
  const pkPrim = scope.primitives.find((p) => p.is_pk)
  const namePrim = scope.primitives.find(
    (p) => !p.is_pk && p.type === "string" && p.is_visible,
  )
  const visiblePrims = scope.primitives.filter(
    (p) =>
      p.is_visible && !p.is_pk && p.type !== "relation" && p.type !== "formula",
  )

  const diffRecords: Record<string, DiffRecord> = {}
  for (const rec of records) {
    const cells: Record<string, DiffCell> = {}
    const isStale = effectiveStaleIds.has(rec.id)

    // Build primary diff cells for mapped primitives (actual export values)
    if (mappedPrimitiveIds && mappedPrimitiveIds.size > 0) {
      for (const prim of visiblePrims) {
        if (!mappedPrimitiveIds.has(prim.id)) continue
        const pv = rec.values[prim.name]
        const val = pv?.value ?? null
        cells[prim.id] = {
          recordId: rec.id,
          primitiveId: prim.id,
          primitiveName: prim.name,
          primitiveTitle: prim.title,
          primitiveType: prim.type as PrimitiveType,
          dbValue: val,
          fileValue: exportMode ? null : val,
          rawFileValue: null,
          typeIncompatible: false,
          accepted: null, // Primary cells always need manual acceptance
        }
      }
    }

    // Inclusion rule:
    //  - freshness filter OFF: show only records with primary cells (existing behavior
    //    when there's nothing row-level to confirm).
    //  - freshness filter ON: show every "involved" record so the user sees full
    //    context. The filter then flips rowFreshnessAccepted (null = yellow/needs
    //    confirm, true = auto-accepted fresh row).
    const hasPrimaryCells = Object.keys(cells).length > 0
    if (!freshnessFilterActive && !hasPrimaryCells) continue

    diffRecords[rec.id] = {
      recordId: rec.id,
      pkValue: pkPrim ? (rec.values[pkPrim.name]?.value ?? rec.id) : rec.id,
      nameValue: namePrim ? (rec.values[namePrim.name]?.value ?? null) : null,
      isNew: false,
      cells,
      rowUpdatedAt: rec.updated_at,
      rowUpdatedSource: rec.updated_source,
      // When the filter is off we intentionally omit rowFreshnessAccepted so
      // it doesn't count as a reviewable item in progress math.
      ...(freshnessFilterActive
        ? { rowFreshnessAccepted: isStale ? null : true }
        : {}),
    }
  }
  return {
    scopeId,
    fileName: "(Kontext)",
    records: diffRecords,
    rawExtractions: [],
    anchoredRecords: [],
    secondaryDiff: true,
  }
}

/** Build an edited CSV from original parsed rows and download it */
async function handleExportDownload(ds: DiffState, translate: TranslateFn) {
  // Format-based export (from ExportConfigModal → diff review)
  if (ds.exportColumns && ds.exportRecordIds) {
    // Conformity letter: generate client-side PDF via conformityPdf
    if (isConformityFormat(ds.targetFormat)) {
      await handleConformityPdfDownload(ds, translate)
      return
    }

    // Multi-scope export: when secondary scope diffs exist, build CSV client-side
    // by driving rows from the leaf scope and repeating primary values.
    const allDiffs = useStore.getState().actionState?.diffs ?? {}
    const secondaryScopeIds = Object.keys(allDiffs).filter(
      (sid) => sid !== ds.scopeId,
    )
    const hasSecondaryData = secondaryScopeIds.some((sid) => {
      const sd = allDiffs[sid]
      return sd && !sd.secondaryDiff && Object.keys(sd.records).length > 0
    })

    if (hasSecondaryData) {
      // Find the leaf scope - the secondary scope whose records drive the CSV rows
      const leafScopeId = secondaryScopeIds.find((sid) => {
        const sd = allDiffs[sid]
        return sd && !sd.secondaryDiff && Object.keys(sd.records).length > 0
      })
      if (leafScopeId) {
        const leafDiff = allDiffs[leafScopeId]
        const primaryDiff = allDiffs[ds.scopeId] ?? ds

        // Resolve a cell's export value per the diff-view contract:
        //   dbValue   = DB value (default export).
        //   fileValue = document value (user's override, edited on the left).
        // Output is dbValue unless the user edited fileValue to something
        // different from the original doc snapshot (rawFileValue).
        const cellVal = (cell: DiffCell | undefined): string => {
          if (!cell) return ""
          if (cell.declined) return ""
          const edited =
            cell.fileValue != null && cell.fileValue !== cell.rawFileValue
          if (edited) return cell.fileValue!
          return cell.dbValue ?? ""
        }

        // Use the primary diff's exportColumns - it has ALL columns in the user's order
        // (columns from primary scope + secondary scope + rollups).
        // Relations are already excluded by ColumnMappingStep.proceed().
        const orderedColumns = ds.exportColumns!
        const headers = orderedColumns.map((c) => c.label)

        // Build primary scope value lookup: PK value(s) and cell values
        const primaryRecords = Object.values(primaryDiff.records)
        const primaryCellLookup: Record<string, string> = {}
        for (const rec of primaryRecords) {
          for (const cell of Object.values(rec.cells)) {
            primaryCellLookup[cell.primitiveId] = cellVal(cell)
          }
        }
        // Primary PK: use pkValue from the first primary record (repeated on every row)
        const primaryPkValue = primaryRecords[0]?.pkValue ?? ""

        // Build data rows: one row per leaf-scope record
        const dataRows: string[][] = []
        for (const rec of Object.values(leafDiff.records)) {
          const row: string[] = []
          for (const col of orderedColumns) {
            if (col.scope_id === ds.scopeId) {
              // Primary scope column: repeat value for each leaf row
              const val = primaryCellLookup[col.primitive_id]
              row.push(val ?? primaryPkValue)
            } else {
              // Leaf scope column (own value or rollup): from leaf diff record cells.
              // PK columns are not stored in cells (skipped during diff building),
              // so fall back to rec.pkValue.
              const cell = rec.cells[col.primitive_id]
              row.push(cell ? cellVal(cell) : (rec.pkValue ?? ""))
            }
          }
          dataRows.push(row)
        }

        // Generate CSV with BOM for Excel compatibility
        const csvContent = [
          headers.map((h) => `"${h.replace(/"/g, '""')}"`).join(","),
          ...dataRows.map((row) =>
            row.map((v) => `"${(v ?? "").replace(/"/g, '""')}"`).join(","),
          ),
        ].join("\r\n")
        const blob = new Blob(
          [new Uint8Array([0xef, 0xbb, 0xbf]), csvContent],
          { type: "text/csv;charset=utf-8" },
        )
        triggerBlobDownload(blob, `${ds.scopeId}_export.csv`)
        return
      }
    }

    // Single-scope export: backend emits dbValue by default for every
    // column.  Only override when the user edited fileValue (the document-
    // side value) away from the original doc snapshot, OR explicitly
    // declined - in which case we send an empty string so the backend
    // omits the cell.
    const overrides: Record<string, Record<string, string>> = {}
    for (const [recordId, rec] of Object.entries(ds.records)) {
      for (const [, cell] of Object.entries(rec.cells)) {
        if (cell.declined) {
          if (!overrides[recordId]) overrides[recordId] = {}
          overrides[recordId][cell.primitiveId] = ""
          continue
        }
        const edited =
          cell.fileValue != null && cell.fileValue !== cell.rawFileValue
        if (!edited) continue
        if (!overrides[recordId]) overrides[recordId] = {}
        overrides[recordId][cell.primitiveId] = cell.fileValue!
      }
    }

    const blob = await api.exportDownload(ds.scopeId, {
      record_ids: ds.exportRecordIds,
      columns: ds.exportColumns,
      format: ds.targetFormat ?? "csv",
      overrides: Object.keys(overrides).length > 0 ? overrides : undefined,
    })
    const ext = ds.targetFormat === "excel" ? ".xlsx" : "_export.csv"
    triggerBlobDownload(blob, `${ds.scopeId}${ext}`)
    return
  }

  // Upload-based export (from IngestionModal → diff review)
  if (!ds.exportParsedHeaders || !ds.exportParsedRows) return

  const edits: {
    row_index: number
    column_index: number
    new_value: string
  }[] = []

  for (const [recordId, rec] of Object.entries(ds.records)) {
    const anchor = ds.anchoredRecords.find((a) => a.record_id === recordId)
    if (!anchor || anchor.row_index == null) continue

    for (const [, cell] of Object.entries(rec.cells)) {
      if (cell.declined) continue
      // Upload-based export writes either the DB value (dbValue, "accept
      // the proposed change") or the user's custom edit (fileValue when
      // edited away from the original doc snapshot).
      const edited =
        cell.fileValue != null && cell.fileValue !== cell.rawFileValue
      let newVal: string | null = null
      if (edited) newVal = cell.fileValue
      else if (cell.accepted === true) newVal = cell.dbValue
      if (newVal == null) continue

      // Find the column_index from the raw extraction entry
      const ex = ds.rawExtractions.find(
        (e) => e.record_id === recordId && e.primitive_id === cell.primitiveId,
      )
      if (ex?.export_column_index != null) {
        edits.push({
          row_index: anchor.row_index,
          column_index: ex.export_column_index,
          new_value: newVal,
        })
      }
    }
  }

  // --- Helper columns ---
  // Virtual formula columns authored in the wizard.  They have NO source
  // column in the uploaded file, so we append them to the header row and
  // emit edits for each anchored row's computed value.  Unanchored rows get
  // an empty helper cell (build-file pads as needed).
  const helperPrimIds: string[] = []
  const helperLabels = new Map<string, string>()
  for (const ex of ds.rawExtractions) {
    if (ex.primitive_type !== "formula") continue
    if (ex.export_column_index != null) continue
    if (!helperLabels.has(ex.primitive_id)) {
      helperPrimIds.push(ex.primitive_id)
      helperLabels.set(ex.primitive_id, ex.primitive_title)
    }
  }
  const augmentedHeaders =
    helperPrimIds.length > 0
      ? [
          ...ds.exportParsedHeaders,
          ...helperPrimIds.map((id) => helperLabels.get(id) ?? id),
        ]
      : ds.exportParsedHeaders
  const baseHeaderCount = ds.exportParsedHeaders.length
  if (helperPrimIds.length > 0) {
    for (const anchor of ds.anchoredRecords) {
      if (anchor.row_index == null) continue
      helperPrimIds.forEach((pid, i) => {
        // Helper output: dbValue is the computed formula (default); use
        // fileValue when the user edited it away from the original doc
        // snapshot.  Declined helpers fall back to empty.
        const cell = ds.records[anchor.record_id]?.cells[pid]
        const ex = ds.rawExtractions.find(
          (e) => e.record_id === anchor.record_id && e.primitive_id === pid,
        )
        let value = ""
        if (cell && !cell.declined) {
          const edited =
            cell.fileValue != null && cell.fileValue !== cell.rawFileValue
          value = (edited ? cell.fileValue : cell.dbValue) ?? ex?.value ?? ""
        } else if (!cell) {
          value = ex?.value ?? ""
        }
        edits.push({
          row_index: anchor.row_index!,
          column_index: baseHeaderCount + i,
          new_value: value,
        })
      })
    }
  }

  if (edits.length > 0 || helperPrimIds.length > 0) {
    // Pre-pad every row to the augmented header width so the exported CSV is
    // uniform - non-anchored rows keep their original values and get empty
    // helper cells; anchored rows get filled by the edits above.
    const paddedRows =
      helperPrimIds.length > 0
        ? ds.exportParsedRows.map((r) => {
            const padded = [...r]
            while (padded.length < augmentedHeaders.length) padded.push("")
            return padded
          })
        : ds.exportParsedRows

    const outName = ds.fileName.replace(/(\.\w+)$/, "_updated$1")
    const blob = await api.exportBuildFile(ds.scopeId, {
      headers: augmentedHeaders,
      rows: paddedRows,
      edits,
      delimiter: ds.exportDelimiter ?? ",",
      file_name: outName,
    })
    triggerBlobDownload(blob, outName)
  }
}

/** Deduplicate SVHC rows by CAS number: concatenate Gehalt and Ort values across components */
function rollupSvhcRows(rows: SvhcTableRow[]): SvhcTableRow[] {
  const byCas = new Map<
    string,
    {
      substanceName: string
      gehalts: string[]
      orts: string[]
      productId: string
      productName: string
    }
  >()
  for (const r of rows) {
    if (!r.casNumber) continue
    if (!byCas.has(r.casNumber)) {
      byCas.set(r.casNumber, {
        substanceName: r.substanceName,
        gehalts: [],
        orts: [],
        productId: r.productId,
        productName: r.productName,
      })
    }
    const entry = byCas.get(r.casNumber)!
    if (r.svhcGehalt) entry.gehalts.push(r.svhcGehalt)
    if (r.locationInComponent) entry.orts.push(r.locationInComponent)
  }
  return [...byCas.entries()].map(([cas, e]) => ({
    productId: e.productId,
    productName: e.productName,
    casNumber: cas,
    substanceName: e.substanceName,
    svhcGehalt: e.gehalts.join("; "),
    locationInComponent: e.orts.join("; "),
  }))
}

async function handleConformityPdfDownload(
  ds: DiffState,
  translate: TranslateFn,
) {
  if (!ds.exportRecordIds) return

  // Only send overrides for cells the user actually changed from the
  // original doc snapshot, or declined.  Backend emits dbValue by default
  // for untouched cells so we don't need to echo those back.
  const overrides: Record<string, Record<string, string>> = {}
  const allDiffs = useStore.getState().actionState?.diffs ?? {}
  for (const sd of Object.values(allDiffs)) {
    for (const [recordId, rec] of Object.entries(sd.records)) {
      for (const cell of Object.values(rec.cells)) {
        if (cell.declined) {
          if (!overrides[recordId]) overrides[recordId] = {}
          overrides[recordId][cell.primitiveName] = ""
          continue
        }
        const edited =
          cell.fileValue != null && cell.fileValue !== cell.rawFileValue
        if (!edited) continue
        if (!overrides[recordId]) overrides[recordId] = {}
        overrides[recordId][cell.primitiveName] = cell.fileValue!
      }
    }
  }
  const hasOverrides = Object.keys(overrides).length > 0 ? overrides : undefined

  const todayStr = new Date().toISOString().slice(0, 10)

  // Product conformity: one PDF per product
  if (ds.targetFormat === "produkt_konformitaet") {
    // Check if SVHC scope has rollup diff data (from cross-scope export config)
    const svhcDiff = allDiffs["substance"]
    const hasRollupDiff = svhcDiff && Object.keys(svhcDiff.records).length > 0

    if (hasRollupDiff) {
      // Use SVHC diff records with rolled-up values - one row per unique SVHC
      const allData = await api.conformityProductData(
        ds.exportRecordIds,
        hasOverrides,
      )

      // Resolve cell value: use edited fileValue if accepted, else dbValue
      const cellVal = (cell: DiffCell | undefined, fallback = ""): string => {
        if (!cell) return fallback
        if (cell.declined) return ""
        const edited =
          cell.fileValue != null && cell.fileValue !== cell.rawFileValue
        if (edited) return cell.fileValue!
        return cell.dbValue ?? fallback
      }

      for (const prod of allData) {
        // Build svhcRows from the SVHC diff state (unique per SVHC, with rollup values)
        const svhcRows: SvhcTableRow[] = Object.values(svhcDiff.records)
          .map((rec) => {
            const casCell = Object.values(rec.cells).find(
              (c) => c.primitiveTitle === "CAS-Nr.",
            )
            const nameCell = Object.values(rec.cells).find(
              (c) => c.primitiveTitle === "SVHC-Stoff",
            )
            const gehaltCell = Object.values(rec.cells).find(
              (c) =>
                c.primitiveId.startsWith("_rollup_") &&
                c.primitiveTitle === "Gehalt (%)",
            )
            const ortCell = Object.values(rec.cells).find(
              (c) =>
                c.primitiveId.startsWith("_rollup_") &&
                c.primitiveTitle === "SVHC-Orte im Bauteil",
            )

            return {
              productId: prod.product_id,
              productName: prod.product_name,
              casNumber: cellVal(casCell, rec.pkValue ?? ""),
              substanceName: cellVal(nameCell),
              svhcGehalt: cellVal(gehaltCell),
              locationInComponent: cellVal(ortCell),
            }
          })
          .filter((r) => r.casNumber) // skip empty rows

        const form: ConformityFormData = {
          issuer: "turnus GmbH",
          recipient: prod.product_name,
          issuerAddress: "Musterstraße 42, 80331 München, Deutschland",
          creationDate: todayStr,
          declarationText: prod.is_positive
            ? generateProductPositiveText(prod.product_name, svhcRows.length)
            : generateProductNegativeText(prod.product_name),
          regulatoryNotes: generateRegulatoryNotes(),
          signatory: "Dr. Max Mustermann",
          signatoryRole: "Compliance Manager · turnus GmbH",
          signatoryPlace: "München",
          signatoryDate: todayStr,
        }

        generateProductConformityPdf(
          form,
          prod.product_id,
          prod.product_name,
          svhcRows,
          prod.is_positive,
        )

        if (allData.length > 1) {
          await new Promise((resolve) => setTimeout(resolve, 150))
        }
      }
    } else {
      // Fallback: use backend API, rollup SVHCs per product
      const allData = await api.conformityProductData(
        ds.exportRecordIds,
        hasOverrides,
      )

      for (const prod of allData) {
        const rawRows: SvhcTableRow[] = prod.rows.map((r) => ({
          productId: r.product_id,
          productName: r.product_name,
          casNumber: r.cas_number,
          substanceName: r.substance_name,
          svhcGehalt: r.svhc_gehalt,
          locationInComponent: r.component_name ?? "",
        }))
        const svhcRows = rollupSvhcRows(rawRows)

        const form: ConformityFormData = {
          issuer: "turnus GmbH",
          recipient: prod.product_name,
          issuerAddress: "Musterstraße 42, 80331 München, Deutschland",
          creationDate: todayStr,
          declarationText: prod.is_positive
            ? generateProductPositiveText(prod.product_name, svhcRows.length)
            : generateProductNegativeText(prod.product_name),
          regulatoryNotes: generateRegulatoryNotes(),
          signatory: "Dr. Max Mustermann",
          signatoryRole: "Compliance Manager · turnus GmbH",
          signatoryPlace: "München",
          signatoryDate: todayStr,
        }

        generateProductConformityPdf(
          form,
          prod.product_id,
          prod.product_name,
          svhcRows,
          prod.is_positive,
        )

        if (allData.length > 1) {
          await new Promise((resolve) => setTimeout(resolve, 150))
        }
      }
    }
    return
  }

  // Escha customer conformity: one PDF per customer with all their products
  // rendered through jsPDF.  Kupferanteil comes from the schema-declared
  // ``kupferanteil_artikel_kg`` formula primitive (backend-evaluated); the
  // Blei % helper is still computed here via evaluateFormula against the
  // raw primitives the backend data endpoint returns.
  if (ds.targetFormat === "escha_kunden_konformitaet") {
    const eschaData = await api.conformityEschaKundenData(
      ds.exportRecordIds,
      hasOverrides,
    )
    const helpers = (
      getConformityConfig("escha_kunden_konformitaet")?.columns ?? []
    )
      .filter((c) => c.helperConfig)
      .map((c) => ({ label: c.label, helper: c.helperConfig! }))

    const fmtNumber = (v: string): string => {
      if (!v) return ""
      const n = Number(v)
      if (!isFinite(n)) return v
      // Display at most 4 significant fraction digits; drop trailing zeros.
      return Number.isInteger(n)
        ? String(n)
        : String(parseFloat(n.toPrecision(6)))
    }

    // The diff view is the source of truth for export values - dbValue
    // holds the computed helper result (right side, default export);
    // fileValue holds the user's override (left side, edited).  The output
    // uses fileValue when the user edited it away from the original doc
    // snapshot, otherwise the computed dbValue.
    const productDiff = allDiffs["product"]
    const diffHelperByProductId = new Map<string, Map<string, string>>()
    if (productDiff) {
      for (const [recId, rec] of Object.entries(productDiff.records)) {
        for (const cell of Object.values(rec.cells)) {
          if (cell.primitiveType !== "formula") continue
          if (cell.declined) continue
          const edited =
            cell.fileValue != null && cell.fileValue !== cell.rawFileValue
          const val = edited ? cell.fileValue : cell.dbValue
          if (val == null) continue
          if (!diffHelperByProductId.has(recId)) {
            diffHelperByProductId.set(recId, new Map<string, string>())
          }
          diffHelperByProductId.get(recId)!.set(cell.primitiveName, val)
        }
      }
    }

    for (const cust of eschaData) {
      const rows: EschaKundenTableRow[] = cust.products.map((p) => {
        const identifiers: Record<string, string> = {
          menge_blei: p.menge_blei,
          basismenge: p.basismenge,
          gewicht: p.gewicht,
        }
        const diffForProduct =
          diffHelperByProductId.get(p.product_record_id) ??
          diffHelperByProductId.get(p.internal_product_id)
        const helperValues: Record<string, string> = {}
        for (const { helper } of helpers) {
          // Prefer the diff cell's current fileValue (possibly edited); fall
          // back to live evaluation only when no diff cell exists.
          const fromDiff = diffForProduct?.get(helper.label)
          if (fromDiff != null) {
            helperValues[helper.label] = fromDiff
            continue
          }
          const result = evaluateFormula(
            helper.expression,
            { identifiers },
            translate,
          )
          helperValues[helper.label] = result.value ?? ""
        }
        return {
          artikelNr: p.internal_product_id,
          fremdartikelNr: p.fremdartikelnummer,
          beschreibung: p.artikelausfuehrung,
          kupferanteilKg: fmtNumber(p.kupferanteil_artikel_kg),
          bleiProzent: fmtNumber(helperValues.blei_prozent ?? ""),
        }
      })
      generateEschaKundenKonformitaetPdf(cust.customer_id, rows, todayStr)
      if (eschaData.length > 1) {
        await new Promise((resolve) => setTimeout(resolve, 150))
      }
    }
    return
  }

  // Customer-scope export: one PDF per customer with all their products
  const allData = await api.conformityLetterData(
    ds.exportRecordIds,
    hasOverrides,
  )

  // Build SVHC rollup lookup from the diff state (keyed by CAS number)
  // This has the computed Gehalt and Ort values from computeRollup
  const svhcDiffCust = allDiffs["substance"]
  const svhcRollupByCas = new Map<string, { gehalt: string; ort: string }>()
  if (svhcDiffCust) {
    const cellValCust = (cell: DiffCell | undefined, fallback = ""): string => {
      if (!cell) return fallback
      if (cell.declined) return ""
      const edited =
        cell.fileValue != null && cell.fileValue !== cell.rawFileValue
      if (edited) return cell.fileValue!
      return cell.dbValue ?? fallback
    }
    for (const rec of Object.values(svhcDiffCust.records)) {
      const gehaltCell = Object.values(rec.cells).find(
        (c) =>
          c.primitiveId.startsWith("_rollup_") &&
          c.primitiveTitle === "Gehalt (%)",
      )
      const ortCell = Object.values(rec.cells).find(
        (c) =>
          c.primitiveId.startsWith("_rollup_") &&
          c.primitiveTitle === "SVHC-Orte im Bauteil",
      )
      if (rec.pkValue) {
        svhcRollupByCas.set(rec.pkValue, {
          gehalt: cellValCust(gehaltCell),
          ort: cellValCust(ortCell),
        })
      }
    }
  }

  for (const cust of allData) {
    // Deduplicate SVHCs per product, enrich with rollup values from diff
    const productGroups = (cust.products ?? []).map((pg) => {
      const rawRows: SvhcTableRow[] = (pg.rows ?? []).map((r) => ({
        productId: r.product_id,
        productName: r.product_name,
        casNumber: r.cas_number,
        substanceName: r.substance_name,
        svhcGehalt: r.svhc_gehalt,
        locationInComponent: "",
      }))
      const rolled = rollupSvhcRows(rawRows)
      // Enrich with rollup values from SVHC diff (Gehalt and Ort computed via computeRollup)
      for (const row of rolled) {
        const rollup = svhcRollupByCas.get(row.casNumber)
        if (rollup) {
          if (rollup.gehalt) row.svhcGehalt = rollup.gehalt
          if (rollup.ort) row.locationInComponent = rollup.ort
        }
      }
      return {
        productId: pg.product_id,
        productName: pg.product_name,
        rows: rolled,
      }
    })

    // Flat list = all rolled-up rows across products
    const svhcRows: SvhcTableRow[] = productGroups.flatMap((pg) => pg.rows)

    const form: ConformityFormData = {
      issuer: "turnus GmbH",
      recipient: cust.customer_name,
      issuerAddress: "Musterstraße 42, 80331 München, Deutschland",
      creationDate: todayStr,
      declarationText: cust.is_positive
        ? generatePositiveText(cust.customer_name, svhcRows.length)
        : generateNegativeText(cust.customer_name, cust.total_product_count),
      regulatoryNotes: generateRegulatoryNotes(),
      signatory: "Dr. Max Mustermann",
      signatoryRole: "Compliance Manager · turnus GmbH",
      signatoryPlace: "München",
      signatoryDate: todayStr,
    }

    generateConformityPdf(
      form,
      cust.customer_name,
      svhcRows,
      cust.total_product_count,
      cust.is_positive,
      productGroups,
    )

    if (allData.length > 1) {
      await new Promise((resolve) => setTimeout(resolve, 150))
    }
  }
}

function triggerBlobDownload(blob: Blob, filename: string) {
  const url = URL.createObjectURL(blob)
  const a = document.createElement("a")
  a.href = url
  a.download = filename
  document.body.appendChild(a)
  a.click()
  document.body.removeChild(a)
  URL.revokeObjectURL(url)
}

// Helper (virtual formula) column styling - mirrors the rollup treatment so
// temporary wizard columns are visually distinct from persisted primitives.
const HELPER_HEADER_CLASS = "bg-success/10 text-success"
const HELPER_CELL_CLASS = "bg-success/5"
const HELPER_SUBTITLE_CLASS = "text-success"

function isHelperPrim(p: { id: string }): boolean {
  return p.id.startsWith("_helper_")
}

// Scope-based colors for rollup columns
const ROLLUP_SCOPE_HEADER: Record<string, string> = {
  supplier: "bg-purple1/10 text-purple1",
  component: "bg-yellow1/10 text-yellow3",
  product: "bg-success/10 text-success",
  customer: "bg-warning/10 text-warning",
  substance: "bg-red1/10 text-red1",
}
const ROLLUP_SCOPE_CELL: Record<string, string> = {
  supplier: "bg-purple1/5",
  component: "bg-yellow1/5",
  product: "bg-success/5",
  customer: "bg-warning/5",
  substance: "bg-red1/5",
}
const ROLLUP_SCOPE_TEXT: Record<string, string> = {
  supplier: "text-purple1",
  component: "text-yellow3",
  product: "text-success",
  customer: "text-warning",
  substance: "text-red1",
}
const ROLLUP_SCOPE_CARD_BORDER: Record<string, string> = {
  supplier: "border-purple1/30",
  component: "border-yellow1/30",
  product: "border-success/30",
  customer: "border-warning/30",
  substance: "border-red1/30",
}
const ROLLUP_SCOPE_CARD_BG: Record<string, string> = {
  supplier: "bg-purple1/5",
  component: "bg-yellow1/5",
  product: "bg-success/5",
  customer: "bg-warning/5",
  substance: "bg-red1/5",
}
const ROLLUP_SCOPE_CARD_HOVER: Record<string, string> = {
  supplier: "hover:border-purple1/50 hover:bg-purple1/10",
  component: "hover:border-yellow1/50 hover:bg-yellow1/10",
  product: "hover:border-success/50 hover:bg-success/10",
  customer: "hover:border-warning/50 hover:bg-warning/10",
  substance: "hover:border-red1/50 hover:bg-red1/10",
}

interface Props {
  scopeId: string
  /** Forces selection mode (checkboxes always visible, no Select/Cancel
   *  buttons). Used by importer APs in idle state where selection IS the
   *  workspace - there's no "start selecting" step to opt into. */
  forceSelectMode?: boolean
  /** Read-only mode: disables editing, used by Monitor evidence overlay (v6). */
  readOnly?: boolean
}

export function PivotTable({ scopeId, forceSelectMode = false }: Props) {
  const { translate, currentLanguage } = useTranslation()
  const {
    openEdit,
    openRecordDetail,
    openIngestion,
    filter,
    removeFilterPk,
    clearFilter,
    addFilterPks,
    setFilter,
    selectedRecordIds,
    toggleRecordSelection: _toggleRecordSelection,
    selectRecords: _selectRecords,
    deselectRecords: _deselectRecords,
    recordSelection,
    rollupState,
    setRollupState,
    clearRollupState,
    // Unified action state
    actionState,
    addPrimaryPkValues,
    removePrimaryPkValue,
    setPrimaryPkValues,
    acceptActionDiffCell,
    declineActionDiffCell,
    acceptActionDiffRow,
    acceptAllActionScopeDiffs,
    editActionDiffCellValue,
    acceptActionRelationDiffEntry,
    acceptAllActionRelationDiffs,
    openCancelActionDialog,
    cancelAction,
    startAction,
    openActionEditor,
    setActionScopeDiff,
    hierarchyLevelFilters,
    setHierarchyLevelFilter,
    freshnessDate: storeFreshnessDate,
    diffFilterMode,
  } = useStore()
  const qc = useQueryClient()

  // Derive diff/download mode from unified actionState
  const scopeDiff = actionState?.diffs[scopeId] ?? null
  const isDiffMode = scopeDiff !== null
  const isSecondaryDiff = scopeDiff?.secondaryDiff ?? false
  // Global diff phase - true when ANY scope has diffs (used to hide action buttons on all scopes)
  const inDiffPhase = actionState?.phase === "diff"
  // Build a DiffState-compatible object for functions that still expect it
  const diffState = scopeDiff
    ? ({ ...scopeDiff, scopeId } as import("../../types").DiffState)
    : null
  // When in diff phase but current scope has no diffs, use primary scope's diff for toolbar display
  const primaryDiffState = (() => {
    if (isDiffMode || !inDiffPhase || !actionState) return null
    const primaryDiff = actionState.diffs[actionState.primaryScopeId]
    if (!primaryDiff) return null
    return {
      ...primaryDiff,
      scopeId: actionState.primaryScopeId,
    } as import("../../types").DiffState
  })()
  const downloadMode =
    actionState !== null &&
    actionState.type === "export" &&
    actionState.phase === "select" &&
    !scopeDiff &&
    actionState.primaryScopeId === scopeId
  // Effective select mode unifies the export "Select" path with the
  // importer-AP "selection is always on" path. Anything that's purely a
  // selection-UI affordance (checkbox column, row toggle, header layout)
  // should consult selectMode; anything tied to the export action lifecycle
  // (Export button text, Konfigurieren title, cancel/apply) stays on downloadMode.
  const selectMode = downloadMode || forceSelectMode
  // Whether this is the primary scope of the active action
  const isPrimaryScope = actionState?.primaryScopeId === scopeId
  // Hide action-unrelated toolbar buttons when viewing a non-primary scope during an active action
  const hideToolbarActions = actionState !== null && !isPrimaryScope

  // Helper: resolve PK value from a record object
  const getPkValue = useCallback(
    (record: QmsRecord, scope: Scope | undefined): string | null => {
      if (!scope) return null
      const pkPrim = scope.primitives.find((p) => p.is_pk)
      if (!pkPrim) return null
      return record.values[pkPrim.name]?.value ?? null
    },
    [],
  )

  // PK-syncing selection wrappers: keep selectedRecordIds AND actionState.primaryPkValues in sync
  const toggleRecordSelection = useCallback(
    (recordId: string) => {
      // Check current state BEFORE toggling
      const wasSelected = useStore.getState().selectedRecordIds.has(recordId)
      _toggleRecordSelection(recordId)
      if (!actionState || !isPrimaryScope) return
      const cached = qc.getQueryData<{ scope: Scope; records: QmsRecord[] }>([
        "records",
        scopeId,
      ])
      const rec = cached?.records.find((r) => r.id === recordId)
      if (!rec || !cached) return
      const pk = getPkValue(rec, cached.scope)
      if (!pk) return
      if (wasSelected) {
        removePrimaryPkValue(pk)
      } else {
        addPrimaryPkValues([pk])
      }
    },
    [actionState, isPrimaryScope, scopeId, qc, getPkValue],
  )

  const selectRecords = useCallback(
    (recordIds: string[]) => {
      _selectRecords(recordIds)
      if (!actionState || !isPrimaryScope) return
      const cached = qc.getQueryData<{ scope: Scope; records: QmsRecord[] }>([
        "records",
        scopeId,
      ])
      if (!cached) return
      const pks = recordIds
        .map((id) => cached.records.find((r) => r.id === id))
        .filter(Boolean)
        .map((r) => getPkValue(r!, cached.scope))
        .filter(Boolean) as string[]
      if (pks.length > 0) addPrimaryPkValues(pks)
    },
    [actionState, isPrimaryScope, scopeId, qc, getPkValue],
  )

  const deselectRecords = useCallback(
    (recordIds: string[]) => {
      _deselectRecords(recordIds)
      if (!actionState || !isPrimaryScope) return
      const cached = qc.getQueryData<{ scope: Scope; records: QmsRecord[] }>([
        "records",
        scopeId,
      ])
      if (!cached) return
      // Remove PKs: set primaryPkValues to those NOT in the deselected set
      const deselectedPks = new Set(
        recordIds
          .map((id) => cached.records.find((r) => r.id === id))
          .filter(Boolean)
          .map((r) => getPkValue(r!, cached.scope))
          .filter(Boolean) as string[],
      )
      const remaining = (actionState?.primaryPkValues ?? []).filter(
        (pk) => !deselectedPks.has(pk),
      )
      setPrimaryPkValues(remaining)
    },
    [actionState, isPrimaryScope, scopeId, qc, getPkValue],
  )

  // Scoped action wrappers
  const acceptDiffCell = (recordId: string, primitiveId: string) =>
    acceptActionDiffCell(scopeId, recordId, primitiveId)
  const declineDiffCell = (recordId: string, primitiveId: string) =>
    declineActionDiffCell(scopeId, recordId, primitiveId)
  const acceptDiffRow = (recordId: string) =>
    acceptActionDiffRow(scopeId, recordId)
  const acceptAllDiffs = () => acceptAllActionScopeDiffs(scopeId)
  const editDiffCellValue = (
    recordId: string,
    primitiveId: string,
    val: string | null,
    ti: boolean,
    secondaryOverride?: boolean,
  ) =>
    editActionDiffCellValue(
      scopeId,
      recordId,
      primitiveId,
      val,
      ti,
      secondaryOverride,
    )
  const acceptRelationDiffEntry = (
    recordId: string,
    primitiveId: string,
    targetId: string,
  ) => acceptActionRelationDiffEntry(scopeId, recordId, primitiveId, targetId)
  const acceptAllRelationDiffs = (recordId: string, primitiveId: string) =>
    acceptAllActionRelationDiffs(scopeId, recordId, primitiveId)
  const openCancelDiffDialog = () => openCancelActionDialog()
  const exitDiffMode = () => cancelAction()

  // Scope names map for DiffToolbar per-scope indicators
  const allStoreScopes = useStore((s) => s.scopes)
  const scopeNamesMap = React.useMemo(() => {
    const m: Record<string, string> = {}
    for (const s of allStoreScopes) m[s.id] = s.name
    return m
  }, [allStoreScopes])

  // Export handler: triggered by Exportieren button when all scopes are 100%
  const [exportInProgress, setExportInProgress] = useState(false)
  const handleExport = useCallback(
    async (historyMode: HistoryMode) => {
      const as = useStore.getState().actionState
      if (!as) return
      // Use the primary scope's diff for the export
      const primaryDiff = as.diffs[as.primaryScopeId]
      if (!primaryDiff) return
      const ds = {
        ...primaryDiff,
        scopeId: as.primaryScopeId,
      } as import("../../types").DiffState
      setExportInProgress(true)
      try {
        await handleExportDownload(ds, translate)
        // Touch only the records that were actually part of the export - across
        // primary and any secondary scope diffs - so the freshness filter is
        // respected and the change timeline gets a single batched event.
        if (historyMode !== "none") {
          const touchedIds = new Set<string>()
          for (const sd of Object.values(as.diffs)) {
            for (const rec of Object.values(sd.records)) {
              touchedIds.add(rec.recordId)
            }
          }
          await api.ingestTouch(ds.scopeId, {
            document_name: ds.fileName,
            history_mode: historyMode,
            record_ids: Array.from(touchedIds),
          })
        }
        // Clean up all diffs
        cancelAction()
      } catch (err) {
        console.error("Export failed:", err)
      } finally {
        setExportInProgress(false)
      }
    },
    [cancelAction],
  )

  const toggleDownloadMode = () => {
    if (actionState) cancelAction()
    else startAction(scopeId, "export")
  }
  const exitDownloadMode = () => cancelAction()
  const openRecordSelectionEditor = () => openActionEditor()
  const [draggingOver, setDraggingOver] = useState(false)
  const [columnPickerOpen, setColumnPickerOpen] = useState(false)
  const [groupByPrimName, setGroupByPrimName] = useState<string | null>(null)
  // Hierarchy level filter: derived from shared store so it syncs across scopes
  const hierLevelFilter = hierarchyLevelFilters[scopeId] ?? null
  const setHierLevelFilter = (label: string | null) =>
    setHierarchyLevelFilter(scopeId, label)
  const [levelDropdownOpen, setLevelDropdownOpen] = useState(false)
  // PK filter state (inline in column header)
  const [pkSearch, setPkSearch] = useState("")
  const [pkDropdownOpen, setPkDropdownOpen] = useState(false)
  // Formula error filter: when set to a prim name, only show records where that formula has errors
  const [formulaErrorFilter, setFormulaErrorFilter] = useState<string | null>(
    null,
  )
  // Relation hierarchy level filters: derived from shared store for cross-scope sync
  const relationLevelFilters = hierarchyLevelFilters
  // Which relation column header's level dropdown is open
  const [relationLevelDropdownOpen, setRelationLevelDropdownOpen] = useState<
    string | null
  >(null)
  const fileInputRef = useRef<HTMLInputElement>(null)
  const tableScrollRef = useRef<HTMLDivElement>(null)

  // Measure the diff actions column width and expose it as a CSS variable
  // so the sticky PK column can position itself flush against it.
  const diffActionRO = useRef<ResizeObserver | null>(null)
  const diffActionColRef = useCallback((node: HTMLTableCellElement | null) => {
    // Disconnect previous observer
    if (diffActionRO.current) {
      diffActionRO.current.disconnect()
      diffActionRO.current = null
    }
    if (!node) return
    const container = tableScrollRef.current
    if (!container) return
    const update = () =>
      container.style.setProperty(
        "--diff-action-col-w",
        `${node.offsetWidth}px`,
      )
    update()
    const ro = new ResizeObserver(update)
    ro.observe(node)
    diffActionRO.current = ro
  }, [])

  const handleFileDrop = useCallback(
    (file: File) => {
      openIngestion(scopeId, file)
    },
    [scopeId, openIngestion],
  )

  const handleDragOver = (e: React.DragEvent) => {
    e.preventDefault()
    setDraggingOver(true)
  }
  const handleDragLeave = () => setDraggingOver(false)
  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault()
    setDraggingOver(false)
    const file = e.dataTransfer.files[0]
    if (file) handleFileDrop(file)
  }
  const handleFileInput = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0]
    if (file) handleFileDrop(file)
    e.target.value = ""
  }

  // Determine if the action filters records for this scope.
  // Primary scope during 'select' phase is NOT filtered - user needs to see all records to select more.
  // Primary scope IS filtered during 'diff' phase and after file-based selection (mapping/diff).
  // Secondary diff scopes are NOT filtered - pending relation changes (e.g. new customer→product
  // mappings) haven't been committed yet, so listFilteredRecords can't traverse them.
  // The diff-mode row filter (diffFilteredRecords) handles showing only relevant records.
  const actionFilterActive =
    actionState !== null &&
    actionState.primaryPkValues.length > 0 &&
    !(isPrimaryScope && actionState.phase === "select") &&
    !isSecondaryDiff
  const actionFilterKey = actionFilterActive
    ? `action:${actionState!.primaryScopeId}:${actionState!.primaryPkValues.join(",")}`
    : ""

  // Record selection: query each pkSet separately, combine on client
  const selectionKey =
    !actionFilterActive && recordSelection
      ? recordSelection.pkSets
          .map((ps) => `${ps.scopeId}:${ps.pkValues.join(",")}`)
          .join("|") + `|${recordSelection.crossScopeOperator}`
      : ""
  const rlFilterKey =
    Object.keys(relationLevelFilters).length > 0
      ? JSON.stringify(relationLevelFilters)
      : ""
  // eslint-disable-next-line @tanstack/query/exhaustive-deps
  const { data, isLoading, error } = useQuery({
    queryKey: actionFilterActive
      ? ["records", scopeId, "action-filtered", actionFilterKey, rlFilterKey]
      : selectionKey
        ? ["records", scopeId, "selection", selectionKey, rlFilterKey]
        : filter
          ? [
              "records",
              scopeId,
              "filtered",
              filter.originScopeId,
              filter.pkValues.join(","),
              rlFilterKey,
            ]
          : ["records", scopeId, rlFilterKey],
    queryFn: async () => {
      const rlf =
        Object.keys(relationLevelFilters).length > 0
          ? relationLevelFilters
          : undefined

      // Action-based filtering: use primary scope PKs to filter this scope
      if (actionFilterActive) {
        const as = actionState!
        // Primary scope: filter by its own PKs
        if (scopeId === as.primaryScopeId) {
          return api.listFilteredRecords(
            scopeId,
            as.primaryScopeId,
            as.primaryPkValues,
            rlf,
          )
        }
        // Secondary scope: filter via relation chain from primary scope
        return api.listFilteredRecords(
          scopeId,
          as.primaryScopeId,
          as.primaryPkValues,
          rlf,
        )
      }

      // Legacy record selection path
      if (selectionKey && recordSelection) {
        const results = await Promise.all(
          recordSelection.pkSets.map((ps) =>
            api.listFilteredRecords(scopeId, ps.scopeId, ps.pkValues, rlf),
          ),
        )
        const scope = results[0]?.scope ?? results.find((r) => r?.scope)?.scope
        if (!scope) return api.listRecords(scopeId, rlf)

        if (
          recordSelection.crossScopeOperator === "or" ||
          results.length === 1
        ) {
          const seen = new Set<string>()
          const merged: (typeof results)[0]["records"] = []
          for (const r of results) {
            for (const rec of r.records) {
              if (!seen.has(rec.id)) {
                seen.add(rec.id)
                merged.push(rec)
              }
            }
          }
          return { scope, records: merged }
        } else {
          const idSets = results.map(
            (r) => new Set(r.records.map((rec) => rec.id)),
          )
          const intersection = idSets.reduce(
            (a, b) => new Set([...a].filter((id) => b.has(id))),
          )
          const allRecords = results.flatMap((r) => r.records)
          const seen = new Set<string>()
          const merged: (typeof results)[0]["records"] = []
          for (const rec of allRecords) {
            if (intersection.has(rec.id) && !seen.has(rec.id)) {
              seen.add(rec.id)
              merged.push(rec)
            }
          }
          return { scope, records: merged }
        }
      }
      if (filter)
        return api.listFilteredRecords(
          scopeId,
          filter.originScopeId,
          filter.pkValues,
          rlf,
        )
      return api.listRecords(scopeId, rlf)
    },
  })

  // Always fetch unfiltered records for the PK dropdown (so all PKs are available)
  const { data: allRecordsData } = useQuery({
    queryKey: ["records", scopeId],
    queryFn: () => api.listRecords(scopeId),
  })

  // Lazy creation: when navigating to a secondary scope during diff phase,
  // auto-generate secondary diff cells from the loaded records.
  // Primitives included in the action's exportColumns become primary cells.
  useEffect(() => {
    if (!inDiffPhase || isDiffMode || !data?.scope || !data?.records?.length)
      return
    if (scopeId === actionState?.primaryScopeId) return
    // Don't overwrite if secondary diff already exists for this scope
    if (actionState?.diffs[scopeId]) return

    // Scopes with export columns are handled by handleExportProceed (with rollup computation) - skip
    const primaryDiff = actionState?.diffs[actionState?.primaryScopeId ?? ""]
    if (primaryDiff?.exportColumns?.some((col) => col.scope_id === scopeId))
      return
    const primaryScopeId = actionState?.primaryScopeId

    // Derive mapped primitive IDs from the primary scope's exportColumns
    const mappedIds = new Set<string>()
    if (primaryDiff?.exportColumns) {
      for (const col of primaryDiff.exportColumns) {
        if (col.scope_id === scopeId) mappedIds.add(col.primitive_id)
      }
    }

    const isExport = !!primaryDiff?.exportMode
    let cancelled = false
    ;(async () => {
      // Export mode: restrict records to those linked to the primary export
      // selection. Skip entirely when this scope is unreachable from the primary.
      let recordsForDiff = data.records
      if (isExport && primaryDiff && primaryScopeId) {
        recordsForDiff = await filterRecordsLinkedToPrimaryExport(
          data.records,
          primaryScopeId,
          primaryDiff,
          scopeId,
          qc,
        )
        if (cancelled) return
        if (recordsForDiff.length === 0) return
      }
      // Re-check after await
      if (useStore.getState().actionState?.diffs[scopeId]) return
      const currentFreshnessDate = useStore.getState().freshnessDate
      const staleIds = await fetchEffectiveStaleIds(
        recordsForDiff,
        scopeId,
        currentFreshnessDate,
      )
      if (cancelled) return
      const secondaryDiff = buildSecondaryDiffState(
        scopeId,
        data.scope,
        recordsForDiff,
        staleIds,
        !!currentFreshnessDate,
        mappedIds.size > 0 ? mappedIds : undefined,
        isExport,
      )
      // Don't create scope diff if no records have reviewable cells
      if (Object.keys(secondaryDiff.records).length === 0) return
      // Inherit export mode from the primary scope's diff
      if (isExport) {
        secondaryDiff.exportMode = true
        secondaryDiff.targetFormat = primaryDiff!.targetFormat
      }
      setActionScopeDiff(scopeId, secondaryDiff)
    })()
    return () => {
      cancelled = true
    }
  }, [
    inDiffPhase,
    isDiffMode,
    scopeId,
    actionState?.primaryScopeId,
    data?.scope,
    data?.records?.length,
  ])

  // Eager creation: when the PRIMARY scope enters diff phase, fetch and populate
  // secondary diffs for ALL other scopes so ScopeTabs badges appear immediately.
  const diffPhaseId = inDiffPhase ? actionState?.id : null
  useEffect(() => {
    if (!diffPhaseId || scopeId !== actionState?.primaryScopeId) return
    const primaryDiff = actionState?.diffs[actionState?.primaryScopeId ?? ""]
    if (!primaryDiff) return
    // Scopes with export columns are handled by handleExportProceed (with rollup computation).
    // The eager effect must NOT create diffs for those scopes, as it would race and overwrite
    // the rollup-enriched diff with a plain one.
    const exportHandledScopes = new Set<string>()
    if (primaryDiff.exportColumns) {
      for (const col of primaryDiff.exportColumns) {
        if (col.scope_id !== scopeId) exportHandledScopes.add(col.scope_id)
      }
    }
    const allScopes = useStore.getState().scopes
    let cancelled = false
    ;(async () => {
      await Promise.all(
        allScopes.map(async (s) => {
          if (s.id === scopeId) return
          if (exportHandledScopes.has(s.id)) return
          if (useStore.getState().actionState?.diffs[s.id]) return
          let scopeData: { records: QmsRecord[]; scope: Scope } | undefined
          try {
            scopeData = await qc.fetchQuery({
              queryKey: ["records", s.id],
              queryFn: () => api.listRecords(s.id),
              staleTime: 30_000,
            })
          } catch {
            return
          }
          if (cancelled || !scopeData?.scope || !scopeData.records?.length)
            return
          // Re-check after await
          if (useStore.getState().actionState?.diffs[s.id]) return
          const mappedIds = new Set<string>()
          if (primaryDiff.exportColumns) {
            for (const col of primaryDiff.exportColumns) {
              if (col.scope_id === s.id) mappedIds.add(col.primitive_id)
            }
          }
          const isExport = !!primaryDiff.exportMode
          // Export mode: restrict records to those linked to the primary export
          // selection. Scopes unreachable from the primary (e.g. supplier when
          // exporting product conformity) are skipped entirely so their records
          // don't get swept into the touch batch.
          let recordsForDiff = scopeData.records
          if (isExport) {
            recordsForDiff = await filterRecordsLinkedToPrimaryExport(
              scopeData.records,
              scopeId,
              primaryDiff,
              s.id,
              qc,
            )
            if (cancelled) return
            if (recordsForDiff.length === 0) return
          }
          const currentFreshnessDate = useStore.getState().freshnessDate
          const staleIds = await fetchEffectiveStaleIds(
            recordsForDiff,
            s.id,
            currentFreshnessDate,
          )
          if (cancelled) return
          const secDiff = buildSecondaryDiffState(
            s.id,
            scopeData.scope,
            recordsForDiff,
            staleIds,
            !!currentFreshnessDate,
            mappedIds.size > 0 ? mappedIds : undefined,
            isExport,
          )
          // Don't create scope diff if no records have reviewable cells
          if (Object.keys(secDiff.records).length === 0) return
          if (isExport) {
            secDiff.exportMode = true
            secDiff.targetFormat = primaryDiff.targetFormat
          }
          useStore.getState().setActionScopeDiff(s.id, secDiff)
        }),
      )
    })()
    return () => {
      cancelled = true
    }
  }, [diffPhaseId, scopeId, actionState?.primaryScopeId])

  // Recompute rollup when rollup flags change in the record selection editor
  const rollupKey =
    recordSelection?.mappings
      .filter((m) => m.isRollup)
      .map((m) => m.primitiveId)
      .sort()
      .join(",") ?? ""
  useEffect(() => {
    if (!recordSelection || !data?.records) return
    const rollupMappings = recordSelection.mappings.filter(
      (m) => m.isRollup && m.visible,
    )
    if (rollupMappings.length === 0) {
      if (rollupState) clearRollupState()
      return
    }
    const columns: RollupColumnDef[] = rollupMappings.map((m) => ({
      primitiveId: m.primitiveId,
      primitiveScopeId: m.scopeId,
      primitiveTitle: m.primitiveTitle,
      primitiveType: m.primitiveType,
      label: m.primitiveTitle,
      aggregation:
        m.primitiveType === "number"
          ? ("sum" as const)
          : ("concat_unique" as const),
    }))
    const recordIds = data.records.map((r: QmsRecord) => r.id)
    let cancelled = false
    api
      .computeRollup(scopeId, {
        record_ids: recordIds,
        columns: columns.map((c) => ({
          primitive_id: c.primitiveId,
          primitive_scope_id: c.primitiveScopeId,
          aggregation: c.aggregation,
          label: c.label,
        })),
      })
      .then((result) => {
        if (cancelled) return
        const values: Record<
          string,
          Record<string, { value: string | null; updated_at?: string | null }>
        > = {}
        for (const v of result.values) {
          if (!values[v.record_id]) values[v.record_id] = {}
          values[v.record_id][v.primitive_id] = {
            value: v.value,
            updated_at: v.updated_at ?? null,
          }
        }
        setRollupState({ scopeId, columns, values, includedRecordIds: {} })
      })
      .catch((err) => {
        if (!cancelled) console.error("Rollup recomputation failed:", err)
      })
    return () => {
      cancelled = true
    }
  }, [rollupKey, data?.records?.length])

  // Derive group-by state (needed before early returns so the useQuery hook below is always called)
  // When recordSelection is active, override visibility to only show selected primitives
  const _splitPrims = (() => {
    const allPrims = data?.scope.primitives ?? []
    let prims: PrimitiveDef[]
    if (recordSelection) {
      const selIds = new Set(recordSelection.visiblePrimitiveIds)
      prims = allPrims.filter(
        (p) => p.type !== "relation" && !p.config?.hidden && selIds.has(p.id),
      )
    } else {
      prims = allPrims.filter(
        (p) => p.type !== "relation" && p.is_visible && !p.config?.hidden,
      )
    }
    // Append cross-scope (rollup) primitives from diff cells or rollupState
    const ownIds = new Set(prims.map((p) => p.id))
    const addVirtualPrim = (
      id: string,
      title: string,
      type: string,
      sourceScopeId?: string,
    ) => {
      if (ownIds.has(id)) return
      ownIds.add(id)
      prims.push({
        id,
        scope_id: sourceScopeId ?? scopeId,
        name: id,
        title,
        description: null,
        type,
        is_pk: false,
        is_visible: true,
        display_order: 9999,
        config: { _rollupSourceScope: sourceScopeId },
        is_custom: false,
        related_scope_id: null,
        relation_cardinality: null,
      } as PrimitiveDef)
    }

    // From export diff cells
    if (isDiffMode && diffState?.exportMode) {
      // Resolve source scope from rawExtractions + exportColumns
      const primScopeMap = new Map<string, string>()
      for (const ex of diffState.rawExtractions ?? []) {
        if (ex.primitive_scope_id && ex.primitive_scope_id !== scopeId) {
          primScopeMap.set(ex.primitive_id, ex.primitive_scope_id)
        }
      }
      for (const col of diffState.exportColumns ?? []) {
        if (col.scope_id !== scopeId) {
          primScopeMap.set(col.primitive_id, col.scope_id)
        }
        // Rollup columns: use the source scope for coloring (e.g., component → yellow)
        if (col.rollupConfig?.sourceScopeId) {
          primScopeMap.set(col.primitive_id, col.rollupConfig.sourceScopeId)
        }
      }
      for (const rec of Object.values(diffState.records)) {
        for (const cell of Object.values(rec.cells)) {
          if (
            !ownIds.has(cell.primitiveId) &&
            cell.primitiveType !== "relation"
          ) {
            addVirtualPrim(
              cell.primitiveId,
              cell.primitiveTitle,
              cell.primitiveType,
              primScopeMap.get(cell.primitiveId),
            )
          }
        }
      }
      // Also create virtual prims from exportColumns with rollupConfig -
      // ensures rollup column headers appear even when computeRollup failed
      // and no diff cells were created for these columns.
      // Only include columns that belong to THIS scope (the primary diff's
      // exportColumns contains ALL columns across all scopes).
      for (const col of diffState.exportColumns ?? []) {
        if (
          col.rollupConfig &&
          col.scope_id === scopeId &&
          !ownIds.has(col.primitive_id)
        ) {
          addVirtualPrim(
            col.primitive_id,
            col.label,
            "string",
            col.rollupConfig.sourceScopeId,
          )
        }
        // Helper columns (virtual formula, evaluated client-side): register
        // so the diff view renders a column for them.  isHelperPrim() keys
        // off the ``_helper_`` ID prefix, so the helper CSS applies
        // automatically without a _rollupSourceScope hint.
        if (
          col.helperConfig &&
          col.scope_id === scopeId &&
          !ownIds.has(col.primitive_id)
        ) {
          addVirtualPrim(col.primitive_id, col.label, "formula")
        }
      }
    }

    // From active rollup state (Select Records flow)
    if (rollupState && rollupState.scopeId === scopeId) {
      for (const col of rollupState.columns) {
        addVirtualPrim(
          col.primitiveId,
          col.primitiveTitle ?? col.label,
          col.primitiveType ?? "string",
          col.primitiveScopeId,
        )
      }
    }

    // Separate rollup prims - place next to their relation column if a direct
    // relation exists; otherwise treat as own columns (for multi-hop rollups).
    const directRelScopeIds = new Set(
      (data?.scope.primitives ?? [])
        .filter((p) => p.type === "relation" && p.related_scope_id)
        .map((p) => p.related_scope_id!),
    )
    // Build authoritative rollup scope lookup from rollupState.columns so
    // the classification is correct even when the diff path added the virtual
    // prim before the rollup path (which can leave _rollupSourceScope unset).
    const rollupScopeLookup = new Map<string, string>()
    if (rollupState && rollupState.scopeId === scopeId) {
      for (const col of rollupState.columns) {
        rollupScopeLookup.set(col.primitiveId, col.primitiveScopeId)
      }
    }
    const own: PrimitiveDef[] = []
    const rollupByScope: Record<string, PrimitiveDef[]> = {}
    for (const p of prims) {
      const src =
        rollupScopeLookup.get(p.id) ??
        (p.config?._rollupSourceScope as string | undefined)
      // Export diff rollup columns (virtual, with diff cells) stay in own -
      // they render as scalar columns, not relation count badges
      const isExportRollup = p.id.startsWith("_rollup_")
      if (!src || isExportRollup) {
        own.push(p)
      } else if (directRelScopeIds.has(src)) {
        // Ensure the virtual prim's config reflects the correct source scope
        // (may have been created by the diff path without _rollupSourceScope)
        if (p.config?._rollupSourceScope !== src) {
          p.config = { ...p.config, _rollupSourceScope: src }
        }
        if (!rollupByScope[src]) rollupByScope[src] = []
        rollupByScope[src].push(p)
      } else {
        // Multi-hop rollup - no direct relation column to attach to, show as own column
        own.push(p)
      }
    }
    // Helpers (virtual formula columns) surface right after the PK so the
    // temporary wizard-generated fields sit at the leftmost data positions -
    // users scan the diff from left to right and expect helper outputs to
    // appear before the persistent primitives they were derived from.
    const pkFirst = own.filter((p) => p.is_pk)
    const helpersFirst = own.filter((p) => !p.is_pk && isHelperPrim(p))
    const rest = own.filter((p) => !p.is_pk && !isHelperPrim(p))
    const ownOrdered = [...pkFirst, ...helpersFirst, ...rest]
    return { own: ownOrdered, rollupByScope }
  })()
  const visiblePrims = _splitPrims.own
  const rollupByScope = _splitPrims.rollupByScope
  const rollupPrimCount = Object.values(rollupByScope).reduce(
    (sum, arr) => sum + arr.length,
    0,
  )

  const groupPrims = visiblePrims.filter((p) => p.config?.is_group)
  const activeGroupPrim =
    groupPrims.find((p) => p.name === groupByPrimName) ?? null
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const isHierarchical = activeGroupPrim?.config?.is_hierarchical === true

  // Find the hierarchy primitive - check for level_labels in config (set during configuration)
  // or fall back to the legacy is_hierarchical flag
  const hierPrim =
    visiblePrims.find(
      (p) => p.config?.is_hierarchical || p.config?.level_labels,
    ) ?? null

  // Fetch group tree for level labels + hierarchical group-by rendering
  const { data: groupTree } = useQuery({
    // eslint-disable-next-line @tanstack/query/exhaustive-deps
    queryKey: ["group-tree", scopeId, hierPrim?.id],
    queryFn: () => api.getGroupTree(scopeId, hierPrim!.id),
    enabled: !!hierPrim,
  })

  // Fetch flat group node list (for potential future use)
  const { data: groupNodes } = useQuery({
    // eslint-disable-next-line @tanstack/query/exhaustive-deps
    queryKey: ["group-nodes", scopeId, hierPrim?.id],
    queryFn: () => api.listGroupNodes(scopeId, hierPrim!.id),
    enabled: !!hierPrim,
  })

  // Fetch hierarchy info for related scopes (used for relation column level dropdowns)
  // We need this before data is available, so we fetch for ALL known relation scopes
  // Pass scopeId as from_scope_id so counts reflect only records related to the current scope
  const relatedScopeIds =
    data?.scope.primitives
      .filter((p) => p.type === "relation" && p.related_scope_id)
      .map((p) => p.related_scope_id!)
      .filter((v, i, a) => a.indexOf(v) === i) ?? []
  const { data: relatedHierarchyInfos } = useQuery({
    queryKey: ["hierarchy-infos", scopeId, ...relatedScopeIds],
    queryFn: async () => {
      const results: Record<
        string,
        {
          scope_id: string
          primitive_id: string
          primitive_name: string
          primitive_title: string
          level_labels: Record<string, string>
          level_filter_options: {
            level: number
            label: string
            count: number
          }[]
          default_level: string
        }
      > = {}
      await Promise.all(
        relatedScopeIds.map(async (sid) => {
          try {
            const info = await api.getHierarchyInfo(sid, scopeId)
            results[sid] = info
          } catch {
            // Scope has no hierarchy - skip
          }
        }),
      )
      return results
    },
    enabled: relatedScopeIds.length > 0,
  })

  // Fetch transitive (multi-hop) relation counts for the active scope
  // Pass hierarchy level filters so transitive counts respect the same filter as direct relation counts
  // eslint-disable-next-line @tanstack/query/exhaustive-deps
  const { data: transitiveCounts } = useQuery({
    queryKey: ["transitive-counts", scopeId, rlFilterKey],
    queryFn: () => {
      const rlf =
        Object.keys(hierarchyLevelFilters).length > 0
          ? hierarchyLevelFilters
          : undefined
      return api.getTransitiveCounts(scopeId, rlf)
    },
    enabled: !recordSelection,
  })

  // Build a map: relatedScopeId → display label for the current hierarchy level filter
  const relationLevelLabels = useMemo(() => {
    const labels: Record<string, string> = {}
    if (!relatedHierarchyInfos) return labels
    for (const [sid, info] of Object.entries(relatedHierarchyInfos)) {
      const filter = hierarchyLevelFilters[sid]
      const opts = info.level_filter_options ?? []
      if (opts.length === 0) continue
      const effective = filter ?? "__leaf__"
      if (effective === "__all__") {
        labels[sid] = translate("QMS/PIVOT/FILTER_ALL")
      } else if (effective === "__leaf__") {
        const maxLevel = Math.max(...opts.map((o) => o.level))
        const leafLabels = new Set(
          opts.filter((o) => o.level === maxLevel).map((o) => o.label),
        )
        labels[sid] =
          leafLabels.size === 1
            ? [...leafLabels][0]
            : translate("QMS/PIVOT/FILTER_ALL_COMPONENTS")
      } else {
        labels[sid] = effective
      }
    }
    return labels
  }, [relatedHierarchyInfos, hierarchyLevelFilters])

  // --- Diff mode state (must be before early returns to maintain hook order) ---
  const [diffCommitting, setDiffCommitting] = useState(false)
  const commitAcceptedRef = useRef<() => Promise<void>>(() => Promise.resolve())

  const { removeScopeDiff, setActiveScopeId } = useStore()

  /** After committing this scope's diffs, remove it and switch to next scope */
  function finishScopeDiff() {
    const as = useStore.getState().actionState
    if (!as) {
      exitDiffMode()
      return
    }
    const remainingScopes = Object.keys(as.diffs).filter((s) => s !== scopeId)
    removeScopeDiff(scopeId)
    // If there are remaining scopes with diffs, switch to the first one
    if (remainingScopes.length > 0) {
      setActiveScopeId(remainingScopes[0])
    }
    // If no remaining scopes, removeScopeDiff already clears actionState
    qc.invalidateQueries({ queryKey: ["records", scopeId] })
    qc.invalidateQueries({ queryKey: ["transitive-counts", scopeId] })
  }

  // Import handler: triggered by Importieren button when all scopes are 100%
  const [importInProgress, setImportInProgress] = useState(false)
  const handleImport = useCallback(
    async (historyMode: HistoryMode) => {
      const as = useStore.getState().actionState
      if (!as) return
      setImportInProgress(true)
      try {
        // Commit each primary (non-secondary) scope diff sequentially
        for (const [sid, sd] of Object.entries(as.diffs)) {
          if (sd.secondaryDiff) continue
          const ds = { ...sd, scopeId: sid } as import("../../types").DiffState
          await commitDiffState(ds, historyMode)
        }
        // When history_mode is "all", the backend touches every PrimitiveValue
        // on the diff-visible + newly-created records - which can span multiple
        // scopes via secondary diffs, so refetch broadly. Await the refetch so
        // subsequent operations see fresh timestamps. Also invalidate history
        // caches - the commit creates new "Confirmed" entries.
        if (historyMode === "all") {
          await qc.refetchQueries({ queryKey: ["records"] })
          await qc.refetchQueries({ queryKey: ["transitive-counts"] })
          qc.invalidateQueries({ queryKey: ["history"] })
        } else {
          // Only invalidate the committed scopes + their relation targets
          for (const [sid, sd] of Object.entries(as.diffs)) {
            if (sd.secondaryDiff) continue
            await qc.refetchQueries({ queryKey: ["records", sid] })
            await qc.refetchQueries({ queryKey: ["transitive-counts", sid] })
          }
          qc.invalidateQueries({ queryKey: ["history"] })
        }
        cancelAction()
      } catch (err) {
        console.error("Import commit failed:", err)
      } finally {
        setImportInProgress(false)
      }
    },
    [cancelAction],
  )

  // Row confirmation is unified now - no user choice between "all" / "changed_only"
  // / "none". The backend always writes a 'Confirmed' history entry for every
  // row the user accepted; we always send history_mode="all" so that happens.
  const handleImportOrExportClick = useCallback(async () => {
    const isExport = !!(diffState?.exportMode || primaryDiffState?.exportMode)
    if (isExport) {
      await handleExport("all")
    } else {
      await handleImport("all")
    }
  }, [diffState, primaryDiffState, handleExport, handleImport])

  /** Commit accepted scalar cells + accepted relation diffs to the backend */
  async function commitDiffState(
    ds: DiffState,
    historyMode: HistoryMode = "changed_only",
  ) {
    // Scalar extractions: accepted cells
    const scalarAccepted = ds.rawExtractions
      .filter((ex) => ex.primitive_type !== "relation")
      .filter((ex) => {
        const cell = ds.records[ex.record_id]?.cells[ex.primitive_id]
        return cell?.accepted === true
      })
      .map((ex) => {
        const cell = ds.records[ex.record_id]?.cells[ex.primitive_id]
        return { ...ex, value: cell?.fileValue ?? ex.value, accepted: true }
      })

    // Relation extractions: accepted add entries from relationDiffs
    const relationAccepted: typeof scalarAccepted = []
    if (ds.relationDiffs) {
      for (const [recordId, primMap] of Object.entries(ds.relationDiffs)) {
        for (const [primitiveId, rd] of Object.entries(primMap)) {
          for (const entry of rd.entries) {
            if (entry.accepted !== true) continue
            if (entry.action === "add") {
              // Find matching raw extraction to get metadata, or build from scratch
              const rawEx = ds.rawExtractions.find(
                (ex) =>
                  ex.record_id === recordId &&
                  ex.primitive_id === primitiveId &&
                  ex.value === entry.targetId,
              )
              relationAccepted.push({
                record_id: recordId,
                primitive_id: primitiveId,
                primitive_scope_id: rawEx?.primitive_scope_id ?? ds.scopeId,
                primitive_title: rd.primitiveTitle,
                primitive_type: "relation",
                value: entry.targetId,
                raw_value: null,
                needs_cleaning: false,
                confidence: 1,
                extraction_type: "explicit" as const,
                related_scope_id: rd.relatedScopeId,
                accepted: true,
                mapped_id: entry.mappedId ?? null,
              })
            } else if (entry.action === "remove") {
              relationAccepted.push({
                record_id: recordId,
                primitive_id: primitiveId,
                primitive_scope_id: ds.scopeId,
                primitive_title: rd.primitiveTitle,
                primitive_type: "relation",
                value: entry.targetId,
                raw_value: null,
                needs_cleaning: false,
                confidence: 1,
                extraction_type: "explicit" as const,
                related_scope_id: rd.relatedScopeId,
                accepted: true,
                mapped_id: null,
                action: "remove",
              })
            }
          }
        }
      }
    }

    const allAccepted = [...scalarAccepted, ...relationAccepted]

    // For history_mode "all": send ALL anchored record IDs so the backend
    // touches every PrimitiveValue on those records (not just the diff cells).
    const touchRecordIds: string[] = []
    if (historyMode === "all") {
      for (const ar of ds.anchoredRecords) {
        touchRecordIds.push(ar.record_id)
      }
    }

    // Collect confirmed_record_ids from ALL scope diffs (including secondary)
    // where row freshness was accepted by the user.
    const confirmedRecordIds: string[] = []
    const actionState = useStore.getState().actionState
    if (actionState) {
      for (const sd of Object.values(actionState.diffs)) {
        for (const rec of Object.values(sd.records)) {
          if (rec.rowFreshnessAccepted === true) {
            confirmedRecordIds.push(rec.recordId)
          }
        }
      }
    }

    if (
      allAccepted.length > 0 ||
      touchRecordIds.length > 0 ||
      confirmedRecordIds.length > 0
    ) {
      const anchoredMap = new Map(
        ds.anchoredRecords.map((a) => [a.record_id, a]),
      )
      await api.ingestCommit(ds.scopeId, {
        document_name: ds.fileName,
        history_mode: historyMode,
        touch_record_ids: touchRecordIds,
        confirmed_record_ids: confirmedRecordIds,
        extractions: allAccepted.map((e) => {
          const anchor = anchoredMap.get(e.record_id)
          return {
            record_id: e.record_id,
            primitive_id: e.primitive_id,
            value: e.value,
            source: ds.fileName,
            confidence: e.confidence,
            extraction_type: e.extraction_type,
            needs_cleaning: e.needs_cleaning,
            confirmed_by_user: true,
            is_new: anchor?.is_new ?? false,
            pk_value: anchor?.is_new ? anchor.pk_value : undefined,
            mapped_id: e.mapped_id ?? undefined,
            ...(e.action === "remove" ? { action: "remove" } : {}),
          }
        }),
      })
      qc.invalidateQueries({ queryKey: ["records", ds.scopeId] })
      qc.invalidateQueries({ queryKey: ["transitive-counts", ds.scopeId] })
      // Invalidate related scopes so their relation/transitive columns refresh
      const relatedScopeIds = new Set<string>()
      if (ds.relationDiffs) {
        for (const primMap of Object.values(ds.relationDiffs)) {
          for (const rd of Object.values(primMap)) {
            if (rd.relatedScopeId) relatedScopeIds.add(rd.relatedScopeId)
          }
        }
      }
      for (const sid of relatedScopeIds) {
        qc.invalidateQueries({ queryKey: ["records", sid] })
        qc.invalidateQueries({ queryKey: ["transitive-counts", sid] })
      }
    }
  }

  // Keep commitAcceptedRef up to date for the store signal (cancel dialog "Apply Confirmed")
  useEffect(() => {
    commitAcceptedRef.current = async () => {
      if (!diffState) return
      setDiffCommitting(true)
      try {
        if (diffState.exportMode) {
          await handleExportDownload(diffState, translate)
        } else {
          await commitDiffState(diffState)
        }
        finishScopeDiff()
      } catch (err) {
        console.error("Diff commit/export failed:", err)
      } finally {
        setDiffCommitting(false)
      }
    }
  }, [isDiffMode, diffState])

  // Listen for commit-accepted signal from CancelDiffDialog (replaces window.__diffCommitAccepted)
  const commitSignal = useStore((s) => s._commitAcceptedSignal)
  useEffect(() => {
    if (commitSignal > 0 && isDiffMode) {
      commitAcceptedRef.current()
    }
  }, [commitSignal])

  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const handleVisibilityToggle = async (prim: PrimitiveDef) => {
    const newVisible = !prim.is_visible
    // Optimistically update all cached queries that contain this scope's primitives
    type CachedWithScope = { scope: { primitives: PrimitiveDef[] } }
    const patchPrimitives = (old: unknown) => {
      if (!old) return old
      const o = old as CachedWithScope
      return {
        ...o,
        scope: {
          ...o.scope,
          primitives: o.scope.primitives.map((p: PrimitiveDef) =>
            p.id === prim.id ? { ...p, is_visible: newVisible } : p,
          ),
        },
      }
    }
    qc.setQueriesData({ queryKey: ["records", scopeId] }, patchPrimitives)
    // Fire-and-forget the API call; invalidate afterward to sync with server
    api.setPrimitiveVisibility(scopeId, prim.id, newVisible).then(() => {
      qc.invalidateQueries({ queryKey: ["records", scopeId] })
    })
  }

  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const handleReorder = (orderedIds: string[]) => {
    type CachedWithScope = { scope: { primitives: PrimitiveDef[] } }
    // Optimistically rewrite display_order in cache
    const patchOrder = (old: unknown) => {
      if (!old) return old
      const o = old as CachedWithScope
      const orderMap = new Map(orderedIds.map((id, i) => [id, i]))
      return {
        ...o,
        scope: {
          ...o.scope,
          primitives: o.scope.primitives
            .map((p: PrimitiveDef) => ({
              ...p,
              display_order: orderMap.get(p.id) ?? p.display_order,
            }))
            .sort(
              (a: PrimitiveDef, b: PrimitiveDef) =>
                a.display_order - b.display_order,
            ),
        },
      }
    }
    qc.setQueriesData({ queryKey: ["records", scopeId] }, patchOrder)
    api.reorderPrimitives(scopeId, orderedIds).then(() => {
      qc.invalidateQueries({ queryKey: ["records", scopeId] })
    })
  }

  if (isLoading) return <LoadingState />
  if (error)
    return (
      <ErrorState
        message={error instanceof Error ? error.message : String(error)}
      />
    )
  if (!data) return <ErrorState message={translate("QMS/PIVOT/NO_DATA")} />

  const { scope, records } = data
  const scopeOrderIndex = new Map(allStoreScopes.map((s, i) => [s.id, i]))
  const relationPrims = scope.primitives
    .filter((p) => p.type === "relation")
    .sort((a, b) => {
      const ai = a.related_scope_id
        ? (scopeOrderIndex.get(a.related_scope_id) ?? Infinity)
        : Infinity
      const bi = b.related_scope_id
        ? (scopeOrderIndex.get(b.related_scope_id) ?? Infinity)
        : Infinity
      return ai - bi
    })

  // PK filter helpers - use unfiltered records so the dropdown always shows all PKs
  const pkPrim = scope.primitives.find((p) => p.is_pk)
  const allPkValues: string[] =
    pkPrim && allRecordsData
      ? allRecordsData.records
          .map((r) => r.values[pkPrim.name]?.value)
          .filter((v): v is string => v != null)
      : []
  const selectedPks = filter?.pkValues ?? []
  const pkSearchLower = pkSearch.toLowerCase()
  const matchingSelectedPks = selectedPks.filter((v) =>
    v.toLowerCase().includes(pkSearchLower),
  )
  const matchingUnselectedPks = allPkValues.filter(
    (v) => v.toLowerCase().includes(pkSearchLower) && !selectedPks.includes(v),
  )
  const handleTogglePk = (pk: string) => {
    if (selectedPks.includes(pk)) {
      removeFilterPk(pk)
    } else if (filter) {
      addFilterPks([pk])
    } else {
      setFilter(scopeId, [pk])
    }
  }

  // Build UUID → display name and UUID → level lookups for hierarchical group values
  const groupNodeNames = new Map<string, string>()
  const groupNodeLevels = new Map<string, number>()
  if (groupNodes) {
    for (const n of groupNodes) {
      groupNodeNames.set(n.id, n.name)
      groupNodeLevels.set(n.id, n.level)
    }
  }

  // Level filter options - each is a selectable entry in the dropdown.
  // For levels with a single Materialgruppe value, there's one option.
  // For mixed levels (e.g. leaf parts), each distinct value is its own option.
  type FilterOption = { level: number; label: string; count: number }
  const filterOptions: FilterOption[] = (() => {
    // 1. Prefer level_filter_options from tree API or config (populated by pk_prefix mode)
    const fromTree = groupTree?.level_filter_options
    const fromConfig = hierPrim?.config?.level_filter_options as
      | FilterOption[]
      | undefined
    let raw: FilterOption[]
    if (fromTree && fromTree.length > 0) raw = fromTree
    else if (fromConfig && fromConfig.length > 0) raw = fromConfig
    else {
      // 2. Fallback: one option per level from level_labels (legacy/other modes)
      const levelLabels: Record<number, string> = {}
      if (hierPrim?.config?.level_labels) {
        const stored = hierPrim.config.level_labels as Record<string, string>
        for (const [k, v] of Object.entries(stored)) levelLabels[Number(k)] = v
      }
      if (groupTree?.level_labels) {
        for (const [k, v] of Object.entries(groupTree.level_labels)) {
          const lv = Number(k)
          const isGeneric = /^Level \d+$/.test(v)
          if (!levelLabels[lv] || !isGeneric) levelLabels[lv] = v
        }
      } else if (Object.keys(levelLabels).length === 0 && groupNodes) {
        const levels = new Set(groupNodes.map((n) => n.level))
        for (const lv of levels)
          levelLabels[lv] = translate("QMS/PIVOT/LEVEL_FALLBACK", { lv })
      }
      raw = Object.entries(levelLabels)
        .map(([k, v]) => ({ level: Number(k), label: v, count: 0 }))
        .sort((a, b) => a.level - b.level)
    }
    // Deduplicate by label: if the same label appears at multiple levels
    // (e.g. "Einzelteil" at level 1 and 2), keep only the deepest entry
    const seen = new Set<string>()
    const deduped: FilterOption[] = []
    for (let i = raw.length - 1; i >= 0; i--) {
      const key = raw[i].label.toLowerCase()
      if (!seen.has(key)) {
        seen.add(key)
        deduped.push(raw[i])
      }
    }
    deduped.reverse()
    return deduped
  })()

  // Build set of all known filter option labels (lowercased) for quick lookup
  const allFilterLabels = new Set(
    filterOptions.map((o) => o.label.toLowerCase()),
  )

  // Helper: is a record connected to the hierarchy tree?
  const _isConnected = (rec: QmsRecord) =>
    rec.values["bom_connected"]?.value?.toLowerCase() === "true"

  // Compute record counts per filter option from actual data.
  // Every distinct Materialgruppe value is its own filter - no lumping.
  const filterOptionCounts: Map<string, number> = new Map()
  let ungroupedCount = 0
  if (hierPrim) {
    for (const rec of records) {
      if (!_isConnected(rec)) {
        ungroupedCount++
        continue
      }
      const val = rec.values[hierPrim.name]?.value?.toLowerCase() ?? ""
      if (val && allFilterLabels.has(val)) {
        filterOptionCounts.set(val, (filterOptionCounts.get(val) || 0) + 1)
      } else if (val) {
        // Connected record whose label isn't in filter options - count as its own label
        filterOptionCounts.set(val, (filterOptionCounts.get(val) || 0) + 1)
      } else {
        ungroupedCount++
      }
    }
  }

  // Special sentinels for filter options
  const UNGROUPED = "__ungrouped__"
  const ALL_LEVELS = "__all__"
  const LEAF_LEVEL = "__leaf__"

  // Leaf-level: all labels at the deepest hierarchy level
  const maxLevel =
    filterOptions.length > 0
      ? Math.max(...filterOptions.map((o) => o.level))
      : -1
  const leafLabels = new Set(
    filterOptions
      .filter((o) => o.level === maxLevel)
      .map((o) => o.label.toLowerCase()),
  )
  const leafCount = (() => {
    let c = 0
    for (const lbl of leafLabels) c += filterOptionCounts.get(lbl) ?? 0
    return c
  })()

  // Default to leaf level (deepest nodes)
  const defaultLabel = filterOptions.length > 0 ? LEAF_LEVEL : null
  const effectiveLabel = hierLevelFilter ?? defaultLabel

  // Filter records by selected label - exact match on Materialgruppe value
  const filteredRecords =
    hierPrim && effectiveLabel
      ? effectiveLabel === UNGROUPED
        ? records.filter((rec) => !_isConnected(rec))
        : effectiveLabel === ALL_LEVELS
          ? records.filter((rec) => _isConnected(rec))
          : effectiveLabel === LEAF_LEVEL
            ? records.filter((rec) => {
                if (!_isConnected(rec)) return false
                const val =
                  rec.values[hierPrim.name]?.value?.toLowerCase() ?? ""
                return leafLabels.has(val)
              })
            : records.filter((rec) => {
                if (!_isConnected(rec)) return false
                const val = rec.values[hierPrim.name]?.value
                return val?.toLowerCase() === effectiveLabel.toLowerCase()
              })
      : records

  // --- Diff mode: filter records ---
  const diffFilteredRecords =
    isDiffMode && diffState
      ? (() => {
          if (isSecondaryDiff) {
            // Only show records that have diff cells (filters out empty rows when freshness is off)
            const diffRecordIds = new Set(Object.keys(scopeDiff!.records))
            return filteredRecords.filter((rec) => diffRecordIds.has(rec.id))
          }
          // Export diffs: show ALL records so the user can review/verify them
          if (diffState.exportMode) {
            return filteredRecords.filter((rec) => !!diffState.records[rec.id])
          }
          const existingIds = new Set(filteredRecords.map((r) => r.id))
          // Show ALL records with diffs (both unresolved and accepted)
          const matched = filteredRecords.filter(
            (rec) => !!diffState.records[rec.id],
          )
          // Inject virtual QmsRecord objects for new records not yet in the DB
          for (const [rid, diffRec] of Object.entries(diffState.records)) {
            if (existingIds.has(rid)) continue
            if (!diffRec.isNew) continue
            matched.push({
              id: rid,
              scope_id: scopeId,
              created_at: new Date().toISOString(),
              values: Object.fromEntries(
                Object.values(diffRec.cells).map((c) => [
                  c.primitiveName,
                  {
                    primitive_id: c.primitiveId,
                    primitive_name: c.primitiveName,
                    value: c.fileValue,
                    source: "pending",
                    confidence: null,
                    extraction_type: null,
                    needs_cleaning: false,
                    confirmed_by_user: false,
                    updated_at: "",
                  },
                ]),
              ),
              relation_tags: [],
              mapping_values: {},
              updated_at: null,
              updated_source: null,
            })
          }
          return matched
        })()
      : inDiffPhase && !storeFreshnessDate
        ? [] // No diff for this scope + no freshness filter → nothing to review
        : filteredRecords

  // Build primitive name → title map for formula display
  const primitiveTitleMap: Record<string, string> = {}
  for (const p of visiblePrims)
    primitiveTitleMap[p.name] = getPrimitiveDisplayTitle(p, currentLanguage)

  // Compute formula error counts across all (non-diff-filtered) records
  const formulaPrims = visiblePrims.filter(
    (p) => p.type === "formula" && p.config?.formula,
  )
  const formulaErrorCounts: Record<string, number> = {}
  for (const fp of formulaPrims) {
    let count = 0
    for (const rec of filteredRecords) {
      const { error } = resolveFormula(
        fp.config!.formula!,
        rec.values,
        translate,
      )
      if (error) count++
    }
    if (count > 0) formulaErrorCounts[fp.name] = count
  }

  // Apply diff filter mode (rows / changes / updates)
  const diffModeFilteredRecords =
    isDiffMode && diffState && diffFilterMode !== "rows"
      ? diffFilteredRecords.filter((rec) => {
          const diffRec = diffState.records[rec.id]
          if (!diffRec) return false
          const cells = Object.values(diffRec.cells)
          if (diffFilterMode === "changes") {
            return cells.some((c) => !c.secondary && c.accepted === null)
          }
          // 'updates'
          return cells.some((c) => c.secondary && c.accepted === null)
        })
      : diffFilteredRecords

  // Apply formula error filter
  const baseRecords = isDiffMode ? diffModeFilteredRecords : filteredRecords
  const displayRecords = formulaErrorFilter
    ? baseRecords.filter((rec) => {
        const fp = formulaPrims.find((p) => p.name === formulaErrorFilter)
        if (!fp) return true
        const { error } = resolveFormula(
          fp.config!.formula!,
          rec.values,
          translate,
        )
        return !!error
      })
    : baseRecords

  return (
    <GroupNodeNamesCtx.Provider value={groupNodeNames}>
      <ScopeOrderCtx.Provider value={scopeOrderIndex}>
        <TransitiveCountsCtx.Provider value={transitiveCounts ?? null}>
          <div
            className={clsx(
              "flex h-full flex-col transition-colors",
              draggingOver && "bg-yellow5/30",
            )}
            onDragOver={handleDragOver}
            onDragLeave={handleDragLeave}
            onDrop={handleDrop}
          >
            {/* Toolbar */}
            {isDiffMode || primaryDiffState ? (
              <div className="border-b bg-grey2 px-4 py-2">
                <DiffToolbar
                  diffState={(() => {
                    const ds = (diffState ?? primaryDiffState)!
                    // Restrict to only the records visible in the current diff view
                    // so the progress bar matches what the user sees.
                    const visibleIds = new Set(
                      diffFilteredRecords.map((r) => r.id),
                    )
                    const filtered: Record<
                      string,
                      import("../../types").DiffRecord
                    > = {}
                    for (const [id, rec] of Object.entries(ds.records)) {
                      if (visibleIds.has(id)) filtered[id] = rec
                    }
                    return { ...ds, records: filtered }
                  })()}
                  onCancel={openCancelDiffDialog}
                  allScopeDiffs={actionState?.diffs}
                  scopeNames={scopeNamesMap}
                  onExport={
                    diffState?.exportMode || primaryDiffState?.exportMode
                      ? handleImportOrExportClick
                      : undefined
                  }
                  exporting={exportInProgress}
                  onImport={
                    !(diffState?.exportMode || primaryDiffState?.exportMode)
                      ? handleImportOrExportClick
                      : undefined
                  }
                  importing={importInProgress}
                />
                {(diffCommitting || importInProgress) && (
                  <div className="mt-2 flex items-center gap-2 text-xs text-grey5">
                    <span className="h-3 w-3 animate-spin rounded-full border-2 border-grey4 border-t-yellow1" />
                    {diffState?.exportMode
                      ? translate("QMS/PIVOT/DOWNLOAD_PREPARING")
                      : translate("QMS/PIVOT/APPLYING_CHANGES")}
                  </div>
                )}
              </div>
            ) : (
              <div className="flex flex-wrap items-center gap-2 border-b bg-grey2 px-4 py-2">
                {scope.description && (
                  <p className="text-xs text-grey5">{scope.description}</p>
                )}

                {/* Record selection badge */}
                {recordSelection && (
                  <button
                    onClick={openRecordSelectionEditor}
                    className="flex items-center gap-1.5 rounded-lg border border-yellow5 bg-yellow5/20 px-3 py-1.5 text-xs font-medium text-yellow1 transition-colors hover:bg-yellow5/30"
                  >
                    <svg
                      className="h-3.5 w-3.5"
                      fill="none"
                      viewBox="0 0 24 24"
                      stroke="currentColor"
                      strokeWidth={2}
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2m-6 9l2 2 4-4"
                      />
                    </svg>
                    {recordSelection.pkSets
                      .flatMap((ps) => ps.fileNames)
                      .join(", ")}
                    <span className="rounded-full bg-yellow1 px-1.5 py-0.5 text-[10px] font-bold leading-none text-black">
                      {data?.records.length ?? "..."}
                    </span>
                  </button>
                )}

                <div className="ml-auto flex items-center gap-2">
                  {groupPrims.length > 0 && (
                    <select
                      value={groupByPrimName ?? ""}
                      onChange={(e) =>
                        setGroupByPrimName(e.target.value || null)
                      }
                      className="rounded-lg border border-grey4 bg-grey2 px-2 py-1.5 text-xs text-grey5 transition-colors hover:bg-grey3"
                      title={translate("QMS/PIVOT/GROUP_BY_FIELD")}
                    >
                      <option value="">
                        {translate("QMS/PIVOT/GROUP_BY_PLACEHOLDER")}
                      </option>
                      {groupPrims.map((p) => (
                        <option key={p.name} value={p.name}>
                          {getPrimitiveDisplayTitle(p, currentLanguage)}
                        </option>
                      ))}
                    </select>
                  )}
                  {!selectMode &&
                    !hideToolbarActions &&
                    !scope.is_singleton && (
                      <button
                        onClick={() => openEdit("new")}
                        className="rounded-lg bg-yellow1 px-3 py-1.5 text-sm font-medium text-black transition-colors hover:bg-yellow3"
                      >
                        {translate("QMS/PIVOT/NEW_RECORD_BTN", {
                          name: getScopeDisplayName(scope, currentLanguage),
                        })}
                      </button>
                    )}
                  {selectMode && (
                    <>
                      {/* Select All */}
                      <button
                        onClick={() =>
                          selectRecords(filteredRecords.map((r) => r.id))
                        }
                        className="rounded-lg border border-grey4 bg-grey2 px-3 py-1.5 text-sm font-medium text-grey6 transition-colors hover:bg-grey3"
                        title={translate("QMS/PIVOT/SELECT_ALL_TITLE")}
                      >
                        {translate("QMS/PIVOT/SELECT_ALL")}
                      </button>
                      {/* Deselect All */}
                      <button
                        onClick={() =>
                          deselectRecords(filteredRecords.map((r) => r.id))
                        }
                        className="rounded-lg border border-grey4 bg-grey2 px-3 py-1.5 text-sm font-medium text-grey6 transition-colors hover:bg-grey3"
                        title={translate("QMS/PIVOT/DESELECT_ALL_TITLE")}
                      >
                        {translate("QMS/PIVOT/DESELECT_ALL")}
                      </button>
                    </>
                  )}
                  {/* Download / Select button - hidden on non-primary scopes during active action.
              In importer-AP idle (forceSelectMode) the selection is implicit, so this
              button is also hidden - there's no opt-in step. */}
                  {!hideToolbarActions && !forceSelectMode && (
                    <button
                      onClick={() => {
                        if (downloadMode && selectedRecordIds.size > 0) {
                          setColumnPickerOpen(true)
                        } else if (!downloadMode) {
                          toggleDownloadMode()
                        }
                      }}
                      disabled={downloadMode && selectedRecordIds.size === 0}
                      className={clsx(
                        "flex items-center gap-1.5 rounded-lg px-3 py-1.5 text-sm font-medium transition-colors",
                        downloadMode
                          ? selectedRecordIds.size > 0
                            ? "bg-yellow1 text-black hover:bg-yellow3"
                            : "cursor-not-allowed border border-grey4 bg-grey2 text-grey5 opacity-50"
                          : "border border-grey4 bg-grey2 text-grey6 hover:bg-grey3",
                      )}
                      title={
                        downloadMode
                          ? selectedRecordIds.size > 0
                            ? translate("QMS/PIVOT/CONFIGURE_DOWNLOAD")
                            : translate("QMS/PIVOT/SELECT_FIRST")
                          : translate("QMS/PIVOT/SELECT_RECORDS_TITLE")
                      }
                    >
                      <svg
                        className="h-4 w-4"
                        fill="none"
                        viewBox="0 0 24 24"
                        stroke="currentColor"
                        strokeWidth={2}
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2m-6 9l2 2 4-4"
                        />
                      </svg>
                      {downloadMode
                        ? translate("QMS/PIVOT/EXPORT_COUNT", {
                            count: selectedRecordIds.size,
                          })
                        : translate("QMS/PIVOT/SELECT")}
                    </button>
                  )}
                  {downloadMode && (
                    <button
                      onClick={exitDownloadMode}
                      className="rounded-lg border border-red1/40 bg-red1/10 px-3 py-1.5 text-sm font-medium text-red1 transition-colors hover:bg-red1/20"
                      title={translate("QMS/PIVOT/CANCEL_SELECTION_TITLE")}
                    >
                      {translate("QMS/COMMON/CANCEL")}
                    </button>
                  )}
                  {/* Import button - hidden in importer-AP idle: the AP has its own
              bucket-import flow on the Aktionen tab. */}
                  {!selectMode && !hideToolbarActions && (
                    <button
                      onClick={() => fileInputRef.current?.click()}
                      className="flex items-center gap-1.5 rounded-lg border border-grey4 bg-grey2 px-3 py-1.5 text-sm font-medium text-grey6 transition-colors hover:bg-grey3"
                      title={translate("QMS/PIVOT/IMPORT_DOC_TITLE")}
                    >
                      <span className="text-base leading-none">+</span>{" "}
                      {translate("QMS/PIVOT/IMPORT_BTN")}
                    </button>
                  )}
                  <input
                    ref={fileInputRef}
                    type="file"
                    accept=".xlsx,.xls,.xlsm,.csv,.pdf,.txt"
                    className="hidden"
                    onChange={handleFileInput}
                  />
                </div>
              </div>
            )}

            {/* Drop overlay hint */}
            {draggingOver && (
              <div className="pointer-events-none absolute inset-0 z-50 flex items-center justify-center">
                <div className="rounded-2xl bg-yellow1 px-6 py-4 text-lg font-semibold text-black shadow-xl">
                  {translate("QMS/PIVOT/DROP_TO_IMPORT", {
                    name: getScopeDisplayName(scope, currentLanguage),
                  })}
                </div>
              </div>
            )}

            {/* Settings panel removed - now in ConfigurationView */}

            {/* Table */}
            <div ref={tableScrollRef} className="flex-1 overflow-auto">
              <table className="w-full border-collapse text-sm">
                <thead>
                  <tr className="sticky top-0 z-10 border-b bg-grey1">
                    {/* Actions column - diff mode has an Accept Row button per row */}
                    {isDiffMode && (
                      <th
                        ref={diffActionColRef}
                        className="sticky left-0 z-20 border-r border-grey4 bg-grey1 px-2 py-2.5"
                        style={{
                          width: "7rem",
                          minWidth: "7rem",
                          maxWidth: "7rem",
                        }}
                      >
                        <button
                          onClick={acceptAllDiffs}
                          className="rounded bg-success/20 px-2 py-1 text-xs font-medium text-success transition-colors hover:bg-success/30"
                        >
                          {translate("QMS/PIVOT/ACCEPT_ALL_ROWS")}
                        </button>
                      </th>
                    )}
                    {/* Select column - only in select/download mode, hidden during diff review */}
                    {selectMode && !isDiffMode && (
                      <th className="sticky left-0 z-20 w-10 border-r border-grey4 bg-grey1 px-2 py-2.5 text-center">
                        <div className="flex flex-col items-center gap-0.5">
                          <input
                            type="checkbox"
                            checked={
                              filteredRecords.length > 0 &&
                              filteredRecords.every((r) =>
                                selectedRecordIds.has(r.id),
                              )
                            }
                            onChange={(e) => {
                              const ids = filteredRecords.map((r) => r.id)
                              if (e.target.checked) selectRecords(ids)
                              else deselectRecords(ids)
                            }}
                            className="h-4 w-4 rounded border-grey4 text-yellow1 focus:ring-yellow1"
                            title={translate(
                              "QMS/PIVOT/SELECT_ALL_DESELECT_TITLE",
                            )}
                          />
                          <span className="whitespace-nowrap text-[10px] text-grey5">
                            {translate("QMS/PIVOT/SELECTED_COUNT", {
                              count: selectedRecordIds.size,
                            })}
                          </span>
                        </div>
                      </th>
                    )}
                    {visiblePrims.map((p) => {
                      const isHierCol =
                        hierPrim &&
                        p.id === hierPrim.id &&
                        filterOptions.length > 1
                      const isPk = p.is_pk
                      const rollupScope = p.config?._rollupSourceScope as
                        | string
                        | undefined
                      const isHelper = isHelperPrim(p)
                      return (
                        <th
                          key={p.id}
                          className={clsx(
                            "whitespace-nowrap border-r border-grey4/50 px-4 py-2.5 text-left font-medium last:border-r-0",
                            isHelper
                              ? HELPER_HEADER_CLASS
                              : rollupScope
                                ? (ROLLUP_SCOPE_HEADER[rollupScope] ??
                                  "text-grey5")
                                : "text-grey5",
                            isPk &&
                              selectMode &&
                              "sticky left-10 z-20 border-r border-grey4 bg-grey1",
                            isPk &&
                              !selectMode &&
                              isDiffMode &&
                              "sticky z-20 border-r border-grey4 bg-grey1",
                            isPk &&
                              !selectMode &&
                              !isDiffMode &&
                              "sticky left-0 z-20 border-r border-grey4 bg-grey1",
                          )}
                          style={
                            isPk && !selectMode && isDiffMode
                              ? { left: "var(--diff-action-col-w, 6rem)" }
                              : undefined
                          }
                        >
                          {isHierCol ? (
                            <div className="relative">
                              <button
                                onClick={() => setLevelDropdownOpen((v) => !v)}
                                className="flex items-center gap-1.5 transition-colors hover:text-yellow1"
                              >
                                <PrimitiveTypeIcon type={p.type} />
                                <span>
                                  {getPrimitiveDisplayTitle(p, currentLanguage)}
                                </span>
                                <svg
                                  className="h-3 w-3 text-grey5"
                                  viewBox="0 0 12 12"
                                  fill="none"
                                >
                                  <path
                                    d="M3 5l3 3 3-3"
                                    stroke="currentColor"
                                    strokeWidth="1.5"
                                    strokeLinecap="round"
                                    strokeLinejoin="round"
                                  />
                                </svg>
                              </button>
                              <div className="mt-0.5 text-[10px] font-normal text-grey5">
                                {effectiveLabel === UNGROUPED
                                  ? translate("QMS/PIVOT/FILTER_UNGROUPED")
                                  : effectiveLabel === ALL_LEVELS
                                    ? translate("QMS/PIVOT/FILTER_ALL")
                                    : effectiveLabel === LEAF_LEVEL
                                      ? translate(
                                          "QMS/PIVOT/FILTER_ALL_COMPONENTS",
                                        )
                                      : (effectiveLabel ??
                                        translate(
                                          "QMS/PIVOT/FILTER_ALL_LEVELS",
                                        ))}{" "}
                                &middot; {filteredRecords.length}/
                                {records.length}
                              </div>
                              {levelDropdownOpen && (
                                <>
                                  <div
                                    className="fixed inset-0 z-20"
                                    onClick={() => setLevelDropdownOpen(false)}
                                  />
                                  <div className="absolute left-0 top-full z-30 mt-1 max-h-[320px] min-w-[160px] overflow-y-auto rounded-lg border border-grey4 bg-grey2 py-1 shadow-lg">
                                    {/* "Alle" option - flattens all levels into one view */}
                                    <button
                                      onClick={() => {
                                        setHierLevelFilter(ALL_LEVELS)
                                        setLevelDropdownOpen(false)
                                      }}
                                      className={clsx(
                                        "flex w-full items-center justify-between gap-3 px-3 py-1.5 text-left text-xs transition-colors",
                                        effectiveLabel === ALL_LEVELS
                                          ? "bg-yellow5/20 font-medium text-yellow1"
                                          : "text-grey5 hover:bg-grey3",
                                      )}
                                    >
                                      <span>
                                        {translate("QMS/PIVOT/FILTER_ALL")}
                                      </span>
                                      <span
                                        className={clsx(
                                          "text-[10px] tabular-nums",
                                          effectiveLabel === ALL_LEVELS
                                            ? "text-yellow1"
                                            : "text-grey5",
                                        )}
                                      >
                                        {
                                          records.filter((r) => _isConnected(r))
                                            .length
                                        }
                                        /{records.length}
                                      </span>
                                    </button>
                                    {/* "Alle Komponenten" - all leaf-level nodes at once */}
                                    {leafLabels.size > 1 && (
                                      <button
                                        onClick={() => {
                                          setHierLevelFilter(LEAF_LEVEL)
                                          setLevelDropdownOpen(false)
                                        }}
                                        className={clsx(
                                          "flex w-full items-center justify-between gap-3 px-3 py-1.5 text-left text-xs transition-colors",
                                          effectiveLabel === LEAF_LEVEL
                                            ? "bg-yellow5/20 font-medium text-yellow1"
                                            : "text-grey5 hover:bg-grey3",
                                        )}
                                      >
                                        <span>
                                          {translate(
                                            "QMS/PIVOT/FILTER_ALL_COMPONENTS",
                                          )}
                                        </span>
                                        <span
                                          className={clsx(
                                            "text-[10px] tabular-nums",
                                            effectiveLabel === LEAF_LEVEL
                                              ? "text-yellow1"
                                              : "text-grey5",
                                          )}
                                        >
                                          {leafCount}/{records.length}
                                        </span>
                                      </button>
                                    )}
                                    <div className="my-0.5 border-t border-grey4/50" />
                                    {filterOptions.map((opt, idx) => {
                                      const isActive =
                                        effectiveLabel === opt.label
                                      const count =
                                        filterOptionCounts.get(
                                          opt.label.toLowerCase(),
                                        ) ?? 0
                                      // Show level separator when level changes
                                      const prevLevel =
                                        idx > 0
                                          ? filterOptions[idx - 1].level
                                          : -1
                                      const showSep =
                                        opt.level !== prevLevel && idx > 0
                                      return (
                                        <div key={`${opt.level}-${opt.label}`}>
                                          {showSep && (
                                            <div className="my-0.5 border-t border-grey4/50" />
                                          )}
                                          <button
                                            onClick={() => {
                                              setHierLevelFilter(opt.label)
                                              setLevelDropdownOpen(false)
                                            }}
                                            className={clsx(
                                              "flex w-full items-center justify-between gap-3 px-3 py-1.5 text-left text-xs transition-colors",
                                              isActive
                                                ? "bg-yellow5/20 font-medium text-yellow1"
                                                : "text-grey5 hover:bg-grey3",
                                            )}
                                          >
                                            <span>{opt.label}</span>
                                            <span
                                              className={clsx(
                                                "text-[10px] tabular-nums",
                                                isActive
                                                  ? "text-yellow1"
                                                  : "text-grey5",
                                              )}
                                            >
                                              {count}/{records.length}
                                            </span>
                                          </button>
                                        </div>
                                      )
                                    })}
                                    {ungroupedCount > 0 && (
                                      <>
                                        <div className="my-0.5 border-t border-grey4/50" />
                                        <button
                                          onClick={() => {
                                            setHierLevelFilter(UNGROUPED)
                                            setLevelDropdownOpen(false)
                                          }}
                                          className={clsx(
                                            "flex w-full items-center justify-between gap-3 px-3 py-1.5 text-left text-xs transition-colors",
                                            effectiveLabel === UNGROUPED
                                              ? "bg-warning/10 font-medium text-warning"
                                              : "text-warning/70 hover:bg-warning/10",
                                          )}
                                        >
                                          <span>
                                            {translate(
                                              "QMS/PIVOT/FILTER_UNGROUPED",
                                            )}
                                          </span>
                                          <span
                                            className={clsx(
                                              "text-[10px] tabular-nums",
                                              effectiveLabel === UNGROUPED
                                                ? "text-warning"
                                                : "text-warning",
                                            )}
                                          >
                                            {ungroupedCount}/{records.length}
                                          </span>
                                        </button>
                                      </>
                                    )}
                                  </div>
                                </>
                              )}
                            </div>
                          ) : p.is_pk ? (
                            <div className="relative">
                              <button
                                onClick={() => setPkDropdownOpen((v) => !v)}
                                className="flex items-center gap-1.5 transition-colors hover:text-yellow1"
                              >
                                <PrimitiveTypeIcon type={p.type} />
                                <span>
                                  {getPrimitiveDisplayTitle(p, currentLanguage)}
                                </span>
                                <span className="text-xs text-warning">PK</span>
                                <svg
                                  className="h-3 w-3 text-grey5"
                                  viewBox="0 0 12 12"
                                  fill="none"
                                >
                                  <path
                                    d="M3 5l3 3 3-3"
                                    stroke="currentColor"
                                    strokeWidth="1.5"
                                    strokeLinecap="round"
                                    strokeLinejoin="round"
                                  />
                                </svg>
                              </button>
                              <div className="mt-0.5 text-[10px] font-normal text-grey5">
                                {filter ? (
                                  <>
                                    {translate("QMS/PIVOT/FILTER_LABEL", {
                                      count: filter.pkValues.length,
                                    })}{" "}
                                    &middot;{" "}
                                    <button
                                      onClick={clearFilter}
                                      className="text-red1 transition-colors hover:text-red1"
                                    >
                                      {translate("QMS/PIVOT/RESET_FILTER")}
                                    </button>
                                  </>
                                ) : (
                                  translate("QMS/PIVOT/NO_FILTER")
                                )}
                              </div>
                              {pkDropdownOpen && (
                                <>
                                  <div
                                    className="fixed inset-0 z-20"
                                    onClick={() => {
                                      setPkDropdownOpen(false)
                                      setPkSearch("")
                                    }}
                                  />
                                  <div className="absolute left-0 top-full z-30 mt-1 min-w-[220px] rounded-lg border border-grey4 bg-grey2 shadow-lg">
                                    <div className="border-b border-grey4/50 p-2">
                                      <input
                                        type="text"
                                        value={pkSearch}
                                        onChange={(e) =>
                                          setPkSearch(e.target.value)
                                        }
                                        placeholder={translate(
                                          "QMS/PIVOT/PK_SEARCH_PLACEHOLDER",
                                          {
                                            name: getScopeDisplayName(
                                              scope,
                                              currentLanguage,
                                            ),
                                          },
                                        )}
                                        className="w-full rounded border border-grey4 px-2 py-1.5 text-xs focus:border-yellow1 focus:outline-hidden focus:ring-2 focus:ring-yellow5"
                                        autoFocus
                                      />
                                    </div>
                                    <div className="max-h-60 overflow-y-auto">
                                      {matchingSelectedPks.length === 0 &&
                                      matchingUnselectedPks.length === 0 ? (
                                        <p className="px-3 py-2 text-xs text-grey5">
                                          {translate(
                                            "QMS/PIVOT/NO_MATCHING_PKS",
                                          )}
                                        </p>
                                      ) : (
                                        <>
                                          {matchingSelectedPks.map((pk) => (
                                            <label
                                              key={pk}
                                              className="flex cursor-pointer items-center gap-2 bg-yellow5/20 px-3 py-1.5 text-xs hover:bg-yellow5/30"
                                            >
                                              <input
                                                type="checkbox"
                                                checked
                                                onChange={() =>
                                                  handleTogglePk(pk)
                                                }
                                                className="accent-yellow1"
                                              />
                                              <span className="truncate font-medium text-yellow1">
                                                {pk}
                                              </span>
                                            </label>
                                          ))}
                                          {matchingSelectedPks.length > 0 &&
                                            matchingUnselectedPks.length >
                                              0 && (
                                              <div className="border-t border-grey4/50" />
                                            )}
                                          {matchingUnselectedPks.map((pk) => (
                                            <label
                                              key={pk}
                                              className="flex cursor-pointer items-center gap-2 px-3 py-1.5 text-xs hover:bg-grey3"
                                            >
                                              <input
                                                type="checkbox"
                                                checked={false}
                                                onChange={() =>
                                                  handleTogglePk(pk)
                                                }
                                                className="accent-yellow1"
                                              />
                                              <span className="truncate">
                                                {pk}
                                              </span>
                                            </label>
                                          ))}
                                        </>
                                      )}
                                    </div>
                                  </div>
                                </>
                              )}
                            </div>
                          ) : p.type === "formula" ? (
                            <FormulaColumnHeader
                              prim={p}
                              errorCount={formulaErrorCounts[p.name] ?? 0}
                              totalCount={filteredRecords.length}
                              filterActive={formulaErrorFilter === p.name}
                              onToggleFilter={() =>
                                setFormulaErrorFilter((prev) =>
                                  prev === p.name ? null : p.name,
                                )
                              }
                              primitiveTitleMap={primitiveTitleMap}
                            />
                          ) : isHelper ? (
                            <div>
                              <div className="flex items-center gap-1.5">
                                <PrimitiveTypeIcon type={p.type} />
                                {getPrimitiveDisplayTitle(p, currentLanguage)}
                              </div>
                              <div
                                className={clsx(
                                  "mt-0.5 text-[10px] font-normal",
                                  HELPER_SUBTITLE_CLASS,
                                )}
                              >
                                {translate("QMS/HELPER/GROUP_LABEL")}
                              </div>
                            </div>
                          ) : rollupScope ? (
                            <div>
                              <div className="flex items-center gap-1.5">
                                <PrimitiveTypeIcon type={p.type} />
                                {getPrimitiveDisplayTitle(p, currentLanguage)}
                              </div>
                              <div
                                className={clsx(
                                  "mt-0.5 text-[10px] font-normal",
                                  ROLLUP_SCOPE_TEXT[rollupScope] ??
                                    "text-grey5",
                                )}
                              >
                                {translate("QMS/PIVOT/AGGREGATION_SUFFIX", {
                                  scope: (() => {
                                    const s = allStoreScopes.find(
                                      (sc) => sc.id === rollupScope,
                                    )
                                    return s
                                      ? getScopeDisplayName(s, currentLanguage)
                                      : rollupScope
                                  })(),
                                })}
                              </div>
                            </div>
                          ) : (
                            <div className="flex items-center gap-1.5">
                              <PrimitiveTypeIcon type={p.type} />
                              {getPrimitiveDisplayTitle(p, currentLanguage)}
                              {p.config?.mapped_to && (
                                <span
                                  className="text-xs text-yellow1"
                                  title={translate("QMS/PIVOT/MAPPED_TO", {
                                    target: p.config.mapped_to,
                                  })}
                                >
                                  ⇄
                                </span>
                              )}
                              {p.config?.is_group && (
                                <span
                                  className="text-xs text-warning"
                                  title={translate(
                                    "QMS/PIVOT/GROUP_BADGE_TITLE",
                                  )}
                                >
                                  ⊞
                                </span>
                              )}
                              {p.config?.mapping_for && (
                                <span
                                  className="text-xs text-purple1"
                                  title={translate(
                                    "QMS/PIVOT/MAPPING_SOURCE_TITLE",
                                  )}
                                >
                                  ⇄
                                </span>
                              )}
                            </div>
                          )}
                        </th>
                      )
                    })}
                    {buildRelationSlots(
                      relationPrims.filter((p) => p.is_visible),
                      transitiveCounts?.transitive_scopes,
                      scopeOrderIndex,
                    ).flatMap((slot) => {
                      if (slot.kind === "transitive") {
                        const ts = slot.ts
                        return [
                          <th
                            key={`trans-${ts.scope_id}`}
                            className="whitespace-nowrap border-r border-grey4/50 px-4 py-2.5 text-left font-medium text-grey5 last:border-r-0"
                          >
                            <span className="flex items-center gap-1">
                              {getScopeDisplayName(
                                { id: ts.scope_id, name: ts.scope_name },
                                currentLanguage,
                              )}
                            </span>
                          </th>,
                        ]
                      }
                      const p = slot.prim
                      {
                        // Render rollup columns for this relation's scope before the relation column
                        const scopeRollups =
                          (p.related_scope_id
                            ? rollupByScope[p.related_scope_id]
                            : null) ?? []
                        const rollupHeaders = scopeRollups.map((rp) => {
                          const rollupScope = rp.config?._rollupSourceScope as
                            | string
                            | undefined
                          return (
                            <th
                              key={rp.id}
                              className={clsx(
                                "whitespace-nowrap border-r border-grey4/50 px-4 py-2.5 text-left font-medium last:border-r-0",
                                rollupScope
                                  ? (ROLLUP_SCOPE_HEADER[rollupScope] ??
                                      "text-grey5")
                                  : "text-grey5",
                              )}
                            >
                              <div>
                                <div className="flex items-center gap-1.5">
                                  <PrimitiveTypeIcon type={rp.type} />
                                  {getPrimitiveDisplayTitle(
                                    rp,
                                    currentLanguage,
                                  )}
                                </div>
                                <div
                                  className={clsx(
                                    "mt-0.5 text-[10px] font-normal",
                                    rollupScope
                                      ? (ROLLUP_SCOPE_TEXT[rollupScope] ??
                                          "text-grey5")
                                      : "text-grey5",
                                  )}
                                >
                                  {translate("QMS/PIVOT/AGGREGATION_SUFFIX", {
                                    scope: (() => {
                                      const s = allStoreScopes.find(
                                        (sc) => sc.id === rollupScope,
                                      )
                                      return s
                                        ? getScopeDisplayName(
                                            s,
                                            currentLanguage,
                                          )
                                        : (rollupScope ?? "")
                                    })(),
                                  })}
                                </div>
                              </div>
                            </th>
                          )
                        })

                        const relHierInfo = p.related_scope_id
                          ? relatedHierarchyInfos?.[p.related_scope_id]
                          : null
                        const isRelLevelOpen =
                          relationLevelDropdownOpen === p.id
                        // Compute the effective label for this relation's hierarchy filter
                        const relFilter = p.related_scope_id
                          ? relationLevelFilters[p.related_scope_id]
                          : undefined
                        const relFilterOpts =
                          relHierInfo?.level_filter_options ?? []
                        const relMaxLevel =
                          relFilterOpts.length > 0
                            ? Math.max(...relFilterOpts.map((o) => o.level))
                            : -1
                        const relLeafOpts = relFilterOpts.filter(
                          (o) => o.level === relMaxLevel,
                        )
                        const relLeafLabels = new Set(
                          relLeafOpts.map((o) => o.label),
                        )
                        const relLeafCount = relLeafOpts.reduce(
                          (sum, o) => sum + o.count,
                          0,
                        )
                        const relAllCount = relFilterOpts.reduce(
                          (sum, o) => sum + o.count,
                          0,
                        )
                        const relEffectiveLabel =
                          relFilter ??
                          (relFilterOpts.length > 0 ? "__leaf__" : undefined)

                        return [
                          ...rollupHeaders,
                          <th
                            key={p.id}
                            className="whitespace-nowrap border-r border-grey4/50 px-4 py-2.5 text-left font-medium text-grey5 last:border-r-0"
                          >
                            {relHierInfo && relFilterOpts.length > 0 ? (
                              <div className="relative">
                                <button
                                  onClick={() =>
                                    setRelationLevelDropdownOpen((v) =>
                                      v === p.id ? null : p.id,
                                    )
                                  }
                                  className="flex items-center gap-1 transition-colors hover:text-white"
                                >
                                  <span>
                                    {getPrimitiveDisplayTitle(
                                      p,
                                      currentLanguage,
                                    )}
                                  </span>
                                  {p.config?.mapping_title && (
                                    <span
                                      className="text-[10px] text-purple1"
                                      title={`Mapping: ${p.config.mapping_title}`}
                                    >
                                      ⇄
                                    </span>
                                  )}
                                  <svg
                                    className="h-3 w-3 text-grey5"
                                    viewBox="0 0 12 12"
                                    fill="none"
                                  >
                                    <path
                                      d="M3 5l3 3 3-3"
                                      stroke="currentColor"
                                      strokeWidth="1.5"
                                      strokeLinecap="round"
                                      strokeLinejoin="round"
                                    />
                                  </svg>
                                </button>
                                <div className="mt-0.5 text-[10px] font-normal text-grey5">
                                  {relEffectiveLabel === "__all__"
                                    ? translate("QMS/PIVOT/FILTER_ALL")
                                    : relEffectiveLabel === "__leaf__"
                                      ? relLeafLabels.size === 1
                                        ? [...relLeafLabels][0]
                                        : translate(
                                            "QMS/PIVOT/FILTER_ALL_COMPONENTS",
                                          )
                                      : (relEffectiveLabel ??
                                        translate("QMS/PIVOT/FILTER_ALL"))}
                                </div>
                                {isRelLevelOpen && (
                                  <>
                                    <div
                                      className="fixed inset-0 z-20"
                                      onClick={() =>
                                        setRelationLevelDropdownOpen(null)
                                      }
                                    />
                                    <div className="absolute left-0 top-full z-30 mt-1 max-h-[320px] min-w-[160px] overflow-y-auto rounded-lg border border-grey4 bg-grey2 py-1 shadow-lg">
                                      {/* "Alle" option - no filtering */}
                                      <button
                                        onClick={() => {
                                          setHierarchyLevelFilter(
                                            p.related_scope_id!,
                                            "__all__",
                                          )
                                          setRelationLevelDropdownOpen(null)
                                        }}
                                        className={clsx(
                                          "flex w-full items-center justify-between gap-3 px-3 py-1.5 text-left text-xs transition-colors",
                                          relEffectiveLabel === "__all__"
                                            ? "bg-yellow5/20 font-medium text-yellow1"
                                            : "text-grey5 hover:bg-grey3",
                                        )}
                                      >
                                        <span>
                                          {translate("QMS/PIVOT/FILTER_ALL")}
                                        </span>
                                        <span
                                          className={clsx(
                                            "text-[10px] tabular-nums",
                                            relEffectiveLabel === "__all__"
                                              ? "text-yellow1"
                                              : "text-grey5",
                                          )}
                                        >
                                          {relAllCount}
                                        </span>
                                      </button>
                                      {/* "Alle Komponenten" - leaf level */}
                                      {relLeafLabels.size > 1 && (
                                        <button
                                          onClick={() => {
                                            setHierarchyLevelFilter(
                                              p.related_scope_id!,
                                              null,
                                            )
                                            setRelationLevelDropdownOpen(null)
                                          }}
                                          className={clsx(
                                            "flex w-full items-center justify-between gap-3 px-3 py-1.5 text-left text-xs transition-colors",
                                            relEffectiveLabel === "__leaf__"
                                              ? "bg-yellow5/20 font-medium text-yellow1"
                                              : "text-grey5 hover:bg-grey3",
                                          )}
                                        >
                                          <span>
                                            {translate(
                                              "QMS/PIVOT/FILTER_ALL_COMPONENTS",
                                            )}
                                          </span>
                                          <span
                                            className={clsx(
                                              "text-[10px] tabular-nums",
                                              relEffectiveLabel === "__leaf__"
                                                ? "text-yellow1"
                                                : "text-grey5",
                                            )}
                                          >
                                            {relLeafCount}
                                          </span>
                                        </button>
                                      )}
                                      <div className="my-0.5 border-t border-grey4/50" />
                                      {relFilterOpts.map((opt, idx) => {
                                        const isActive =
                                          relEffectiveLabel === opt.label
                                        const prevLevel =
                                          idx > 0
                                            ? relFilterOpts[idx - 1].level
                                            : -1
                                        const showSep =
                                          opt.level !== prevLevel && idx > 0
                                        return (
                                          <div
                                            key={`${opt.level}-${opt.label}`}
                                          >
                                            {showSep && (
                                              <div className="my-0.5 border-t border-grey4/50" />
                                            )}
                                            <button
                                              onClick={() => {
                                                setHierarchyLevelFilter(
                                                  p.related_scope_id!,
                                                  opt.label,
                                                )
                                                setRelationLevelDropdownOpen(
                                                  null,
                                                )
                                              }}
                                              className={clsx(
                                                "flex w-full items-center justify-between gap-3 px-3 py-1.5 text-left text-xs transition-colors",
                                                isActive
                                                  ? "bg-yellow5/20 font-medium text-yellow1"
                                                  : "text-grey5 hover:bg-grey3",
                                              )}
                                            >
                                              <span>{opt.label}</span>
                                              <span
                                                className={clsx(
                                                  "text-[10px] tabular-nums",
                                                  isActive
                                                    ? "text-yellow1"
                                                    : "text-grey5",
                                                )}
                                              >
                                                {opt.count}
                                              </span>
                                            </button>
                                          </div>
                                        )
                                      })}
                                    </div>
                                  </>
                                )}
                              </div>
                            ) : (
                              <span className="flex items-center gap-1">
                                {getPrimitiveDisplayTitle(p, currentLanguage)}
                                {p.config?.mapping_title && (
                                  <span
                                    className="text-[10px] text-purple1"
                                    title={`Mapping: ${p.config.mapping_title}`}
                                  >
                                    ⇄
                                  </span>
                                )}
                              </span>
                            )}
                          </th>,
                        ]
                      }
                    })}
                  </tr>
                </thead>
                <tbody>
                  {isDiffMode ? (
                    /* --- Diff mode rows --- */
                    displayRecords.length === 0 ? (
                      <tr>
                        <td
                          colSpan={
                            visiblePrims.length +
                            rollupPrimCount +
                            relationPrims.filter((p) => p.is_visible).length +
                            (transitiveCounts?.transitive_scopes.length ?? 0) +
                            1
                          }
                          className="py-12 text-center text-grey5"
                        >
                          {diffCommitting
                            ? diffState?.exportMode
                              ? translate("QMS/PIVOT/DOWNLOAD_PREPARING")
                              : translate("QMS/PIVOT/APPLYING_CHANGES")
                            : translate("QMS/PIVOT/ALL_RESOLVED")}
                        </td>
                      </tr>
                    ) : (
                      displayRecords.map((record) => {
                        const diffRec = diffState!.records[record.id]
                        if (!diffRec) return null
                        return (
                          <DiffPivotRow
                            key={record.id}
                            record={record}
                            diffRec={diffRec}
                            scope={scope}
                            visiblePrims={visiblePrims}
                            relationPrims={relationPrims}
                            relationDiffs={
                              diffState!.relationDiffs?.[record.id]
                            }
                            exportMode={!!diffState!.exportMode}
                            secondary={isSecondaryDiff}
                            diffFilterMode={diffFilterMode}
                            onAcceptCell={(primId) =>
                              acceptDiffCell(record.id, primId)
                            }
                            onAcceptRow={() => acceptDiffRow(record.id)}
                            onEditCell={(primId, val, ti) =>
                              editDiffCellValue(record.id, primId, val, ti)
                            }
                            onDeclineCell={(primId) => {
                              const c =
                                diffState!.records[record.id]?.cells[primId]
                              if (!c) return
                              // Export-mode mapped primary cell (from secondary scope):
                              // two-step decline - first revert edit, then mark as declined.
                              if (
                                isSecondaryDiff &&
                                !c.secondary &&
                                diffState!.exportMode
                              ) {
                                // If cell was edited, first revert to original primary state
                                if (c.fileValue != null && !c.declined) {
                                  editDiffCellValue(
                                    record.id,
                                    primId,
                                    null,
                                    false,
                                    false,
                                  )
                                  return
                                }
                                // Mark as declined - document cell won't be touched during export.
                                declineDiffCell(record.id, primId)
                                return
                              }
                              // Promoted secondary cell (import mode): revert to secondary state
                              if (isSecondaryDiff && !c.secondary) {
                                editDiffCellValue(
                                  record.id,
                                  primId,
                                  c.dbValue ?? "",
                                  false,
                                  true,
                                )
                                return
                              }
                              // Import decline: keep DB value; Export decline: keep document value
                              const declineValue = diffState!.exportMode
                                ? (c.rawFileValue ?? c.fileValue ?? "")
                                : (c.dbValue ?? "")
                              editDiffCellValue(
                                record.id,
                                primId,
                                declineValue,
                                false,
                              )
                              acceptDiffCell(record.id, primId)
                            }}
                            onAcceptRelationEntry={(primId, targetId) =>
                              acceptRelationDiffEntry(
                                record.id,
                                primId,
                                targetId,
                              )
                            }
                            onAcceptAllRelations={(primId) =>
                              acceptAllRelationDiffs(record.id, primId)
                            }
                            relationLevelLabels={relationLevelLabels}
                          />
                        )
                      })
                    )
                  ) : /* --- Normal mode rows --- */
                  displayRecords.length === 0 ? (
                    <tr>
                      <td
                        colSpan={
                          visiblePrims.length +
                          rollupPrimCount +
                          relationPrims.length +
                          (transitiveCounts?.transitive_scopes.length ?? 0) +
                          (selectMode ? 1 : 0)
                        }
                        className="py-12 text-center text-grey5"
                      >
                        {records.length === 0
                          ? translate("QMS/PIVOT/NO_RECORDS_YET", {
                              scope: scope.name,
                            })
                          : translate("QMS/PIVOT/NO_RECORDS_LEVEL", {
                              total: records.length,
                            })}
                      </td>
                    </tr>
                  ) : activeGroupPrim ? (
                    <GroupedRows
                      records={displayRecords}
                      scope={scope}
                      visiblePrims={visiblePrims}
                      relationPrims={relationPrims}
                      groupPrimName={activeGroupPrim.name}
                      totalCols={
                        visiblePrims.length +
                        rollupPrimCount +
                        relationPrims.filter((p) => p.is_visible).length +
                        (transitiveCounts?.transitive_scopes.length ?? 0) +
                        (selectMode ? 1 : 0)
                      }
                      onRowClick={(id) =>
                        selectMode
                          ? toggleRecordSelection(id)
                          : openRecordDetail(id)
                      }
                      downloadMode={selectMode}
                      selectedRecordIds={selectedRecordIds}
                      onToggleSelect={toggleRecordSelection}
                      relationLevelFilters={relationLevelFilters}
                      relationLevelLabels={relationLevelLabels}
                      draggingOver={draggingOver}
                    />
                  ) : (
                    /* --- Virtualized flat rows --- */
                    <VirtualizedRows
                      displayRecords={displayRecords}
                      scrollRef={tableScrollRef}
                      scope={scope}
                      visiblePrims={visiblePrims}
                      relationPrims={relationPrims}
                      onRowClick={(id) =>
                        selectMode
                          ? toggleRecordSelection(id)
                          : openRecordDetail(id)
                      }
                      downloadMode={selectMode}
                      selectedRecordIds={selectedRecordIds}
                      onToggleSelect={toggleRecordSelection}
                      relationLevelFilters={relationLevelFilters}
                      relationLevelLabels={relationLevelLabels}
                      draggingOver={draggingOver}
                    />
                  )}
                </tbody>
              </table>
            </div>
          </div>
          {columnPickerOpen && (
            <ExportColumnPickerModal
              scopeId={scopeId}
              selectedCount={selectedRecordIds.size}
              selectedRecordIds={selectedRecordIds}
              onClose={() => setColumnPickerOpen(false)}
              onDownloaded={() => {
                setColumnPickerOpen(false)
                exitDownloadMode()
              }}
            />
          )}
        </TransitiveCountsCtx.Provider>
      </ScopeOrderCtx.Provider>
    </GroupNodeNamesCtx.Provider>
  )
}

// ---------------------------------------------------------------------------
// Virtualized flat rows - only renders rows visible in the viewport
// ---------------------------------------------------------------------------

function VirtualizedRows({
  displayRecords,
  scrollRef,
  scope,
  visiblePrims,
  relationPrims,
  onRowClick,
  downloadMode: dlMode,
  selectedRecordIds: selIds,
  onToggleSelect,
  relationLevelFilters,
  relationLevelLabels,
  draggingOver,
}: {
  displayRecords: QmsRecord[]
  scrollRef: React.RefObject<HTMLDivElement | null>
  scope: Scope
  visiblePrims: PrimitiveDef[]
  relationPrims: PrimitiveDef[]
  onRowClick: (id: string) => void
  downloadMode?: boolean
  selectedRecordIds?: Set<string>
  onToggleSelect?: (id: string) => void
  relationLevelFilters?: Record<string, string>
  relationLevelLabels?: Record<string, string>
  draggingOver?: boolean
}) {
  const ROW_HEIGHT = 41
  const rowVirtualizer = useVirtualizer({
    count: displayRecords.length,
    getScrollElement: () => scrollRef.current,
    estimateSize: () => ROW_HEIGHT,
    overscan: 20,
  })

  const virtualItems = rowVirtualizer.getVirtualItems()
  const paddingTop = virtualItems.length > 0 ? virtualItems[0].start : 0
  const paddingBottom =
    virtualItems.length > 0
      ? rowVirtualizer.getTotalSize() -
        virtualItems[virtualItems.length - 1].end
      : 0

  return (
    <>
      {paddingTop > 0 && (
        <tr>
          <td style={{ height: paddingTop, padding: 0, border: "none" }} />
        </tr>
      )}
      {virtualItems.map((virtualRow) => {
        const record = displayRecords[virtualRow.index]
        return (
          <PivotRow
            key={record.id}
            record={record}
            scope={scope}
            visiblePrims={visiblePrims}
            relationPrims={relationPrims}
            onRowClick={() => onRowClick(record.id)}
            downloadMode={dlMode}
            selected={selIds?.has(record.id)}
            onToggleSelect={() => onToggleSelect?.(record.id)}
            relationLevelFilters={relationLevelFilters}
            relationLevelLabels={relationLevelLabels}
            draggingOver={draggingOver}
          />
        )
      })}
      {paddingBottom > 0 && (
        <tr>
          <td style={{ height: paddingBottom, padding: 0, border: "none" }} />
        </tr>
      )}
    </>
  )
}

// ---------------------------------------------------------------------------
// Grouped rows renderer
// ---------------------------------------------------------------------------

function GroupedRows({
  records,
  scope,
  visiblePrims,
  relationPrims,
  groupPrimName,
  totalCols,
  onRowClick,
  downloadMode: dlMode,
  selectedRecordIds: selIds,
  onToggleSelect,
  relationLevelFilters,
  relationLevelLabels,
  draggingOver,
}: {
  records: QmsRecord[]
  scope: Scope
  visiblePrims: PrimitiveDef[]
  relationPrims: PrimitiveDef[]
  groupPrimName: string
  totalCols: number
  onRowClick: (id: string) => void
  downloadMode?: boolean
  selectedRecordIds?: Set<string>
  onToggleSelect?: (id: string) => void
  relationLevelFilters?: Record<string, string>
  relationLevelLabels?: Record<string, string>
  draggingOver?: boolean
}) {
  const { translate } = useTranslation()
  // Build ordered group map (preserves first-seen order)
  const groupMap = new Map<string, QmsRecord[]>()
  for (const record of records) {
    const groupVal =
      record.values[groupPrimName]?.value ??
      translate("QMS/PIVOT/UNGROUPED_LABEL")
    if (!groupMap.has(groupVal)) groupMap.set(groupVal, [])
    groupMap.get(groupVal)!.push(record)
  }

  return (
    <>
      {[...groupMap.entries()].map(([groupVal, groupRecords]) => (
        <React.Fragment key={groupVal}>
          <tr className="border-b border-grey4 bg-grey3">
            <td colSpan={totalCols} className="px-4 py-1.5">
              <span className="text-xs font-semibold uppercase tracking-wide text-grey5">
                {groupVal}
              </span>
              <span className="ml-2 text-xs text-grey5">
                {groupRecords.length}
              </span>
            </td>
          </tr>
          {groupRecords.map((record) => (
            <PivotRow
              key={record.id}
              record={record}
              scope={scope}
              visiblePrims={visiblePrims}
              relationPrims={relationPrims}
              onRowClick={() => onRowClick(record.id)}
              downloadMode={dlMode}
              selected={selIds?.has(record.id)}
              onToggleSelect={() => onToggleSelect?.(record.id)}
              relationLevelFilters={relationLevelFilters}
              relationLevelLabels={relationLevelLabels}
              draggingOver={draggingOver}
            />
          ))}
        </React.Fragment>
      ))}
    </>
  )
}

// ---------------------------------------------------------------------------
// Hierarchical grouped rows renderer
// ---------------------------------------------------------------------------

const HIER_COLORS = [
  "bg-yellow1/15 text-yellow1",
  "bg-success/20 text-success",
  "bg-purple1/20 text-purple1",
  "bg-warning/20 text-warning",
  "bg-red1/20 text-red1",
]

// eslint-disable-next-line @typescript-eslint/no-unused-vars
function HierarchicalGroupedRows({
  records,
  scope,
  visiblePrims,
  relationPrims,
  groupPrimName,
  groupTree,
  totalCols,
  onRowClick,
  relationLevelFilters,
  relationLevelLabels,
}: {
  records: QmsRecord[]
  scope: Scope
  visiblePrims: PrimitiveDef[]
  relationPrims: PrimitiveDef[]
  groupPrimName: string
  groupTree: GroupTree
  totalCols: number
  onRowClick: (id: string) => void
  relationLevelFilters?: Record<string, string>
  relationLevelLabels?: Record<string, string>
}) {
  const { translate } = useTranslation()
  // Build index: GroupNode ID → records that belong to it
  const recordsByGroup = new Map<string, QmsRecord[]>()
  const ungrouped: QmsRecord[] = []
  for (const record of records) {
    const val = record.values[groupPrimName]?.value
    if (val && groupTree.roots.length > 0) {
      if (!recordsByGroup.has(val)) recordsByGroup.set(val, [])
      recordsByGroup.get(val)!.push(record)
    } else {
      ungrouped.push(record)
    }
  }

  // Also index by group name for fallback matching (when value is a name, not an ID)
  const nodesByName = new Map<string, GroupNode>()
  function indexNames(nodes: GroupNode[]) {
    for (const n of nodes) {
      nodesByName.set(n.name.toLowerCase(), n)
      indexNames(n.children)
    }
  }
  indexNames(groupTree.roots)

  // Try matching records by name if not matched by ID
  for (const record of [...ungrouped]) {
    const val = record.values[groupPrimName]?.value
    if (!val) continue
    const node = nodesByName.get(val.toLowerCase())
    if (node) {
      const idx = ungrouped.indexOf(record)
      if (idx >= 0) ungrouped.splice(idx, 1)
      if (!recordsByGroup.has(node.id)) recordsByGroup.set(node.id, [])
      recordsByGroup.get(node.id)!.push(record)
    }
  }

  function countRecordsDeep(node: GroupNode): number {
    let count = (recordsByGroup.get(node.id) ?? []).length
    for (const child of node.children) count += countRecordsDeep(child)
    return count
  }

  return (
    <>
      {groupTree.roots.map((root) => (
        <HierarchyGroupNode
          key={root.id}
          node={root}
          depth={0}
          recordsByGroup={recordsByGroup}
          countRecordsDeep={countRecordsDeep}
          scope={scope}
          visiblePrims={visiblePrims}
          relationPrims={relationPrims}
          totalCols={totalCols}
          onRowClick={onRowClick}
          relationLevelLabels={relationLevelLabels}
        />
      ))}
      {ungrouped.length > 0 && (
        <>
          <tr className="border-b border-grey4 bg-grey3">
            <td colSpan={totalCols} className="px-4 py-1.5">
              <span className="text-xs font-semibold uppercase tracking-wide text-grey5">
                {translate("QMS/PIVOT/UNGROUPED_LABEL")}
              </span>
              <span className="ml-2 text-xs text-grey5">
                {ungrouped.length}
              </span>
            </td>
          </tr>
          {ungrouped.map((record) => (
            <PivotRow
              key={record.id}
              record={record}
              scope={scope}
              visiblePrims={visiblePrims}
              relationPrims={relationPrims}
              onRowClick={() => onRowClick(record.id)}
              relationLevelFilters={relationLevelFilters}
              relationLevelLabels={relationLevelLabels}
            />
          ))}
        </>
      )}
    </>
  )
}

function HierarchyGroupNode({
  node,
  depth,
  recordsByGroup,
  countRecordsDeep,
  scope,
  visiblePrims,
  relationPrims,
  totalCols,
  onRowClick,
  relationLevelFilters,
  relationLevelLabels,
}: {
  node: GroupNode
  depth: number
  recordsByGroup: Map<string, QmsRecord[]>
  countRecordsDeep: (node: GroupNode) => number
  scope: Scope
  visiblePrims: PrimitiveDef[]
  relationPrims: PrimitiveDef[]
  totalCols: number
  onRowClick: (id: string) => void
  relationLevelFilters?: Record<string, string>
  relationLevelLabels?: Record<string, string>
}) {
  const { translate } = useTranslation()
  const [expanded, setExpanded] = useState(true)
  const directRecords = recordsByGroup.get(node.id) ?? []
  const totalCount = countRecordsDeep(node)
  const hasChildren = node.children.length > 0
  const colorClass = HIER_COLORS[depth % HIER_COLORS.length]

  return (
    <>
      {/* Group header row */}
      <tr
        className="cursor-pointer border-b border-grey4 transition-colors hover:bg-grey3"
        style={{ backgroundColor: `rgba(0,0,0,${0.02 + depth * 0.015})` }}
        onClick={() => setExpanded(!expanded)}
      >
        <td
          colSpan={totalCols}
          className="py-1.5"
          style={{ paddingLeft: `${16 + depth * 20}px` }}
        >
          <div className="flex items-center gap-2">
            <span className="w-3 text-xs text-grey5">
              {expanded ? "▾" : "▸"}
            </span>
            <span
              className={clsx(
                "font-mono rounded px-1.5 py-0.5 text-[10px] font-medium",
                colorClass,
              )}
            >
              L{node.level}
            </span>
            <span className="text-xs font-semibold text-grey5">
              {node.name}
            </span>
            <span className="text-xs text-grey5">
              {translate("QMS/PIVOT/RECORD_COUNT_TOTAL", {
                count: totalCount,
                label: translate(
                  totalCount !== 1
                    ? "QMS/PIVOT/RECORDS_PLURAL"
                    : "QMS/PIVOT/RECORD_SINGULAR",
                ),
              })}
            </span>
            {!hasChildren && (
              <span className="font-mono text-[9px] text-grey5">
                {translate("QMS/PIVOT/LEAF_BADGE")}
              </span>
            )}
          </div>
        </td>
      </tr>

      {expanded && (
        <>
          {/* Child group nodes */}
          {node.children.map((child) => (
            <HierarchyGroupNode
              key={child.id}
              node={child}
              depth={depth + 1}
              recordsByGroup={recordsByGroup}
              countRecordsDeep={countRecordsDeep}
              scope={scope}
              visiblePrims={visiblePrims}
              relationPrims={relationPrims}
              totalCols={totalCols}
              onRowClick={onRowClick}
              relationLevelFilters={relationLevelFilters}
              relationLevelLabels={relationLevelLabels}
            />
          ))}

          {/* Direct records at this node */}
          {directRecords.map((record) => (
            <PivotRow
              key={record.id}
              record={record}
              scope={scope}
              visiblePrims={visiblePrims}
              relationPrims={relationPrims}
              onRowClick={() => onRowClick(record.id)}
              relationLevelFilters={relationLevelFilters}
              relationLevelLabels={relationLevelLabels}
            />
          ))}
        </>
      )}
    </>
  )
}

// ---------------------------------------------------------------------------
// Templates Dropdown (used inside Download Column Picker)
// ---------------------------------------------------------------------------

function TemplatesDropdown({
  onSelectFormat,
  selectedFormat,
  scopeId,
}: {
  onSelectFormat: (fmt: ExportFormat) => void
  selectedFormat?: ExportFormat
  scopeId: string
}) {
  const { translate } = useTranslation()
  const [open, setOpen] = useState(false)
  const ref = useRef<HTMLDivElement>(null)
  const isSelected = hasTemplateMapping(selectedFormat)

  // Fetch active-config templates with readiness status.  This is the
  // use-time check: a template whose declared primitives aren't in the
  // active config is returned with status='missing_primitives' and a
  // list of missing refs - we render it disabled with the missing list
  // inline so the user knows exactly what to add to schema.yaml.
  const { data: active } = useQuery({
    queryKey: ["active-templates"],
    queryFn: () => api.activeTemplates(),
    staleTime: 5 * 60_000,
  })
  // Only templates whose declared anchor_scope_ids include the active
  // scope tab - mirrors haas's setup where produkt_konformitaet (anchor:
  // product) appears in the product tab and kunden_konformitaet (anchor:
  // customer) appears in the customer tab.
  const templates = (active?.export ?? []).filter((t) =>
    t.anchor_scope_ids.includes(scopeId),
  )

  useEffect(() => {
    if (!open) return
    const handler = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false)
    }
    document.addEventListener("mousedown", handler)
    return () => document.removeEventListener("mousedown", handler)
  }, [open])

  if (templates.length === 0) return null

  const selectedLabel = isSelected
    ? templates.find((t) => t.format === selectedFormat)?.label
    : undefined

  return (
    <div ref={ref} className="relative flex-1">
      <button
        onClick={() => setOpen((o) => !o)}
        className={`w-full rounded-lg border px-3 py-2 text-xs font-medium transition-colors ${
          open || isSelected
            ? "border-yellow1 bg-yellow5/20 text-yellow1"
            : "border-grey4 bg-grey2 text-grey5 hover:bg-grey3"
        }`}
      >
        {isSelected && selectedLabel
          ? selectedLabel
          : translate("QMS/PIVOT/TEMPLATES_BTN")}
      </button>
      {open && (
        <div className="absolute left-0 right-0 top-full z-10 mt-1 overflow-hidden rounded-lg border border-grey4 bg-grey2 shadow-xl">
          {templates.map((tmpl) => {
            const disabled = tmpl.status !== "ready"
            return (
              <button
                key={tmpl.id}
                disabled={disabled}
                onClick={() => {
                  if (disabled) return
                  setOpen(false)
                  onSelectFormat(tmpl.format as ExportFormat)
                }}
                className={`w-full border-b border-grey4 px-3 py-2.5 text-left text-xs font-medium transition-colors ${
                  disabled
                    ? "cursor-not-allowed bg-grey1 text-grey5 opacity-60"
                    : "text-white hover:bg-grey3"
                }`}
              >
                <div className="flex items-center gap-2">
                  <span className="block">{tmpl.label}</span>
                  {disabled && (
                    <span className="rounded bg-warning/20 px-1.5 py-0.5 text-[9px] font-medium text-warning">
                      {translate("QMS/PIVOT/UNAVAILABLE")}
                    </span>
                  )}
                </div>
                {tmpl.description && (
                  <span className="mt-0.5 block text-[10px] text-grey5">
                    {tmpl.description}
                  </span>
                )}
                {disabled && tmpl.missing.length > 0 && (
                  <div className="mt-1.5 text-[10px] text-warning">
                    <div className="font-medium">
                      {translate("QMS/PIVOT/MISSING_FIELDS_LABEL")}
                    </div>
                    <ul className="font-mono mt-0.5 space-y-0.5">
                      {tmpl.missing.map((m) => (
                        <li key={m}>· {m}</li>
                      ))}
                    </ul>
                  </div>
                )}
              </button>
            )
          })}
        </div>
      )}
    </div>
  )
}

// ---------------------------------------------------------------------------
// Conformity Summary - shown inside Download Column Picker when template selected
// ---------------------------------------------------------------------------

// ---------------------------------------------------------------------------
// Export Column Picker Modal - unified with ColumnMappingStep
// ---------------------------------------------------------------------------

import type { DownloadColumn, ReachablePrimitive } from "../../types"
import { ColumnMappingStep } from "../ingestion/ColumnMappingStep"

/** Hardcoded column set for the product conformity (conformity letter) template.
 *  Maps each DB primitive to its label in the final PDF.
 *  Gehalt and Orte are rolled up from Component → SVHC level. */
const CONFORMITY_COLUMNS: {
  scope_id: string
  primitive_name: string
  label: string
  rollup_from?: {
    source_scope_id: string
    source_primitive_name: string
    aggregation: "concat" | "concat_unique" | "sum" | "count"
  }
}[] = [
  // Product (primary scope) - primitive names match those declared by the
  // configs the produkt_konformitaet template targets; ``alt_names``
  // fallbacks were removed with the strict-mode migration.
  { scope_id: "product", primitive_name: "product_id", label: "Produkt-Nr." },
  { scope_id: "product", primitive_name: "product_name", label: "Produktname" },
  // SVHC (via Product → Component → Substance)
  { scope_id: "substance", primitive_name: "cas_number", label: "CAS-Nr." },
  {
    scope_id: "substance",
    primitive_name: "substance_name",
    label: "SVHC-Stoff",
  },
  // Rollups on substance level: aggregate Component fields across all components linked to each substance
  {
    scope_id: "substance",
    primitive_name: "_rollup_svhc_concentration",
    label: "Gehalt (%)",
    rollup_from: {
      source_scope_id: "component",
      source_primitive_name: "svhc_concentration",
      aggregation: "concat",
    },
  },
  {
    scope_id: "substance",
    primitive_name: "_rollup_article_name",
    label: "SVHC-Orte im Bauteil",
    rollup_from: {
      source_scope_id: "component",
      source_primitive_name: "article_name",
      aggregation: "concat",
    },
  },
]

/** Relation chain for the conformity template: Product → Component → Substance,
 *  with a Component → Supplier branch.  Names match what the yaml declares
 *  (including mirrors synthesized by parse_schema): product→component uses
 *  the auto-mirror ``component``; component→substance and component→supplier
 *  are declared directly on component.  */
const CONFORMITY_RELATIONS: {
  from_scope: string
  relation_name: string
  to_scope: string
}[] = [
  { from_scope: "product", relation_name: "component", to_scope: "component" },
  {
    from_scope: "component",
    relation_name: "substance",
    to_scope: "substance",
  },
  { from_scope: "component", relation_name: "supplier", to_scope: "supplier" },
]

type ConformityColumnDef = {
  scope_id: string
  primitive_name: string
  label: string
  /** Optional i18n key override for the label, resolved at render time. */
  labelKey?: string
  rollup_from?: {
    source_scope_id: string
    source_primitive_name: string
    aggregation: "concat" | "concat_unique" | "sum" | "count"
  }
  /** When true, the template's preset surfaces this relation primitive as a
   *  mapping-title row - same shape the wizard produces when a user picks
   *  a mapping anchor (is_mapping_title).  Used for columns like
   *  Fremdartikelnummer that carry the edge's mapped_id, not the PK. */
  is_mapping_title?: boolean
  /** When set, the template's preset surfaces this as a user-authored
   *  helper/formula row - same shape the wizard produces via HelperEditor.
   *  `expression` uses bare identifiers matching the scope's primitive
   *  `name`s (no brackets - no document cells at play in template mode). */
  helperConfig?: { label: string; expression: string }
}

/** Customer conformity: Customer → Product → Component → SVHC.  Uses the
 *  same naming conventions as produkt_konformitaet.  Configs that don't
 *  declare a customer scope + relation to product surface this template
 *  as ``missing_primitives`` in the export dropdown. */
const CUSTOMER_CONFORMITY_COLUMNS: ConformityColumnDef[] = [
  { scope_id: "customer", primitive_name: "customer_id", label: "Kunden-ID" },
  {
    scope_id: "customer",
    primitive_name: "customer_name",
    label: "Kundenname",
  },
  { scope_id: "product", primitive_name: "product_id", label: "Produkt-Nr." },
  { scope_id: "product", primitive_name: "product_name", label: "Produktname" },
  { scope_id: "substance", primitive_name: "cas_number", label: "CAS-Nr." },
  {
    scope_id: "substance",
    primitive_name: "substance_name",
    label: "SVHC-Stoff",
  },
  {
    scope_id: "substance",
    primitive_name: "_rollup_svhc_concentration",
    label: "Gehalt (%)",
    rollup_from: {
      source_scope_id: "component",
      source_primitive_name: "svhc_concentration",
      aggregation: "concat",
    },
  },
  {
    scope_id: "substance",
    primitive_name: "_rollup_article_name",
    label: "SVHC-Orte im Bauteil",
    rollup_from: {
      source_scope_id: "component",
      source_primitive_name: "article_name",
      aggregation: "concat",
    },
  },
]

const CUSTOMER_CONFORMITY_RELATIONS: {
  from_scope: string
  relation_name: string
  to_scope: string
}[] = [
  { from_scope: "customer", relation_name: "product", to_scope: "product" },
  { from_scope: "product", relation_name: "component", to_scope: "component" },
  {
    from_scope: "component",
    relation_name: "substance",
    to_scope: "substance",
  },
  { from_scope: "component", relation_name: "supplier", to_scope: "supplier" },
]

/** Escha customer conformity - client-rendered PDF anchored on customer.
 *  The wizard preset mirrors exactly what a user can build manually:
 *    - Three direct product primitives (incl. the schema-declared
 *      kupferanteil_artikel_kg formula primitive)
 *    - One mapping-title column (Fremdartikelnummer - mapped_id on the
 *      product→customer edge)
 *    - One helper/formula column (Blei %)
 *
 *  The Blei % helper is evaluated client-side by evaluateFormula using
 *  the raw primitives the escha schema declares; the bare identifiers
 *  in its expression must match those primitive names.  Kupferanteil
 *  uses the regular schema-declared formula primitive, so the backend
 *  evaluates it alongside every other primitive. */
const ESCHA_KUNDEN_KONFORMITAET_COLUMNS: ConformityColumnDef[] = [
  {
    scope_id: "product",
    primitive_name: "internal_product_id",
    label: "Artikel-Nr.",
  },
  {
    scope_id: "product",
    primitive_name: "customer",
    label: "Fremd-artikel-Nr.",
    is_mapping_title: true,
  },
  {
    scope_id: "product",
    primitive_name: "artikelausfuehrung",
    label: "Beschreibung",
    labelKey: "QMS/PIVOT/DESCRIPTION",
  },
  {
    scope_id: "product",
    primitive_name: "kupferanteil_artikel_kg",
    label: "Kupferanteil (kg)",
  },
  {
    scope_id: "product",
    primitive_name: "_helper_blei_prozent",
    label: "Blei % (w/w)",
    helperConfig: {
      label: "blei_prozent",
      expression: "menge_blei * basismenge / gewicht * 100",
    },
  },
]

/** Customer anchor → single hop to product.  Direction matters: the
 *  exportRelationChain handler keys ``scopeLocks`` on ``to_scope``, so
 *  we need ``to_scope: 'product'`` here for product to unlock.  Owner
 *  primitive on the customer side is the auto-mirror ``customer.product``. */
const ESCHA_KUNDEN_KONFORMITAET_RELATIONS: {
  from_scope: string
  relation_name: string
  to_scope: string
}[] = [
  { from_scope: "customer", relation_name: "product", to_scope: "product" },
]

/** Select the right template column config by format.  Templates that
 *  appear here get a preset, read-only column mapping step; any format
 *  not listed falls through to the free-form column picker. */
function getConformityConfig(format: string): {
  columns: ConformityColumnDef[]
  relations: typeof CONFORMITY_RELATIONS
} | null {
  if (format === "produkt_konformitaet")
    return { columns: CONFORMITY_COLUMNS, relations: CONFORMITY_RELATIONS }
  if (format === "kunden_konformitaet")
    return {
      columns: CUSTOMER_CONFORMITY_COLUMNS,
      relations: CUSTOMER_CONFORMITY_RELATIONS,
    }
  if (format === "escha_kunden_konformitaet")
    return {
      columns: ESCHA_KUNDEN_KONFORMITAET_COLUMNS,
      relations: ESCHA_KUNDEN_KONFORMITAET_RELATIONS,
    }
  return null
}

function hasTemplateMapping(format: string | undefined): boolean {
  return format !== undefined && getConformityConfig(format) !== null
}

/**
 * Traverse the relation chain from primary scope to target scope to find linked record IDs.
 * E.g. Product → Component → SVHC, or Product → Component → Supplier.
 *
 * Uses BFS over the chain's scope graph so branches from a shared scope
 * (Component → SVHC and Component → Supplier) both resolve correctly.
 */
async function findLinkedRecordIds(
  startRecordIds: string[],
  startScopeId: string,
  targetScopeId: string,
  qc: import("@tanstack/react-query").QueryClient,
  relationChain?: {
    from_scope: string
    relation_name: string
    to_scope: string
  }[],
): Promise<Set<string>> {
  const chain = relationChain ?? CONFORMITY_RELATIONS
  type Hop = { from_scope: string; relation_name: string; to_scope: string }

  // BFS: find shortest sequence of hops from startScopeId → targetScopeId
  const queue: { scope: string; path: Hop[] }[] = [
    { scope: startScopeId, path: [] },
  ]
  const visited = new Set<string>([startScopeId])
  let path: Hop[] = []
  while (queue.length > 0) {
    const { scope, path: currentPath } = queue.shift()!
    if (scope === targetScopeId) {
      path = currentPath
      break
    }
    for (const link of chain) {
      if (link.from_scope !== scope) continue
      if (visited.has(link.to_scope)) continue
      visited.add(link.to_scope)
      queue.push({ scope: link.to_scope, path: [...currentPath, link] })
    }
  }
  if (path.length === 0) return new Set()

  // Traverse each hop
  let currentIds = new Set(startRecordIds)
  for (const hop of path) {
    const nextIds = new Set<string>()
    for (const recId of currentIds) {
      try {
        const preview = await api.getRelationPreview(recId, hop.relation_name)
        for (const linked of preview.records) {
          nextIds.add(linked.id)
        }
      } catch {
        /* skip failed lookups */
      }
    }
    currentIds = nextIds
    if (currentIds.size === 0) break
  }
  return currentIds
}

/**
 * Query the backend directional-cascade staleness endpoint for a single scope.
 * Returns the set of record IDs whose effective staleness is true, i.e. rows
 * that must be confirmed in the diff (either because their own updated_at is
 * stale or because an upstream record - lower rank in the supplier→customer
 * hierarchy - reached through relations is stale).
 *
 * No-ops (returns empty set) when freshnessDate is null or records is empty.
 * Falls back to treating all records as stale on API error so the user errs
 * toward reviewing rather than silently auto-accepting.
 */
async function fetchEffectiveStaleIds(
  records: QmsRecord[],
  scopeId: string,
  freshnessDate: string | null,
): Promise<Set<string>> {
  if (!freshnessDate || records.length === 0) return new Set()
  try {
    const result = await api.getEffectiveStaleness(freshnessDate, {
      [scopeId]: records.map((r) => r.id),
    })
    return new Set(result.stale_record_ids)
  } catch (err) {
    console.warn(
      "[fetchEffectiveStaleIds] staleness check failed, treating all as stale",
      err,
    )
    return new Set(records.map((r) => r.id))
  }
}

/**
 * Restrict `records` to those transitively linked to the primary scope's
 * exportRecordIds. Uses the conformity relation chain when the export format
 * defines one, otherwise derives a chain from actionState.scopeMappings.
 * Returns [] when the secondary scope is unreachable from the primary - the
 * caller should then skip creating a secondary diff for that scope.
 */
async function filterRecordsLinkedToPrimaryExport(
  records: QmsRecord[],
  primaryScopeId: string,
  primaryDiff: ScopeDiffState,
  secondaryScopeId: string,
  qc: import("@tanstack/react-query").QueryClient,
): Promise<QmsRecord[]> {
  if (!primaryDiff.exportRecordIds || primaryDiff.exportRecordIds.length === 0)
    return []

  let chain = getConformityConfig(primaryDiff.targetFormat ?? "")?.relations
  if (!chain) {
    const actionSt = useStore.getState().actionState
    if (actionSt?.scopeMappings) {
      const derivedChain: {
        from_scope: string
        relation_name: string
        to_scope: string
      }[] = []
      const visited = new Set<string>([primaryScopeId])
      const queue = [primaryScopeId]
      while (queue.length > 0) {
        const current = queue.shift()!
        let primitives: { id: string; name: string }[] = []
        const cached =
          qc.getQueryData<{ primitives: typeof primitives }>([
            "scope",
            current,
          ]) ??
          qc.getQueryData<{ scope: { primitives: typeof primitives } }>([
            "records",
            current,
          ])
        primitives =
          cached && "primitives" in cached
            ? cached.primitives
            : ((cached as { scope?: { primitives?: typeof primitives } })?.scope
                ?.primitives ?? [])
        if (primitives.length === 0) {
          try {
            const fetched = await qc.fetchQuery({
              queryKey: ["scope", current],
              queryFn: () => api.getScope(current),
            })
            primitives = fetched?.primitives ?? []
          } catch {
            /* ignore */
          }
        }
        for (const [targetSid, mapping] of Object.entries(
          actionSt.scopeMappings,
        )) {
          if (visited.has(targetSid) || mapping.status === "removed") continue
          const relPrim = primitives.find(
            (p) => p.id === mapping.relationPrimitiveId,
          )
          if (relPrim) {
            derivedChain.push({
              from_scope: current,
              relation_name: relPrim.name,
              to_scope: targetSid,
            })
            visited.add(targetSid)
            queue.push(targetSid)
          }
        }
      }
      if (derivedChain.length > 0) chain = derivedChain
    }
  }

  if (!chain) return []

  const linkedIds = await findLinkedRecordIds(
    primaryDiff.exportRecordIds,
    primaryScopeId,
    secondaryScopeId,
    qc,
    chain,
  )
  if (linkedIds.size === 0) return []
  return records.filter((r) => linkedIds.has(r.id))
}

function ExportColumnPickerModal({
  scopeId,
  selectedCount,
  selectedRecordIds,
  onClose,
  onDownloaded: _onDownloaded, // eslint-disable-line @typescript-eslint/no-unused-vars
}: {
  scopeId: string
  selectedCount: number
  selectedRecordIds: Set<string>
  onClose: () => void
  onDownloaded: () => void
}) {
  const { translate } = useTranslation()
  const [exportFormat, setExportFormat] = useState<ExportFormat>("csv")
  const [downloading, setDownloading] = useState(false)
  const qc = useQueryClient()
  const formatLabels: Record<string, string> = { csv: "CSV", excel: "Excel" }

  async function handleExportProceed(
    columns: DownloadColumn[],
    format: string,
  ) {
    setDownloading(true)
    try {
      // Group columns by scope
      const columnsByScope = new Map<string, DownloadColumn[]>()
      for (const c of columns) {
        const sid = c.scope_id
        if (!columnsByScope.has(sid)) columnsByScope.set(sid, [])
        columnsByScope.get(sid)!.push(c)
      }

      const primaryColumns = columnsByScope.get(scopeId) ?? []
      const secondaryScopeIds = [...columnsByScope.keys()].filter(
        (s) => s !== scopeId,
      )

      // --- Primary scope diff ---
      const primaryRegularCols = primaryColumns.filter((c) => !c.rollupConfig)
      const previewColumns = primaryRegularCols.map((c) => ({
        primitive_id: c.primitive_id,
        scope_id: c.scope_id,
        rollup: false,
      }))

      const preview = await api.exportPreview(scopeId, {
        record_ids: [...selectedRecordIds],
        columns: previewColumns,
      })

      const cached = qc.getQueryData<{
        records: {
          id: string
          values: Record<string, { value: string | null }>
        }[]
        scope: { primitives: { id: string; name: string; is_pk: boolean }[] }
      }>(["records", scopeId])
      const pkPrim = cached?.scope.primitives.find((p) => p.is_pk)

      const diffRecords: Record<string, DiffRecord> = {}
      const available = await api.exportAvailableColumns(scopeId)

      // Seed a placeholder diff record per selected anchor so templates that
      // don't map any primary-scope columns (e.g. customer-anchored exports
      // whose columns all live on product) still render the anchor row in
      // the diff view - otherwise the primary tab looks empty and the user
      // can't see which records they're actually exporting for.
      for (const rid of selectedRecordIds) {
        const cachedRec = cached?.records.find((r) => r.id === rid)
        const pkVal = pkPrim
          ? (cachedRec?.values[pkPrim.name]?.value ?? rid)
          : rid
        diffRecords[rid] = {
          recordId: rid,
          pkValue: pkVal,
          nameValue: null,
          isNew: false,
          cells: {},
        }
      }

      for (const cell of preview.cells) {
        if (!diffRecords[cell.record_id]) {
          const cachedRec = cached?.records.find((r) => r.id === cell.record_id)
          const pkVal = pkPrim
            ? (cachedRec?.values[pkPrim.name]?.value ?? cell.record_id)
            : cell.record_id
          diffRecords[cell.record_id] = {
            recordId: cell.record_id,
            pkValue: pkVal,
            nameValue: null,
            isNew: false,
            cells: {},
          }
        }
        const avail = available.find(
          (c) => c.primitive_id === cell.primitive_id,
        )
        if (avail?.is_pk) continue
        if (avail && avail.scope_id !== scopeId) continue
        const colDef = columns.find((c) => c.primitive_id === cell.primitive_id)
        const title =
          colDef?.label ?? avail?.primitive_title ?? cell.primitive_id
        const type = avail?.primitive_type ?? "string"
        // Cell layout in the diff view:
        //   dbValue   → right side (with timestamp) = DB value, read-only.
        //   fileValue → left side (accept / edit / decline) = document
        //               value, i.e. what ends up in the exported file.
        // primitiveName carries the BACKEND name (snake_case) so the
        // download's override map keys match what the backend expects.
        diffRecords[cell.record_id].cells[cell.primitive_id] = {
          recordId: cell.record_id,
          primitiveId: cell.primitive_id,
          primitiveName: avail?.primitive_name ?? cell.primitive_id,
          primitiveTitle: title,
          primitiveType: type as PrimitiveType,
          dbValue: cell.db_value,
          fileValue: null,
          rawFileValue: null,
          typeIncompatible: false,
          accepted: null,
          lastHistorySource: cell.db_source ?? null,
          lastHistoryTimestamp: cell.db_updated_at ?? null,
        }
      }

      // Populate helper cells on the primary diff so any helper column
      // declared on the anchor scope renders its computed value in the
      // diff view (evaluateFormula reads the record's raw primitives as
      // bare identifiers - same contract as the wizard's HelperEditor).
      const primaryHelperCols = primaryColumns.filter((c) => c.helperConfig)
      if (primaryHelperCols.length > 0) {
        for (const rid of selectedRecordIds) {
          const rec = cached?.records.find((r) => r.id === rid)
          if (!rec || !diffRecords[rid]) continue
          const identifiers: Record<string, string> = {}
          for (const [name, v] of Object.entries(rec.values)) {
            identifiers[name] = v?.value ?? ""
          }
          for (const hc of primaryHelperCols) {
            if (!hc.helperConfig) continue
            const result = evaluateFormula(
              hc.helperConfig.expression,
              { identifiers },
              translate,
            )
            // Helpers follow the same layout: computed value on the right
            // (dbValue) for read-only reference, left (fileValue) empty for
            // the user to optionally override the exported value.
            // primitiveName = helperConfig.label (the identifier) so escha-
            // style downstream consumers that key by helper.label match.
            diffRecords[rid].cells[hc.primitive_id] = {
              recordId: rid,
              primitiveId: hc.primitive_id,
              primitiveName: hc.helperConfig.label,
              primitiveTitle: hc.label,
              primitiveType: "formula" as PrimitiveType,
              dbValue: result.value ?? "",
              fileValue: null,
              rawFileValue: null,
              typeIncompatible: false,
              accepted: null,
              lastHistorySource: null,
              lastHistoryTimestamp: null,
            }
          }
        }
      }

      const resolvedExportColumns: DownloadColumn[] = columns.map((c) => ({
        primitive_id: c.primitive_id,
        scope_id: c.scope_id,
        label: c.label,
        rollup: !!c.rollupConfig,
        rollupConfig: c.rollupConfig,
        helperConfig: c.helperConfig,
      }))

      const primaryDiffState: import("../../types").DiffState = {
        scopeId,
        fileName: `${scopeId}_export`,
        records: diffRecords,
        rawExtractions: [],
        anchoredRecords: [],
        exportMode: true,
        targetFormat: format as ExportFormat,
        exportColumns: resolvedExportColumns,
        exportRecordIds: [...selectedRecordIds],
      }

      useStore.getState().enterDiffMode(primaryDiffState)

      // --- Secondary scope diffs (e.g., SVHC with rollups from Component) ---
      for (const secScopeId of secondaryScopeIds) {
        const secColumns = columnsByScope.get(secScopeId) ?? []
        const secRegularCols = secColumns.filter((c) => !c.rollupConfig)
        const secRollupCols = secColumns.filter((c) => c.rollupConfig)
        // Find records in this secondary scope - fetch if not cached
        const secRecordIds = new Set<string>()
        let secCachedPage = qc.getQueryData<{
          records: {
            id: string
            values: Record<
              string,
              { value: string | null; source?: string; updated_at?: string }
            >
          }[]
          scope: {
            primitives: {
              id: string
              name: string
              is_pk: boolean
              type: string
              title: string
              related_scope_id?: string | null
            }[]
          }
        }>(["records", secScopeId])

        if (!secCachedPage) {
          try {
            secCachedPage = await qc.fetchQuery({
              queryKey: ["records", secScopeId],
              queryFn: () => api.listRecords(secScopeId),
            })
          } catch {
            /* ignore */
          }
        }

        if (secCachedPage) {
          // Filter: only include secondary records transitively linked to the selected primary records.
          // Traverse the relation chain: prefer template config, then derive from actionState.scopeMappings.
          const conformityConfig = getConformityConfig(exportFormat)
          let chainForTraversal = conformityConfig?.relations
          if (!chainForTraversal) {
            // Derive relation chain from actionState.scopeMappings (set by ColumnMappingStep.proceed()).
            // Uses fetchQuery to load scope data on demand for intermediate scopes
            // that may not be in cache (e.g., Component when the user skipped that tab).
            const actionSt = useStore.getState().actionState
            if (actionSt?.scopeMappings) {
              const derivedChain: {
                from_scope: string
                relation_name: string
                to_scope: string
              }[] = []
              const visited = new Set<string>([scopeId])
              const queue = [scopeId]
              while (queue.length > 0) {
                const current = queue.shift()!
                let primitives: {
                  id: string
                  name: string
                  type: string
                  related_scope_id?: string | null
                }[] = []
                // Try cache first, then fetch on demand
                const cached =
                  qc.getQueryData<{ primitives: typeof primitives }>([
                    "scope",
                    current,
                  ]) ??
                  qc.getQueryData<{ scope: { primitives: typeof primitives } }>(
                    ["records", current],
                  )
                primitives =
                  cached && "primitives" in cached
                    ? cached.primitives
                    : ((
                        cached as { scope?: { primitives?: typeof primitives } }
                      )?.scope?.primitives ?? [])
                if (primitives.length === 0) {
                  try {
                    const fetched = await qc.fetchQuery({
                      queryKey: ["scope", current],
                      queryFn: () => api.getScope(current),
                    })
                    primitives = fetched?.primitives ?? []
                  } catch {
                    /* ignore */
                  }
                }
                for (const [targetSid, mapping] of Object.entries(
                  actionSt.scopeMappings,
                )) {
                  if (visited.has(targetSid) || mapping.status === "removed")
                    continue
                  const relPrim = primitives.find(
                    (p) => p.id === mapping.relationPrimitiveId,
                  )
                  if (relPrim) {
                    derivedChain.push({
                      from_scope: current,
                      relation_name: relPrim.name,
                      to_scope: targetSid,
                    })
                    visited.add(targetSid)
                    queue.push(targetSid)
                  }
                }
              }
              if (derivedChain.length > 0) chainForTraversal = derivedChain
            }
          }
          const linkedSecIds = await findLinkedRecordIds(
            [...selectedRecordIds],
            scopeId,
            secScopeId,
            qc,
            chainForTraversal,
          )
          if (linkedSecIds.size > 0) {
            // Use only linked records
            for (const id of linkedSecIds) {
              if (secCachedPage.records.some((r) => r.id === id)) {
                secRecordIds.add(id)
              }
            }
          } else {
            // Fallback: if traversal failed, use all records
            for (const rec of secCachedPage.records) {
              secRecordIds.add(rec.id)
            }
          }
        }

        if (secRecordIds.size === 0) continue

        // Fetch available columns for this secondary scope (needed for rollup primitive resolution)
        let secAvailable: ReachablePrimitive[] = []
        try {
          secAvailable = await api.exportAvailableColumns(secScopeId)
        } catch {
          /* ignore */
        }

        const secPkPrim = secCachedPage?.scope.primitives.find((p) => p.is_pk)
        const secDiffRecords: Record<string, DiffRecord> = {}

        // Build diff records from regular (non-rollup) columns using cached data
        for (const recId of secRecordIds) {
          const rec = secCachedPage?.records.find((r) => r.id === recId)
          if (!rec) continue
          const pkVal = secPkPrim
            ? (rec.values[secPkPrim.name]?.value ?? recId)
            : recId
          secDiffRecords[recId] = {
            recordId: recId,
            pkValue: pkVal,
            nameValue: null,
            isNew: false,
            cells: {},
          }
          for (const col of secRegularCols) {
            const primDef = secCachedPage?.scope.primitives.find(
              (p) => p.id === col.primitive_id,
            )
            if (!primDef || primDef.is_pk) continue
            const val = rec.values[primDef.name]
            // primitiveName = backend snake_case name so overrides sent to
            // the conformity / export endpoints key correctly.
            secDiffRecords[recId].cells[col.primitive_id] = {
              recordId: recId,
              primitiveId: col.primitive_id,
              primitiveName: primDef.name,
              primitiveTitle: col.label,
              primitiveType: (primDef.type ?? "string") as PrimitiveType,
              dbValue: val?.value ?? null,
              fileValue: null,
              rawFileValue: null,
              typeIncompatible: false,
              accepted: null,
              lastHistorySource: val?.source ?? null,
              lastHistoryTimestamp: val?.updated_at ?? null,
            }
          }
        }

        // Evaluate helper/formula columns against each secondary record's
        // primitive values.  Helpers are client-side virtual columns - the
        // backend doesn't know their expressions, so we compute them here
        // using evaluateFormula (the same evaluator the wizard's
        // HelperEditor uses for live preview).
        const secHelperCols = secColumns.filter((c) => c.helperConfig)
        if (secHelperCols.length > 0) {
          for (const recId of Object.keys(secDiffRecords)) {
            const rec = secCachedPage?.records.find((r) => r.id === recId)
            if (!rec) continue
            const identifiers: Record<string, string> = {}
            for (const [name, v] of Object.entries(rec.values)) {
              identifiers[name] = v?.value ?? ""
            }
            for (const hc of secHelperCols) {
              if (!hc.helperConfig) continue
              const result = evaluateFormula(
                hc.helperConfig.expression,
                { identifiers },
                translate,
              )
              // See primary-helper comment above: computed value in fileValue,
              // Helper: computed value on the right (dbValue); document
              // value (overridable via edits) on the left, starts empty.
              // primitiveName = helperConfig.label (snake_case identifier)
              // so escha-style consumers keying by helper.label match.
              secDiffRecords[recId].cells[hc.primitive_id] = {
                recordId: recId,
                primitiveId: hc.primitive_id,
                primitiveName: hc.helperConfig.label,
                primitiveTitle: hc.label,
                primitiveType: "formula" as PrimitiveType,
                dbValue: result.value ?? "",
                fileValue: null,
                rawFileValue: null,
                typeIncompatible: false,
                accepted: null,
                lastHistorySource: null,
                lastHistoryTimestamp: null,
              }
            }
          }
        }

        // Compute rollup values for this scope's records
        if (secRollupCols.length > 0) {
          const secRecordIdList = [...secRecordIds]
          for (const rc of secRollupCols) {
            if (!rc.rollupConfig) continue
            // Resolve source primitive ID: prefer the one from rollupConfig,
            // fallback to looking up by scope_id + name in available columns,
            // then try the query cache for the source scope's primitives
            let resolvedPrimId = rc.rollupConfig.sourcePrimitiveId
            if (!resolvedPrimId) {
              const found =
                secAvailable.find(
                  (a) =>
                    a.scope_id === rc.rollupConfig!.sourceScopeId &&
                    a.primitive_name === rc.rollupConfig!.sourcePrimitiveName,
                ) ??
                available.find(
                  (a) =>
                    a.scope_id === rc.rollupConfig!.sourceScopeId &&
                    a.primitive_name === rc.rollupConfig!.sourcePrimitiveName,
                )
              resolvedPrimId = found?.primitive_id ?? ""
            }
            // Last resort: look up in the source scope's primitives (cache first, then fetch)
            if (!resolvedPrimId) {
              let prims: { id: string; name: string }[] = []
              const srcScopeData =
                qc.getQueryData<{ primitives: { id: string; name: string }[] }>(
                  ["scope", rc.rollupConfig.sourceScopeId],
                ) ??
                qc.getQueryData<{
                  scope: { primitives: { id: string; name: string }[] }
                }>(["records", rc.rollupConfig.sourceScopeId])
              prims =
                srcScopeData && "primitives" in srcScopeData
                  ? srcScopeData.primitives
                  : ((
                      srcScopeData as {
                        scope?: { primitives?: { id: string; name: string }[] }
                      }
                    )?.scope?.primitives ?? [])
              if (prims.length === 0) {
                try {
                  const fetched = await qc.fetchQuery({
                    queryKey: ["scope", rc.rollupConfig.sourceScopeId],
                    queryFn: () => api.getScope(rc.rollupConfig!.sourceScopeId),
                  })
                  prims = fetched?.primitives ?? []
                } catch {
                  /* ignore */
                }
              }
              const srcPrim = prims.find(
                (p: { id: string; name: string }) =>
                  p.name === rc.rollupConfig!.sourcePrimitiveName,
              )
              if (srcPrim) resolvedPrimId = srcPrim.id
            }
            if (!resolvedPrimId) {
              console.warn(
                "[ExportProceed] Could not resolve primitive ID for rollup",
                rc.label,
                rc.rollupConfig,
              )
              continue
            }
            try {
              const result = await api.computeRollup(secScopeId, {
                record_ids: secRecordIdList,
                columns: [
                  {
                    primitive_id: resolvedPrimId,
                    primitive_scope_id: rc.rollupConfig.sourceScopeId,
                    aggregation: rc.rollupConfig.aggregation,
                    label: rc.label,
                  },
                ],
              })

              for (const rv of result.values) {
                if (!secDiffRecords[rv.record_id]) {
                  continue
                }
                // Rollup: aggregated DB value on the right (dbValue) for
                // reference; left (fileValue) empty for user overrides.
                secDiffRecords[rv.record_id].cells[rc.primitive_id] = {
                  recordId: rv.record_id,
                  primitiveId: rc.primitive_id,
                  primitiveName: rc.label,
                  primitiveTitle: rc.label,
                  primitiveType: "string" as PrimitiveType,
                  dbValue: rv.value,
                  fileValue: null,
                  rawFileValue: null,
                  typeIncompatible: false,
                  accepted: null,
                  lastHistorySource: null,
                  lastHistoryTimestamp: null,
                }
              }
            } catch (e) {
              console.warn(
                "[ExportProceed] Rollup computation failed for",
                rc.label,
                e,
              )
            }
          }
        }

        if (Object.keys(secDiffRecords).length > 0) {
          const secDiff: import("../../types").ScopeDiffState = {
            scopeId: secScopeId,
            fileName: `${secScopeId}_export`,
            records: secDiffRecords,
            rawExtractions: [],
            anchoredRecords: [],
            exportMode: true,
            targetFormat: format as ExportFormat,
            exportColumns: secColumns.map((c) => ({
              primitive_id: c.primitive_id,
              scope_id: c.scope_id,
              label: c.label,
              rollup: !!c.rollupConfig,
              rollupConfig: c.rollupConfig,
              helperConfig: c.helperConfig,
            })),
            exportRecordIds: [...secRecordIds],
          }
          useStore.getState().setActionScopeDiff(secScopeId, secDiff)
        }
      }

      onClose()
    } catch (e) {
      console.error("Preview failed:", e)
    } finally {
      setDownloading(false)
    }
  }

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 backdrop-blur-xs">
      <div className="flex max-h-[85vh] w-full max-w-2xl flex-col overflow-hidden rounded-2xl bg-grey2 shadow-2xl">
        <div className="flex items-center justify-between border-b px-6 py-4">
          <div>
            <h2 className="text-base font-semibold text-white">
              {translate("QMS/PIVOT/DOWNLOAD_RECORDS", {
                count: selectedCount,
              })}
            </h2>
            <p className="mt-0.5 text-xs text-grey5">
              {translate("QMS/PIVOT/CONFIGURE_COLUMNS")}
            </p>
          </div>
          <button
            onClick={onClose}
            className="p-1 text-xl leading-none text-grey5 hover:text-grey6"
          >
            x
          </button>
        </div>

        <div className="flex-1 overflow-y-auto p-6">
          {downloading ? (
            <div className="flex items-center justify-center py-12">
              <div className="h-6 w-6 animate-spin rounded-full border-2 border-yellow5 border-t-yellow1" />
            </div>
          ) : (
            <>
              {/* Freshness date filter */}
              <div className="mb-4 rounded-xl border border-grey4 bg-grey1 p-3">
                <FreshnessDatePicker />
              </div>

              {/* Format selector */}
              <div className="mb-4">
                <label className="mb-1.5 block text-xs font-medium text-grey5">
                  {translate("QMS/PIVOT/EXPORT_FORMAT_LABEL")}
                </label>
                <div className="flex gap-2">
                  {(["csv", "excel"] as const).map((fmt) => (
                    <button
                      key={fmt}
                      onClick={() => setExportFormat(fmt)}
                      className={`flex-1 rounded-lg border px-3 py-2 text-xs font-medium transition-colors ${
                        exportFormat === fmt
                          ? "border-yellow1 bg-yellow5/20 text-yellow1"
                          : "border-grey4 bg-grey2 text-grey5 hover:bg-grey3"
                      }`}
                    >
                      {formatLabels[fmt]}
                    </button>
                  ))}
                  <TemplatesDropdown
                    onSelectFormat={(fmt) => setExportFormat(fmt)}
                    selectedFormat={exportFormat}
                    scopeId={scopeId}
                  />
                </div>
              </div>

              {/* Unified column mapping step in export mode */}
              <ColumnMappingStep
                key={
                  hasTemplateMapping(exportFormat)
                    ? `conformity-${exportFormat}`
                    : "standard"
                }
                scopeId={scopeId}
                mode="export"
                onExportProceed={handleExportProceed}
                selectedRecordIds={selectedRecordIds}
                selectedCount={selectedCount}
                exportFormatState={exportFormat}
                onExportFormatChange={setExportFormat}
                readOnly={hasTemplateMapping(exportFormat)}
                exportColumnFilter={
                  hasTemplateMapping(exportFormat)
                    ? (getConformityConfig(exportFormat)?.columns ??
                      CONFORMITY_COLUMNS)
                    : undefined
                }
                exportRelationChain={
                  hasTemplateMapping(exportFormat)
                    ? (getConformityConfig(exportFormat)?.relations ??
                      CONFORMITY_RELATIONS)
                    : undefined
                }
              />
            </>
          )}
        </div>
      </div>
    </div>
  )
}

// ---------------------------------------------------------------------------
// ---------------------------------------------------------------------------
// Transitive count tag - shows count of records reachable via multi-hop
// ---------------------------------------------------------------------------

const SCOPE_COLORS_TRANSITIVE: Record<string, string> = {
  supplier: "bg-purple1/15 text-[#c084fc] hover:bg-purple1/25",
  component: "bg-yellow1/10 text-yellow3/80 hover:bg-yellow1/15",
  product: "bg-success/10 text-success/80 hover:bg-success/20",
  customer: "bg-warning/10 text-warning/80 hover:bg-warning/20",
  substance: "bg-red1/15 text-[#ff9a8d] hover:bg-red1/25",
}

function TransitiveCountTag({
  count,
  scopeId,
  scopeName,
  onClick,
  active,
  earliestUpdatedAt,
}: {
  count: number
  scopeId: string
  scopeName: string
  onClick: () => void
  active?: boolean
  earliestUpdatedAt?: string | null
}) {
  const { translate } = useTranslation()
  const color = SCOPE_COLORS_TRANSITIVE[scopeId] ?? "bg-grey4/20 text-grey5/80"
  const isDiffMode = useStore(
    (s) =>
      s.actionState !== null && Object.keys(s.actionState.diffs).length > 0,
  )
  const freshnessDate = useStore((s) => s.freshnessDate)
  const freshness =
    isDiffMode && count > 0
      ? getRelationFreshness(earliestUpdatedAt ?? null, freshnessDate)
      : null
  const fStyles = freshness ? RELATION_FRESHNESS_STYLES[freshness] : null

  if (count === 0) {
    return <span className="text-xs text-grey5">--</span>
  }

  return (
    <div className="flex flex-col items-start gap-0.5">
      {freshness && (
        <span className={clsx("text-[9px] leading-tight", fStyles!.text)}>
          {freshness === "empty"
            ? translate("QMS/PIVOT/NO_DATE")
            : translate("QMS/PIVOT/OLDEST_LABEL", {
                ts: formatRelativeTime(earliestUpdatedAt!, translate),
              })}
        </span>
      )}
      <button
        onClick={onClick}
        className={clsx(
          "inline-flex items-center gap-1 rounded-full border border-dashed px-2 py-0.5 text-xs font-medium transition-colors",
          color,
          active && "ring-2 ring-current ring-offset-1 ring-offset-grey2",
          fStyles && `${fStyles.bg} ${fStyles.border}`,
        )}
        title={translate("QMS/PIVOT/TRANSITIVE_TITLE", {
          count,
          scope: scopeName,
        })}
      >
        <span>{count}</span>
        <span className="opacity-60">{scopeName}</span>
      </button>
    </div>
  )
}

// ---------------------------------------------------------------------------
// Row
// ---------------------------------------------------------------------------

interface RowProps {
  record: QmsRecord
  scope: Scope
  visiblePrims: PrimitiveDef[]
  relationPrims: PrimitiveDef[]
  onRowClick: () => void
  downloadMode?: boolean
  selected?: boolean
  onToggleSelect?: () => void
  relationLevelFilters?: Record<string, string>
  relationLevelLabels?: Record<string, string>
  draggingOver?: boolean
}

// ---------------------------------------------------------------------------
// Diff mode row - renders diff cells instead of normal PivotCells
// ---------------------------------------------------------------------------

function DiffPivotRow({
  record,
  diffRec,
  scope,
  visiblePrims,
  relationPrims,
  relationDiffs,
  exportMode,
  onAcceptCell,
  onAcceptRow,
  onEditCell,
  onDeclineCell,
  onAcceptRelationEntry,
  onAcceptAllRelations,
  secondary, // eslint-disable-line @typescript-eslint/no-unused-vars
  diffFilterMode, // eslint-disable-line @typescript-eslint/no-unused-vars
  relationLevelLabels,
}: {
  record: QmsRecord
  diffRec: DiffRecord
  scope: Scope
  visiblePrims: PrimitiveDef[]
  relationPrims: PrimitiveDef[]
  relationDiffs?: Record<string, RelationDiff>
  exportMode: boolean
  secondary?: boolean
  diffFilterMode: "rows" | "changes" | "updates"
  onAcceptCell: (primitiveId: string) => void
  onAcceptRow: () => void
  onEditCell: (
    primitiveId: string,
    value: string,
    typeIncompatible: boolean,
  ) => void
  onDeclineCell: (primitiveId: string) => void
  onAcceptRelationEntry: (primitiveId: string, targetId: string) => void
  onAcceptAllRelations: (primitiveId: string) => void
  relationLevelLabels?: Record<string, string>
}) {
  const { translate, currentLanguage } = useTranslation()
  const [expandedPrimId, setExpandedPrimId] = useState<string | null>(null)
  const transitiveCounts = useContext(TransitiveCountsCtx)
  const scopeOrderIndex = useContext(ScopeOrderCtx)

  // Determine if expanding a direct relation or a transitive one (prefixed "trans::")
  const isTransitiveExpand = expandedPrimId?.startsWith("trans::") ?? false
  const expandedTransitiveScopeId = isTransitiveExpand
    ? expandedPrimId!.slice(7)
    : null

  // Lazy-fetch history for this record (only for existing records, not new ones)
  const { data: historyData } = useQuery({
    queryKey: ["history", record.id],
    queryFn: () => api.getHistory(record.id),
    enabled: !diffRec.isNew,
    staleTime: 5 * 60_000,
  })

  // Fetch relation preview for the expanded direct relation primitive.
  // Use cached data from extraction when available (avoids re-fetch on expand/collapse).
  const expandedPrim =
    !isTransitiveExpand && expandedPrimId
      ? relationPrims.find((p) => p.id === expandedPrimId)
      : null
  const cachedPreview =
    expandedPrimId && relationDiffs?.[expandedPrimId]?.cachedRelationData
  const { data: relationData, isLoading: relationLoading } = useQuery({
    // eslint-disable-next-line @tanstack/query/exhaustive-deps
    queryKey: ["relation-preview", record.id, expandedPrim?.name],
    queryFn: () => api.getRelationPreview(record.id, expandedPrim!.name),
    enabled: !!expandedPrim && !cachedPreview,
    initialData: cachedPreview || undefined,
    staleTime: cachedPreview ? Infinity : 0,
  })

  // Transitive relation preview via graph traversal
  const pivotPkPrim = scope.primitives.find((p) => p.is_pk)
  const pivotPkValue = pivotPkPrim
    ? record.values[pivotPkPrim.name]?.value
    : null
  const { data: transitivePageData, isLoading: transitiveLoading } = useQuery({
    queryKey: [
      "filtered-records",
      expandedTransitiveScopeId,
      scope.id,
      pivotPkValue,
    ],
    queryFn: () =>
      api.listFilteredRecords(expandedTransitiveScopeId!, scope.id, [
        pivotPkValue!,
      ]),
    enabled:
      isTransitiveExpand && !!expandedTransitiveScopeId && !!pivotPkValue,
  })

  // Convert transitive RecordsPage into RelationPreview shape
  const transitiveRelationData: RelationPreview | undefined = transitivePageData
    ? {
        primitive_id: "",
        primitive_name: "",
        related_scope_id: expandedTransitiveScopeId!,
        records: transitivePageData.records.map((r) => {
          const rpk = transitivePageData.scope.primitives.find((p) => p.is_pk)
          const rname = transitivePageData.scope.primitives.find(
            (p) => !p.is_pk && p.type === "string" && p.is_visible,
          )
          const relTs = transitivePageData.relation_timestamps?.[r.id]
          return {
            id: r.id,
            scope_id: r.scope_id,
            pk_value: rpk ? (r.values[rpk.name]?.value ?? null) : null,
            name_value: rname ? (r.values[rname.name]?.value ?? null) : null,
            values: r.values,
            mapped_id: null,
            relation_id: null,
            relation_source: relTs?.source ?? null,
            relation_created_at: relTs?.created_at ?? null,
          }
        }),
        scope_primitives: transitivePageData.scope.primitives,
      }
    : undefined

  const effectiveRelationData = isTransitiveExpand
    ? transitiveRelationData
    : relationData
  const effectiveRelationLoading = isTransitiveExpand
    ? transitiveLoading
    : relationLoading

  // Build per-primitive-id history lookup
  const historyByPrimId = React.useMemo(() => {
    const map = new Map<string, HistoryEntry[]>()
    if (!historyData) return map
    for (const group of historyData) {
      map.set(group.primitive_id, group.entries)
    }
    return map
  }, [historyData])

  const freshnessDate = useStore((s) => s.freshnessDate)
  const hasUnresolvedPrimary = Object.values(diffRec.cells).some(
    (c) => c.accepted === null && !c.typeIncompatible,
  )
  const hasUnresolvedRels = relationDiffs
    ? Object.values(relationDiffs).some((rd) =>
        rd.entries.some((e) => e.accepted === null),
      )
    : false
  // Row-level freshness: unresolved if stale and not yet confirmed
  const rowFreshnessUnresolved =
    diffRec.rowFreshnessAccepted === null &&
    getFreshnessStatus(diffRec.rowUpdatedAt ?? null, freshnessDate) !==
      "fresh" &&
    freshnessDate !== null
  const hasUnresolved =
    hasUnresolvedPrimary || rowFreshnessUnresolved || hasUnresolvedRels
  const rowIsPrimary = hasUnresolvedPrimary || hasUnresolvedRels

  const rollupColCount = (() => {
    const rsState = useStore.getState().rollupState
    if (!rsState) return 0
    return relationPrims
      .filter((p) => p.is_visible && p.related_scope_id)
      .reduce((sum, p) => {
        return (
          sum +
          rsState.columns.filter(
            (c) => c.primitiveScopeId === p.related_scope_id,
          ).length
        )
      }, 0)
  })()
  const totalCols =
    1 +
    visiblePrims.length +
    rollupColCount +
    relationPrims.filter((p) => p.is_visible).length +
    (transitiveCounts?.transitive_scopes.length ?? 0)

  return (
    <>
      <tr
        className={clsx(
          "group border-b transition-colors",
          expandedPrimId && "border-b-0 bg-grey3/30",
        )}
      >
        {/* Actions column - frozen first */}
        <td
          className="sticky left-0 z-2 border-r border-grey4 bg-grey2! px-4 py-2 text-left [box-shadow:2px_0_4px_-2px_rgba(0,0,0,0.08)] group-hover:bg-[#323235]!"
          style={{ width: "7rem", minWidth: "7rem", maxWidth: "7rem" }}
        >
          {hasUnresolved ? (
            <button
              onClick={() => {
                onAcceptRow()
                if (relationDiffs) {
                  for (const [primId] of Object.entries(relationDiffs)) {
                    onAcceptAllRelations(primId)
                  }
                }
              }}
              className={clsx(
                "rounded px-2 py-1 text-xs font-medium transition-colors",
                rowIsPrimary
                  ? "bg-success/20 text-success hover:bg-success/30"
                  : rowFreshnessUnresolved
                    ? "bg-yellow1/20 text-yellow1 hover:bg-yellow1/30"
                    : "bg-grey4/50 text-grey5 hover:bg-grey4 hover:text-grey6",
              )}
              title={
                rowIsPrimary
                  ? translate("QMS/PIVOT/ACCEPT_ROW_TITLE")
                  : translate("QMS/PIVOT/CONFIRM_ROW_TITLE")
              }
            >
              {rowIsPrimary
                ? translate("QMS/PIVOT/ACCEPT_ROW")
                : translate("QMS/PIVOT/CONFIRM_ROW")}
            </button>
          ) : (
            <span className="text-xs text-success">
              <svg
                className="inline h-4 w-4"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
                strokeWidth={2.5}
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  d="M5 13l4 4L19 7"
                />
              </svg>
            </span>
          )}
        </td>
        {visiblePrims.map((p) => {
          const diffCell = diffRec.cells[p.id]
          const isPk = p.is_pk
          const diffRollupScope = p.config?._rollupSourceScope as
            | string
            | undefined
          const isHelper = isHelperPrim(p)
          if (diffCell) {
            // Helper cells stay visually prominent even after the user has
            // accepted/declined other diffs - they're computed values, not
            // decisions, so fading them out would just hide useful context.
            const cellFaded = diffCell.accepted !== null && !isHelper
            return (
              <td
                key={p.id}
                className={clsx(
                  "border-r border-grey4/30 px-4 py-2 last:border-r-0",
                  isHelper
                    ? HELPER_CELL_CLASS
                    : diffRollupScope
                      ? (ROLLUP_SCOPE_CELL[diffRollupScope] ?? "")
                      : "",
                  isPk
                    ? "sticky z-2 border-r border-grey4 bg-grey2! [box-shadow:2px_0_4px_-2px_rgba(0,0,0,0.08)] group-hover:bg-[#323235]!"
                    : "group-hover:bg-grey3/30",
                )}
                style={
                  isPk ? { left: "var(--diff-action-col-w, 6rem)" } : undefined
                }
              >
                <DiffCellComponent
                  cell={diffCell}
                  exportMode={exportMode}
                  faded={cellFaded}
                  onAccept={() => onAcceptCell(p.id)}
                  onEdit={(val, ti) => onEditCell(p.id, val, ti)}
                  onDecline={() => onDeclineCell(p.id)}
                  history={historyByPrimId.get(p.id)}
                />
              </td>
            )
          }
          // No diff for this cell - show normal value
          const rsVals = useStore.getState().rollupState?.values
          const val =
            record.values[p.name] ??
            (rsVals?.[record.id]?.[p.id]?.value != null
              ? {
                  value: rsVals[record.id][p.id].value,
                  source: "rollup",
                  updated_at: rsVals[record.id][p.id].updated_at ?? undefined,
                }
              : undefined)
          return (
            <td
              key={p.id}
              className={clsx(
                "border-r border-grey4/30 px-4 py-2 last:border-r-0",
                isHelper
                  ? HELPER_CELL_CLASS
                  : diffRollupScope
                    ? (ROLLUP_SCOPE_CELL[diffRollupScope] ?? "")
                    : "",
                isPk
                  ? "sticky z-2 border-r border-grey4 bg-grey2! [box-shadow:2px_0_4px_-2px_rgba(0,0,0,0.08)] group-hover:bg-[#323235]!"
                  : "group-hover:bg-grey3/30",
              )}
              style={
                isPk ? { left: "var(--diff-action-col-w, 6rem)" } : undefined
              }
            >
              <span className="whitespace-nowrap text-sm text-grey5">
                {val?.value || <span className="text-grey5">--</span>}
              </span>
            </td>
          )
        })}
        {/* Relation columns - count badge with diff delta, clickable to expand */}
        {buildRelationSlots(
          relationPrims.filter((p) => p.is_visible),
          transitiveCounts?.transitive_scopes,
          scopeOrderIndex,
        ).flatMap((slot) => {
          if (slot.kind === "transitive") {
            const ts = slot.ts
            const count =
              transitiveCounts!.counts[record.id]?.[ts.scope_id] ?? 0
            const transKey = `trans::${ts.scope_id}`
            const transitiveEarliestTs =
              transitiveCounts!.earliest_timestamps?.[record.id]?.[
                ts.scope_id
              ] ?? null
            return [
              <td
                key={`trans-${ts.scope_id}`}
                className="border-r border-grey4/30 px-4 py-2 last:border-r-0 group-hover:bg-grey3/30"
              >
                <div className="flex flex-col gap-0.5">
                  <TransitiveCountTag
                    count={count}
                    scopeId={ts.scope_id}
                    scopeName={getScopeDisplayName(
                      { id: ts.scope_id, name: ts.scope_name },
                      currentLanguage,
                    )}
                    onClick={() =>
                      setExpandedPrimId((prev) =>
                        prev === transKey ? null : transKey,
                      )
                    }
                    active={expandedPrimId === transKey}
                    earliestUpdatedAt={transitiveEarliestTs}
                  />
                </div>
              </td>,
            ]
          }
          const p = slot.prim
          {
            // Render rollup diff cells for this relation's scope
            const rsState = useStore.getState().rollupState
            const scopeRollupCols =
              rsState && p.related_scope_id
                ? rsState.columns.filter(
                    (c) => c.primitiveScopeId === p.related_scope_id,
                  )
                : []
            const rollupCells = scopeRollupCols.map((col) => {
              const diffCell = diffRec.cells[col.primitiveId]
              const diffRollupScope = p.related_scope_id
              if (diffCell) {
                const cellFaded = diffCell.accepted !== null
                return (
                  <td
                    key={col.primitiveId}
                    className={clsx(
                      "border-r border-grey4/30 px-4 py-2 last:border-r-0",
                      diffRollupScope
                        ? (ROLLUP_SCOPE_CELL[diffRollupScope] ?? "")
                        : "",
                      "group-hover:bg-grey3/30",
                    )}
                  >
                    <DiffCellComponent
                      cell={diffCell}
                      exportMode={exportMode}
                      faded={cellFaded}
                      onAccept={() => onAcceptCell(col.primitiveId)}
                      onEdit={(val, ti) => onEditCell(col.primitiveId, val, ti)}
                      onDecline={() => onDeclineCell(col.primitiveId)}
                      history={historyByPrimId.get(col.primitiveId)}
                    />
                  </td>
                )
              }
              const rsVals = rsState?.values
              const val =
                rsVals?.[record.id]?.[col.primitiveId]?.value != null
                  ? {
                      value: rsVals![record.id][col.primitiveId].value,
                      source: "rollup",
                      updated_at:
                        rsVals![record.id][col.primitiveId].updated_at ??
                        undefined,
                    }
                  : undefined
              return (
                <td
                  key={col.primitiveId}
                  className={clsx(
                    "border-r border-grey4/30 px-4 py-2 last:border-r-0",
                    diffRollupScope
                      ? (ROLLUP_SCOPE_CELL[diffRollupScope] ?? "")
                      : "",
                    "group-hover:bg-grey3/30",
                  )}
                >
                  <span className="whitespace-nowrap text-sm text-grey5">
                    {val?.value || "--"}
                  </span>
                </td>
              )
            })

            const rd = relationDiffs?.[p.id]
            const tag = record.relation_tags?.find(
              (t) => t.primitive_id === p.id,
            )
            const currentCount = rd?.currentCount ?? tag?.count ?? 0

            // Compute net delta from diff entries
            const addCount = rd
              ? rd.entries.filter((e) => e.action === "add").length
              : 0
            const removeCount = rd
              ? rd.entries.filter((e) => e.action === "remove").length
              : 0
            const netDelta = addCount - removeCount
            const hasDiff = rd && rd.entries.length > 0
            const unresolvedCount = rd
              ? rd.entries.filter((e) => e.accepted === null).length
              : 0
            const isExpanded = expandedPrimId === p.id

            return [
              ...rollupCells,
              <td
                key={p.id}
                className={clsx(
                  "border-r border-grey4/30 px-4 py-2 last:border-r-0",
                  "group-hover:bg-grey3/30",
                )}
              >
                <div className="flex flex-col gap-0.5">
                  <DiffRelationTag
                    count={currentCount}
                    delta={netDelta}
                    hasDiff={!!hasDiff}
                    unresolvedCount={unresolvedCount}
                    primTitle={getPrimitiveDisplayTitle(p, currentLanguage)}
                    levelLabel={
                      p.related_scope_id
                        ? relationLevelLabels?.[p.related_scope_id]
                        : undefined
                    }
                    relatedScopeId={p.related_scope_id ?? ""}
                    onClick={() =>
                      setExpandedPrimId((prev) => (prev === p.id ? null : p.id))
                    }
                    active={isExpanded}
                    earliestUpdatedAt={tag?.earliest_updated_at ?? null}
                    cascadeEarliestTimestamp={(() => {
                      // Find the earliest Record.updated_at across all upstream scopes
                      // reachable through this relation (from transitiveCounts)
                      const allTs =
                        transitiveCounts?.earliest_timestamps[record.id]
                      if (!allTs) return null
                      let earliest: string | null = null
                      for (const ts of Object.values(allTs)) {
                        if (ts && (!earliest || ts < earliest)) earliest = ts
                      }
                      return earliest
                    })()}
                  />
                </div>
              </td>,
            ]
          }
        })}
      </tr>
      {/* Expanded relation diff row */}
      {expandedPrimId && (
        <tr className="border-b bg-grey3/50">
          <td colSpan={totalCols} className="p-0">
            <div className="sticky left-0 w-fit max-w-[calc(100vw-10rem)] overflow-x-auto px-4 py-3">
              <DiffRelationExpandedRow
                relationData={effectiveRelationData}
                isLoading={effectiveRelationLoading}
                relationDiff={
                  isTransitiveExpand
                    ? undefined
                    : relationDiffs?.[expandedPrimId]
                }
                onAcceptEntry={(targetId) =>
                  isTransitiveExpand
                    ? undefined
                    : onAcceptRelationEntry(expandedPrimId, targetId)
                }
                onAcceptAll={() =>
                  isTransitiveExpand
                    ? undefined
                    : onAcceptAllRelations(expandedPrimId)
                }
              />
            </div>
          </td>
        </tr>
      )}
    </>
  )
}

// ---------------------------------------------------------------------------
// Diff relation tag - count badge with diff delta indicator
// ---------------------------------------------------------------------------

const DIFF_SCOPE_COLORS: Record<string, { base: string; ring: string }> = {
  supplier: {
    base: "bg-purple1/25 text-[#c084fc] hover:bg-purple1/35",
    ring: "ring-[#c084fc]",
  },
  component: {
    base: "bg-yellow1/15 text-yellow3 hover:bg-yellow1/25",
    ring: "ring-yellow3",
  },
  product: {
    base: "bg-success/20 text-success hover:bg-success/30",
    ring: "ring-success",
  },
  customer: {
    base: "bg-warning/20 text-warning hover:bg-warning/30",
    ring: "ring-warning",
  },
  substance: {
    base: "bg-red1/25 text-[#ff9a8d] hover:bg-red1/35",
    ring: "ring-[#ff9a8d]",
  },
}

const RELATION_FRESHNESS_STYLES = {
  fresh: {
    bg: "bg-success/15",
    border: "ring-1 ring-inset ring-success/30",
    text: "text-success",
  },
  stale: {
    bg: "bg-yellow1/15",
    border: "ring-1 ring-inset ring-yellow1/30",
    text: "text-yellow1",
  },
  empty: {
    bg: "bg-red1/15",
    border: "ring-1 ring-inset ring-red1/30",
    text: "text-red1",
  },
} as const

function getRelationFreshness(
  timestamp: string | null,
  freshnessDate: string | null,
): "fresh" | "stale" | "empty" {
  if (!timestamp) return "empty"
  if (!freshnessDate) return "fresh"
  return timestamp.slice(0, 10) >= freshnessDate ? "fresh" : "stale"
}

function DiffRelationTag({
  count,
  delta,
  hasDiff,
  unresolvedCount,
  primTitle,
  levelLabel,
  relatedScopeId,
  onClick,
  active,
  earliestUpdatedAt,
  cascadeEarliestTimestamp,
}: {
  count: number
  delta: number
  hasDiff: boolean
  unresolvedCount: number
  primTitle: string
  levelLabel?: string
  relatedScopeId: string
  onClick: () => void
  active?: boolean
  earliestUpdatedAt: string | null
  cascadeEarliestTimestamp?: string | null
}) {
  const { translate } = useTranslation()
  const colors = DIFF_SCOPE_COLORS[relatedScopeId] ?? {
    base: "bg-grey4/40 text-grey5",
    ring: "ring-grey5",
  }
  const freshnessDate = useStore((s) => s.freshnessDate)
  // Use the earliest of direct and cascade timestamps for freshness display
  const effectiveTs =
    cascadeEarliestTimestamp && earliestUpdatedAt
      ? cascadeEarliestTimestamp < earliestUpdatedAt
        ? cascadeEarliestTimestamp
        : earliestUpdatedAt
      : (cascadeEarliestTimestamp ?? earliestUpdatedAt)
  const freshness =
    count > 0 ? getRelationFreshness(effectiveTs, freshnessDate) : null
  const fStyles = freshness ? RELATION_FRESHNESS_STYLES[freshness] : null

  return (
    <div className="flex flex-col items-start gap-0.5">
      {/* Freshness hint above badge */}
      {freshness && count > 0 && (
        <span className={clsx("text-[9px] leading-tight", fStyles!.text)}>
          {freshness === "empty"
            ? translate("QMS/PIVOT/NO_DATE")
            : translate("QMS/PIVOT/OLDEST_LABEL", {
                ts: formatRelativeTime(effectiveTs!, translate),
              })}
        </span>
      )}
      <button
        onClick={onClick}
        className={clsx(
          "inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-xs font-medium transition-colors",
          colors.base,
          active && `ring-2 ring-offset-1 ring-offset-grey2 ${colors.ring}`,
          hasDiff && unresolvedCount > 0 && !active && "animate-pulse",
          fStyles && `${fStyles.bg} ${fStyles.border}`,
        )}
      >
        <span>{count}</span>
        {hasDiff && (
          <span
            className={clsx(
              "text-[10px] font-semibold",
              delta > 0
                ? "text-success"
                : delta < 0
                  ? "text-red1"
                  : "text-warning",
            )}
          >
            {delta > 0 ? `+${delta}` : delta < 0 ? `${delta}` : "\u00b1"}
          </span>
        )}
        <span className="opacity-70">{levelLabel ?? primTitle}</span>
        {unresolvedCount > 0 && (
          <span className="ml-0.5 flex h-4 w-4 items-center justify-center rounded-full bg-warning/30 text-[9px] font-bold text-warning">
            {unresolvedCount}
          </span>
        )}
        <span className="opacity-50">{active ? "\u2193" : "\u2192"}</span>
      </button>
    </div>
  )
}

// ---------------------------------------------------------------------------
// Expanded row for relation diffs - shows existing records + add/remove entries
// ---------------------------------------------------------------------------

function DiffRelationExpandedRow({
  relationData,
  isLoading,
  relationDiff,
  onAcceptEntry,
  onAcceptAll,
}: {
  relationData: RelationPreview | undefined
  isLoading: boolean
  relationDiff: RelationDiff | undefined
  onAcceptEntry: (targetId: string) => void
  onAcceptAll: () => void
}) {
  const { translate, currentLanguage } = useTranslation()
  const freshnessDate = useStore((s) => s.freshnessDate)

  if (isLoading) {
    return (
      <p className="py-2 text-xs text-grey5">
        {translate("QMS/PIVOT/LINKED_LOADING")}
      </p>
    )
  }

  const entries = relationDiff?.entries ?? []
  const unresolvedCount = entries.filter((e) => e.accepted === null).length

  // Build a set of target IDs that are being removed (to mark them in the existing list)
  const removingIds = new Set(
    entries.filter((e) => e.action === "remove").map((e) => e.targetId),
  )
  const addEntries = entries.filter((e) => e.action === "add")
  const removeEntries = entries.filter((e) => e.action === "remove")

  // Existing linked records (from relation preview), excluding ones being added (they show in diff section)
  const addTargetIds = new Set(addEntries.map((e) => e.targetId))
  const existingRecords = (relationData?.records ?? []).filter(
    (r) => !addTargetIds.has(r.id),
  )

  const prims =
    relationData?.scope_primitives?.filter(
      (p) => p.type !== "relation" && p.is_visible,
    ) ?? []

  return (
    <div className="flex flex-col gap-3">
      {/* Existing linked records */}
      {existingRecords.length > 0 && (
        <div className="flex flex-wrap items-start gap-2">
          {existingRecords.map((rec) => {
            const isBeingRemoved = removingIds.has(rec.id)
            const removeEntry = removeEntries.find((e) => e.targetId === rec.id)
            const cardFreshness = !isBeingRemoved
              ? getRelationFreshness(rec.relation_created_at, freshnessDate)
              : null
            const cardFStyles = cardFreshness
              ? RELATION_FRESHNESS_STYLES[cardFreshness]
              : null
            return (
              <div
                key={rec.id}
                className={clsx(
                  "min-w-[180px] max-w-[260px] rounded-lg border px-3 py-2 shadow-xs transition-all",
                  isBeingRemoved
                    ? "border-red1/40 bg-red1/5"
                    : cardFStyles
                      ? `${cardFStyles.bg} ${cardFStyles.border}`
                      : "border-grey4 bg-grey2",
                )}
              >
                <p
                  className={clsx(
                    "mb-1 truncate text-sm font-medium",
                    isBeingRemoved ? "text-red1 line-through" : "text-white",
                  )}
                >
                  {rec.name_value ??
                    rec.pk_value ??
                    translate("QMS/COMMON/UNNAMED")}
                </p>
                {prims.slice(0, 3).map((p) => {
                  const val = rec.values[p.name]
                  if (!val || val.value == null) return null
                  return (
                    <div
                      key={p.id}
                      className="flex items-baseline gap-1.5 text-xs"
                    >
                      <span className="shrink-0 text-grey5">
                        {getPrimitiveDisplayTitle(p, currentLanguage)}
                      </span>
                      <span
                        className={clsx(
                          "truncate",
                          isBeingRemoved
                            ? "text-grey5 line-through"
                            : "text-grey6",
                        )}
                      >
                        {val.value}
                      </span>
                    </div>
                  )
                })}
                {rec.relation_source && (
                  <p className="mt-1.5 border-t border-grey4/30 pt-1 text-[9px] leading-tight text-grey5/60">
                    {rec.relation_created_at
                      ? formatRelativeTime(rec.relation_created_at, translate) +
                        " "
                      : ""}
                    via{" "}
                    {rec.relation_source.replace(/\.(xlsx|csv|xls|tsv)$/i, "")}
                  </p>
                )}
                {isBeingRemoved && removeEntry && (
                  <div className="mt-1.5 flex items-center justify-between border-t border-red1/20 pt-1.5">
                    <div className="flex flex-col">
                      <span className="text-[10px] font-medium text-red1">
                        {translate("QMS/PIVOT/REMOVE")}
                      </span>
                      {removeEntry.source && (
                        <span className="text-[9px] text-grey5/70">
                          {removeEntry.timestamp
                            ? formatRelativeTime(
                                removeEntry.timestamp,
                                translate,
                              ) + " "
                            : ""}
                          via{" "}
                          {removeEntry.source.replace(
                            /\.(xlsx|csv|xls|tsv)$/i,
                            "",
                          )}
                        </span>
                      )}
                    </div>
                    {removeEntry.accepted === null ? (
                      <button
                        onClick={() => onAcceptEntry(removeEntry.targetId)}
                        className="rounded bg-success/20 px-1.5 py-0.5 text-[10px] font-medium text-success transition-colors hover:bg-success/30"
                        title={translate("QMS/PIVOT/ACCEPT_REMOVAL_TITLE")}
                      >
                        {translate("QMS/PIVOT/ACCEPT")}
                      </button>
                    ) : (
                      <span className="text-[10px] text-grey5">
                        {translate("QMS/PIVOT/ACCEPTED")}
                      </span>
                    )}
                  </div>
                )}
              </div>
            )
          })}
        </div>
      )}

      {/* New additions */}
      {addEntries.length > 0 && (
        <div>
          {existingRecords.length > 0 && (
            <p className="mb-1.5 text-[10px] font-semibold uppercase tracking-wide text-grey5">
              {translate("QMS/PIVOT/ADD_HEADING")}
            </p>
          )}
          <div className="flex flex-wrap items-start gap-2">
            {addEntries.map((entry) => (
              <div
                key={entry.targetId}
                className="min-w-[180px] max-w-[260px] rounded-lg border border-success/40 bg-success/5 px-3 py-2 shadow-xs"
              >
                <div className="flex items-start justify-between gap-2">
                  <div className="min-w-0">
                    <p className="truncate text-sm font-medium text-success">
                      {entry.targetNameValue ?? entry.targetPkValue}
                    </p>
                    {entry.targetNameValue && (
                      <p className="font-mono truncate text-[10px] text-grey5">
                        {entry.targetPkValue}
                      </p>
                    )}
                  </div>
                  <span className="shrink-0 text-xs font-bold text-success">
                    +
                  </span>
                </div>
                {entry.source && (
                  <p className="mt-1 text-[9px] text-grey5/70">
                    {entry.timestamp
                      ? formatRelativeTime(entry.timestamp, translate) + " "
                      : ""}
                    via {entry.source.replace(/\.(xlsx|csv|xls|tsv)$/i, "")}
                  </p>
                )}
                <div className="mt-1.5 flex justify-end border-t border-success/20 pt-1.5">
                  {entry.accepted === null ? (
                    <button
                      onClick={() => onAcceptEntry(entry.targetId)}
                      className="rounded bg-success/20 px-1.5 py-0.5 text-[10px] font-medium text-success transition-colors hover:bg-success/30"
                      title={translate("QMS/PIVOT/ACCEPT_ADDITION")}
                    >
                      {translate("QMS/PIVOT/ACCEPT")}
                    </button>
                  ) : (
                    <span className="text-[10px] text-grey5">
                      {translate("QMS/PIVOT/ACCEPTED")}
                    </span>
                  )}
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Accept all button */}
      {unresolvedCount > 1 && (
        <div className="flex justify-end">
          <button
            onClick={onAcceptAll}
            className="rounded bg-success/10 px-2 py-1 text-xs font-medium text-success transition-colors hover:bg-success/20 hover:text-success/80"
          >
            {translate("QMS/PIVOT/ACCEPT_ALL", { count: unresolvedCount })}
          </button>
        </div>
      )}

      {/* Empty state */}
      {entries.length === 0 && existingRecords.length === 0 && (
        <p className="py-2 text-xs text-grey5">
          {translate("QMS/PIVOT/NO_LINKED")}
        </p>
      )}
      {entries.length === 0 && existingRecords.length > 0 && (
        <p className="text-[10px] italic text-grey5">
          {translate("QMS/PIVOT/NO_LINK_CHANGES")}
        </p>
      )}
    </div>
  )
}

// ---------------------------------------------------------------------------
// Normal pivot row
// ---------------------------------------------------------------------------

function PivotRow({
  record,
  scope,
  visiblePrims,
  relationPrims,
  onRowClick,
  downloadMode: dlMode,
  selected,
  onToggleSelect,
  relationLevelFilters,
  relationLevelLabels,
  draggingOver,
}: RowProps) {
  const { currentLanguage, translate } = useTranslation()
  const [expandedPrimName, setExpandedPrimName] = useState<string | null>(null)
  const rollupState = useStore((s) => s.rollupState)

  // Build rollup prims grouped by source scope for inline rendering before relation columns
  const rollupByScope: Record<string, PrimitiveDef[]> = {}
  if (rollupState && rollupState.scopeId === scope.id) {
    for (const col of rollupState.columns) {
      const src = col.primitiveScopeId
      if (!rollupByScope[src]) rollupByScope[src] = []
      // Build a virtual PrimitiveDef for the rollup column
      if (!rollupByScope[src].some((p) => p.id === col.primitiveId)) {
        rollupByScope[src].push({
          id: col.primitiveId,
          scope_id: src,
          name: col.primitiveId,
          title: col.primitiveTitle ?? col.label,
          description: null,
          type: col.primitiveType ?? "string",
          is_pk: false,
          is_visible: true,
          display_order: 9999,
          config: { _rollupSourceScope: src },
          is_custom: false,
          related_scope_id: null,
          relation_cardinality: null,
        } as PrimitiveDef)
      }
    }
  }
  const transitiveCounts = useContext(TransitiveCountsCtx)
  const scopeOrderIndex = useContext(ScopeOrderCtx)

  // Determine if expanding a direct relation or a transitive one (prefixed "trans::")
  const isTransitiveExpand = expandedPrimName?.startsWith("trans::") ?? false
  const expandedTransitiveScopeId = isTransitiveExpand
    ? expandedPrimName!.slice(7)
    : null

  // Find the related scope for the expanded direct relation to pass the hierarchy filter
  const expandedRelPrim =
    !isTransitiveExpand && expandedPrimName
      ? relationPrims.find((p) => p.name === expandedPrimName)
      : null
  const expandedRelFilter =
    expandedRelPrim?.related_scope_id &&
    relationLevelFilters?.[expandedRelPrim.related_scope_id]
      ? relationLevelFilters[expandedRelPrim.related_scope_id]
      : undefined

  const { data: relationData, isLoading: relationLoading } = useQuery({
    queryKey: [
      "relation-preview",
      record.id,
      expandedPrimName,
      expandedRelFilter,
    ],
    queryFn: () =>
      api.getRelationPreview(record.id, expandedPrimName!, expandedRelFilter),
    enabled: !!expandedPrimName && !isTransitiveExpand,
  })

  // Transitive relation preview via graph traversal
  const pivotPkPrim = scope.primitives.find((p) => p.is_pk)
  const pivotPkValue = pivotPkPrim
    ? record.values[pivotPkPrim.name]?.value
    : null
  const { data: transitivePageData, isLoading: transitiveLoading } = useQuery({
    queryKey: [
      "filtered-records",
      expandedTransitiveScopeId,
      scope.id,
      pivotPkValue,
    ],
    queryFn: () =>
      api.listFilteredRecords(expandedTransitiveScopeId!, scope.id, [
        pivotPkValue!,
      ]),
    enabled:
      isTransitiveExpand && !!expandedTransitiveScopeId && !!pivotPkValue,
  })

  // Convert transitive RecordsPage into RelationPreview shape
  const transitiveRelationData: RelationPreview | undefined = transitivePageData
    ? {
        primitive_id: "",
        primitive_name: "",
        related_scope_id: expandedTransitiveScopeId!,
        records: transitivePageData.records.map((r) => {
          const rpk = transitivePageData.scope.primitives.find((p) => p.is_pk)
          const rname = transitivePageData.scope.primitives.find(
            (p) => !p.is_pk && p.type === "string" && p.is_visible,
          )
          const relTs = transitivePageData.relation_timestamps?.[r.id]
          return {
            id: r.id,
            scope_id: r.scope_id,
            pk_value: rpk ? (r.values[rpk.name]?.value ?? null) : null,
            name_value: rname ? (r.values[rname.name]?.value ?? null) : null,
            values: r.values,
            mapped_id: null,
            relation_id: null,
            relation_source: relTs?.source ?? null,
            relation_created_at: relTs?.created_at ?? null,
          }
        }),
        scope_primitives: transitivePageData.scope.primitives,
      }
    : undefined

  const effectiveRelationData = isTransitiveExpand
    ? transitiveRelationData
    : relationData
  const effectiveRelationLoading = isTransitiveExpand
    ? transitiveLoading
    : relationLoading

  const handleRelationClick = (primName: string) => {
    setExpandedPrimName((prev) => (prev === primName ? null : primName))
  }

  const rollupColCount = Object.values(rollupByScope).reduce(
    (sum, arr) => sum + arr.length,
    0,
  )
  const totalCols =
    visiblePrims.length +
    rollupColCount +
    relationPrims.filter((p) => p.is_visible).length +
    (transitiveCounts?.transitive_scopes.length ?? 0) +
    (dlMode ? 1 : 0)

  return (
    <>
      <tr
        className={clsx(
          "group cursor-pointer border-b transition-colors",
          expandedPrimName && "border-b-0 bg-grey3/30",
          dlMode && selected && "bg-yellow5/20/40",
        )}
        onClick={onRowClick}
      >
        {/* Select column - only in select/download mode */}
        {dlMode && (
          <td
            className={clsx(
              "sticky left-0 z-2 border-r border-grey4 px-2 py-2 text-center [box-shadow:2px_0_4px_-2px_rgba(0,0,0,0.08)]",
              selected
                ? "bg-yellow5/20/40 group-hover:bg-yellow5/30"
                : "bg-grey2 group-hover:bg-grey3",
            )}
          >
            <input
              type="checkbox"
              checked={!!selected}
              onChange={onToggleSelect}
              onClick={(e) => e.stopPropagation()}
              className="h-4 w-4 rounded border-grey4 text-yellow1 focus:ring-yellow1"
            />
          </td>
        )}
        {visiblePrims.map((p) => {
          let formulaError: string | null = null
          let formulaOverride: string | null = null
          if (p.type === "formula" && p.config?.formula) {
            const { value: computed, error } = resolveFormula(
              p.config.formula,
              record.values,
              translate,
            )
            formulaOverride = computed
            formulaError = error
          }
          const isSelectedRow = dlMode && selected
          const cellRollupScope = p.config?._rollupSourceScope as
            | string
            | undefined
          return (
            <td
              key={p.id}
              className={clsx(
                "border-r border-grey4/30 px-4 py-2 last:border-r-0",
                cellRollupScope
                  ? (ROLLUP_SCOPE_CELL[cellRollupScope] ?? "")
                  : "",
                p.is_pk
                  ? isSelectedRow
                    ? "group-hover:bg-yellow5/30"
                    : "group-hover:bg-grey3"
                  : isSelectedRow
                    ? "group-hover:bg-yellow5/30/50"
                    : "group-hover:bg-grey3/30",
                p.is_pk &&
                  dlMode &&
                  "sticky left-10 z-2 border-r border-grey4 [box-shadow:2px_0_4px_-2px_rgba(0,0,0,0.08)]",
                p.is_pk &&
                  !dlMode &&
                  "sticky left-0 z-2 border-r border-grey4 [box-shadow:2px_0_4px_-2px_rgba(0,0,0,0.08)]",
                p.is_pk && isSelectedRow
                  ? "bg-yellow5/20/40"
                  : p.is_pk && (draggingOver ? "bg-yellow5/30" : "bg-grey2"),
              )}
            >
              <PivotCell
                value={
                  record.values[p.name] ??
                  (useStore.getState().rollupState?.values?.[record.id]?.[p.id]
                    ?.value != null
                    ? {
                        value:
                          useStore.getState().rollupState!.values[record.id][
                            p.id
                          ].value,
                        source: "rollup",
                        updated_at:
                          useStore.getState().rollupState!.values[record.id][
                            p.id
                          ].updated_at ?? undefined,
                      }
                    : undefined)
                }
                prim={p}
                {...(p.type === "formula"
                  ? { displayOverride: formulaOverride, formulaError }
                  : {})}
              />
            </td>
          )
        })}
        {buildRelationSlots(
          relationPrims.filter((p) => p.is_visible),
          transitiveCounts?.transitive_scopes,
          scopeOrderIndex,
        ).flatMap((slot) => {
          if (slot.kind === "transitive") {
            const ts = slot.ts
            const count =
              transitiveCounts!.counts[record.id]?.[ts.scope_id] ?? 0
            const transitiveEarliestTs =
              transitiveCounts!.earliest_timestamps?.[record.id]?.[
                ts.scope_id
              ] ?? null
            return [
              <td
                key={`trans-${ts.scope_id}`}
                className={clsx(
                  "border-r border-grey4/30 px-4 py-2 last:border-r-0",
                  dlMode && selected
                    ? "group-hover:bg-yellow5/30/50"
                    : "group-hover:bg-grey3/30",
                )}
              >
                <div
                  className="flex flex-col gap-0.5"
                  onClick={(e) => e.stopPropagation()}
                >
                  <TransitiveCountTag
                    count={count}
                    scopeId={ts.scope_id}
                    scopeName={getScopeDisplayName(
                      { id: ts.scope_id, name: ts.scope_name },
                      currentLanguage,
                    )}
                    onClick={() => handleRelationClick(`trans::${ts.scope_id}`)}
                    active={expandedPrimName === `trans::${ts.scope_id}`}
                    earliestUpdatedAt={transitiveEarliestTs}
                  />
                </div>
              </td>,
            ]
          }
          const p = slot.prim
          {
            const scopeRollups =
              (p.related_scope_id ? rollupByScope[p.related_scope_id] : null) ??
              []
            const isSelectedRow = dlMode && selected
            const rollupCells = scopeRollups.map((rp) => {
              const cellRollupScope = rp.config?._rollupSourceScope as
                | string
                | undefined
              const rsVals = rollupState?.values
              return (
                <td
                  key={rp.id}
                  className={clsx(
                    "border-r border-grey4/30 px-4 py-2 last:border-r-0",
                    cellRollupScope
                      ? (ROLLUP_SCOPE_CELL[cellRollupScope] ?? "")
                      : "",
                    isSelectedRow
                      ? "group-hover:bg-yellow5/30/50"
                      : "group-hover:bg-grey3/30",
                  )}
                >
                  <PivotCell
                    value={
                      rsVals?.[record.id]?.[rp.id]?.value != null
                        ? ({
                            primitive_id: rp.id,
                            primitive_name: rp.name,
                            value: rsVals[record.id][rp.id].value,
                            source: "rollup",
                            confidence: null,
                            extraction_type: null,
                            needs_cleaning: false,
                            confirmed_by_user: false,
                            updated_at:
                              rsVals[record.id][rp.id].updated_at ?? undefined,
                          } satisfies PrimitiveValue)
                        : undefined
                    }
                    prim={rp}
                  />
                </td>
              )
            })

            const tag = record.relation_tags.find(
              (t) => t.primitive_name === p.name,
            )
            const mappedVal = record.mapping_values?.[p.name]
            return [
              ...rollupCells,
              <td
                key={p.id}
                className={clsx(
                  "border-r border-grey4/30 px-4 py-2 last:border-r-0",
                  dlMode && selected
                    ? "group-hover:bg-yellow5/30/50"
                    : "group-hover:bg-grey3/30",
                )}
              >
                <div
                  className="flex items-center gap-1.5"
                  onClick={(e) => e.stopPropagation()}
                >
                  <RelationTag
                    tag={tag}
                    primTitle={getPrimitiveDisplayTitle(p, currentLanguage)}
                    levelLabel={
                      p.related_scope_id
                        ? relationLevelLabels?.[p.related_scope_id]
                        : undefined
                    }
                    relatedScopeId={p.related_scope_id ?? ""}
                    onClick={() => handleRelationClick(p.name)}
                    active={expandedPrimName === p.name}
                  />
                  {mappedVal && (
                    <span
                      className="font-mono max-w-[80px] truncate rounded bg-purple1/10 px-1 py-0.5 text-[10px] text-purple1"
                      title={mappedVal}
                    >
                      {mappedVal}
                    </span>
                  )}
                </div>
              </td>,
            ]
          }
        })}
      </tr>
      {expandedPrimName && (
        <tr className="border-b bg-grey3/50">
          <td colSpan={totalCols} className="p-0">
            <div className="sticky left-0 w-fit max-w-[calc(100vw-10rem)] overflow-x-auto px-4 py-3">
              <RelationExpandedRow
                data={effectiveRelationData}
                isLoading={effectiveRelationLoading}
                parentRecordId={record.id}
              />
            </div>
          </td>
        </tr>
      )}
    </>
  )
}

// ---------------------------------------------------------------------------
// Expanded row: renders related records as inline cards
// ---------------------------------------------------------------------------

function RelationExpandedRow({
  data,
  isLoading,
  parentRecordId, // eslint-disable-line @typescript-eslint/no-unused-vars
}: {
  data: RelationPreview | undefined
  isLoading: boolean
  parentRecordId: string
}) {
  const { translate, currentLanguage } = useTranslation()
  const rollupState = useStore((s) => s.rollupState)
  const isDiffMode = useStore(
    (s) =>
      s.actionState !== null && Object.keys(s.actionState.diffs).length > 0,
  )
  const freshnessDate = useStore((s) => s.freshnessDate)
  const [recomputing, setRecomputing] = useState(false)

  const relatedScopeId = data?.related_scope_id ?? ""
  // Check if rollup columns exist for this related scope
  const hasRollup =
    !!rollupState &&
    rollupState.columns.some((c) => c.primitiveScopeId === relatedScopeId)

  // Determine included record IDs - if not yet initialised for this scope, all records are included
  const includedIds = rollupState?.includedRecordIds[relatedScopeId]

  // Lazy-initialise: first time we see relation data with a rollup, populate includedRecordIds
  const initializedRef = useRef(false)
  useEffect(() => {
    if (hasRollup && data && !includedIds && !initializedRef.current) {
      initializedRef.current = true
      useStore.getState().setRollupIncludedRecords(
        relatedScopeId,
        data.records.map((r) => r.id),
      )
    }
  }, [hasRollup, data, includedIds, relatedScopeId])

  const allRecordIds = data?.records.map((r) => r.id) ?? []
  const effectiveIncluded = new Set(includedIds ?? allRecordIds)

  const handleCardClick = async (recordId: string) => {
    if (!hasRollup || !rollupState) return

    // Toggle in store
    useStore.getState().toggleRollupRecord(relatedScopeId, recordId)

    // Compute new included set after toggle
    const newIncluded = effectiveIncluded.has(recordId)
      ? [...effectiveIncluded].filter((id) => id !== recordId)
      : [...effectiveIncluded, recordId]

    // Recompute rollup values via backend
    setRecomputing(true)
    try {
      const result = await api.computeRollup(rollupState.scopeId, {
        record_ids: Object.keys(rollupState.values),
        columns: rollupState.columns.map((c) => ({
          primitive_id: c.primitiveId,
          primitive_scope_id: c.primitiveScopeId,
          aggregation: c.aggregation,
          label: c.label,
        })),
        source_record_ids: { [relatedScopeId]: newIncluded },
      })

      // Update rollup values in store
      const values: Record<
        string,
        Record<string, { value: string | null; updated_at?: string | null }>
      > = {}
      for (const v of result.values) {
        if (!values[v.record_id]) values[v.record_id] = {}
        values[v.record_id][v.primitive_id] = {
          value: v.value,
          updated_at: v.updated_at ?? null,
        }
      }
      useStore.getState().setRollupState({
        ...rollupState,
        values,
        includedRecordIds: {
          ...rollupState.includedRecordIds,
          [relatedScopeId]: newIncluded,
        },
      })
    } catch (err) {
      console.error("Rollup recompute failed:", err)
      // Revert toggle on failure
      useStore.getState().toggleRollupRecord(relatedScopeId, recordId)
    } finally {
      setRecomputing(false)
    }
  }

  if (isLoading) {
    return (
      <p className="py-2 text-xs text-grey5">
        {translate("QMS/PIVOT/LINKED_LOADING")}
      </p>
    )
  }
  if (!data || data.records.length === 0) {
    return (
      <p className="py-2 text-xs text-grey5">
        {translate("QMS/PIVOT/NO_LINKED")}
      </p>
    )
  }

  const prims = data.scope_primitives.filter(
    (p) => p.type !== "relation" && p.is_visible,
  )

  return (
    <div className="flex flex-wrap items-start gap-3">
      {data.records.map((rec) => {
        const isIncluded = hasRollup && effectiveIncluded.has(rec.id)
        const cardBorder = isIncluded
          ? (ROLLUP_SCOPE_CARD_BORDER[relatedScopeId] ?? "border-grey4")
          : "border-grey4"
        const cardBg = isIncluded
          ? (ROLLUP_SCOPE_CARD_BG[relatedScopeId] ?? "bg-grey2")
          : "bg-grey2"
        const cardHover = hasRollup
          ? (ROLLUP_SCOPE_CARD_HOVER[relatedScopeId] ?? "")
          : ""
        const cardFreshness = isDiffMode
          ? getRelationFreshness(rec.relation_created_at, freshnessDate)
          : null
        const cardFStyles = cardFreshness
          ? RELATION_FRESHNESS_STYLES[cardFreshness]
          : null

        return (
          <div
            key={rec.id}
            onClick={() => handleCardClick(rec.id)}
            className={clsx(
              "min-w-[180px] max-w-[260px] rounded-lg border px-3 py-2 shadow-xs transition-all duration-150",
              cardFStyles
                ? `${cardFStyles.bg} ${cardFStyles.border}`
                : `${cardBorder} ${cardBg}`,
              hasRollup && "cursor-pointer select-none",
              cardHover,
              recomputing && "pointer-events-none opacity-60",
            )}
          >
            <p className="mb-1.5 truncate text-sm font-medium text-white">
              {rec.name_value ??
                rec.pk_value ??
                translate("QMS/COMMON/UNNAMED")}
            </p>
            <div className="flex flex-col gap-0.5">
              {prims.map((p) => {
                const val = rec.values[p.name]
                if (!val || val.value == null) return null
                return (
                  <div
                    key={p.id}
                    className="flex items-baseline gap-1.5 text-xs"
                  >
                    <span className="shrink-0 text-grey5">
                      {getPrimitiveDisplayTitle(p, currentLanguage)}
                    </span>
                    <span className="truncate text-grey6">{val.value}</span>
                  </div>
                )
              })}
            </div>
            {isDiffMode && rec.relation_source && (
              <p className="mt-1.5 border-t border-grey4/30 pt-1 text-[9px] leading-tight text-grey5/60">
                {rec.relation_created_at
                  ? formatRelativeTime(rec.relation_created_at, translate) + " "
                  : ""}
                via {rec.relation_source.replace(/\.(xlsx|csv|xls|tsv)$/i, "")}
              </p>
            )}
          </div>
        )
      })}
      <button
        className="ml-1 cursor-default self-center whitespace-nowrap rounded-lg border border-grey4/30 px-3 py-1.5 text-xs font-medium text-grey5"
        disabled
      >
        {translate("QMS/PIVOT/EDIT_AGGREGATION")}
      </button>
    </div>
  )
}

// ---------------------------------------------------------------------------
// Settings panel
// ---------------------------------------------------------------------------

function SortableChip({
  prim,
  onToggle,
}: {
  prim: PrimitiveDef
  onToggle: (p: PrimitiveDef) => void
}) {
  const { currentLanguage, translate } = useTranslation()
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id: prim.id })

  const style: React.CSSProperties = {
    transform: CSS.Transform.toString(transform),
    transition,
    zIndex: isDragging ? 50 : undefined,
  }

  return (
    <div
      ref={setNodeRef}
      style={style}
      className={clsx(
        "flex select-none items-center gap-1.5 rounded-lg border px-2.5 py-1.5 text-xs",
        isDragging
          ? "scale-105 border-warning bg-warning/20 shadow-lg ring-2 ring-warning/30"
          : "border-warning/30 bg-grey2 hover:border-warning hover:shadow-xs",
      )}
    >
      {/* Drag handle */}
      <span
        {...attributes}
        {...listeners}
        className="mr-0.5 cursor-grab text-warning hover:text-warning active:cursor-grabbing"
        title={translate("QMS/PIVOT/COL_DRAG_TOOLTIP")}
      >
        ⠿
      </span>
      <label className="flex cursor-pointer items-center gap-1.5">
        <input
          type="checkbox"
          checked={prim.is_visible}
          onChange={() => onToggle(prim)}
          className="accent-yellow1"
        />
        <PrimitiveTypeIcon type={prim.type} />
        <span
          className={prim.is_visible ? "text-white" : "text-grey5 line-through"}
        >
          {getPrimitiveDisplayTitle(prim, currentLanguage)}
        </span>
      </label>
    </div>
  )
}

// eslint-disable-next-line @typescript-eslint/no-unused-vars
function SettingsPanel({
  scope,
  scopeId, // eslint-disable-line @typescript-eslint/no-unused-vars
  onToggle,
  onReorder,
  onClose,
}: {
  scope: Scope
  scopeId: string
  onToggle: (p: PrimitiveDef) => void
  onReorder: (orderedIds: string[]) => void
  onClose: () => void
}) {
  const { translate } = useTranslation()
  const nonPk = scope.primitives.filter((p) => !p.is_pk && !p.config?.hidden)
  const nonPkIds = nonPk.map((p) => p.id)

  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 5 } }),
  )

  const handleDragEnd = (event: DragEndEvent) => {
    const { active, over } = event
    if (!over || active.id === over.id) return

    const oldIndex = nonPkIds.indexOf(active.id as string)
    const newIndex = nonPkIds.indexOf(over.id as string)
    const reorderedNonPk = arrayMove(nonPkIds, oldIndex, newIndex)

    // Reconstruct full ID list preserving PK positions
    const allIds: string[] = []
    let nonPkIdx = 0
    for (const p of scope.primitives) {
      if (p.is_pk) {
        allIds.push(p.id)
      } else {
        allIds.push(reorderedNonPk[nonPkIdx++])
      }
    }
    onReorder(allIds)
  }

  return (
    <div className="border-b border-warning/30 bg-warning/10 px-6 py-3">
      <div className="mb-2 flex items-center justify-between">
        <div className="flex items-center gap-2">
          <h3 className="text-sm font-semibold text-warning">
            {translate("QMS/PIVOT/COL_VISIBILITY_HEADING")}
          </h3>
          <span className="text-[10px] text-warning/70">
            {translate("QMS/PIVOT/COL_DRAG_TOOLTIP")}
          </span>
        </div>
        <button
          onClick={onClose}
          className="text-xs text-warning hover:text-warning"
        >
          {translate("QMS/COMMON/CLOSE")}
        </button>
      </div>
      <DndContext
        sensors={sensors}
        collisionDetection={closestCenter}
        onDragEnd={handleDragEnd}
      >
        <SortableContext
          items={nonPkIds}
          strategy={horizontalListSortingStrategy}
        >
          <div className="flex flex-wrap gap-2">
            {nonPk.map((p) => (
              <SortableChip key={p.id} prim={p} onToggle={onToggle} />
            ))}
          </div>
        </SortableContext>
      </DndContext>
    </div>
  )
}

// ---------------------------------------------------------------------------
// Utilities
// ---------------------------------------------------------------------------

function PrimitiveTypeIcon({ type }: { type: string }) {
  const icons: Record<string, string> = {
    string: "Aa",
    number: "#",
    date: "D",
    relation: "↗",
    mapping: "⇄",
    formula: "fx",
  }
  return (
    <span className="font-mono text-[10px] font-semibold text-grey5">
      {icons[type] ?? type}
    </span>
  )
}

function LoadingState() {
  const { translate } = useTranslation()
  return (
    <div className="flex h-64 items-center justify-center text-grey5">
      {translate("QMS/COMMON/LOADING")}
    </div>
  )
}

function ErrorState({ message }: { message: string }) {
  const { translate } = useTranslation()
  return (
    <div className="flex h-64 items-center justify-center text-red1">
      {translate("QMS/COMMON/ERROR_PREFIX", { message })}
    </div>
  )
}

// ---------------------------------------------------------------------------
// Formula utilities
// ---------------------------------------------------------------------------

/** Replace bare field references with human-readable titles. */
function formatFormulaDisplay(
  formula: string,
  primitiveTitleMap?: Record<string, string>,
): string {
  if (!primitiveTitleMap) return formula
  return formula.replace(
    /[a-zA-Z_]\w*/g,
    (field) => primitiveTitleMap[field] ?? field,
  )
}

/**
 * Client-side formula evaluator for simple arithmetic.
 * Returns { value, error } - one is always null.
 */
function resolveFormula(
  formula: string,
  recordValues: Record<string, import("../../types").PrimitiveValue>,
  translate: TranslateFn,
): { value: string | null; error: string | null } {
  if (!formula)
    return { value: null, error: translate("QMS/FORMULA/ERR_EMPTY") }

  // Substitute bare field references with numeric values
  let expr = formula
  const refPattern = /[a-zA-Z_]\w*/g
  let match: RegExpExecArray | null
  while ((match = refPattern.exec(formula)) !== null) {
    const fieldName = match[0]
    const pv = recordValues[fieldName]
    const raw = pv?.value
    if (raw == null || raw === "") {
      return {
        value: null,
        error: translate("QMS/FORMULA/ERR_MISSING_VALUE", {
          ident: fieldName,
        }),
      }
    }
    const num = Number(raw)
    if (isNaN(num)) {
      return {
        value: null,
        error: translate("QMS/FORMULA/ERR_NON_NUMERIC_VALUE", {
          ident: fieldName,
          raw: String(raw),
        }),
      }
    }
    expr = expr.replace(match[0], String(num))
  }

  // Validate: only numbers, operators, parens, whitespace, decimal points
  if (!/^[\d\s+\-*/().]+$/.test(expr)) {
    return { value: null, error: translate("QMS/FORMULA/ERR_INVALID_CHARS") }
  }

  try {
    // Safe: expr contains only numbers and arithmetic operators after substitution
    const result = Function(`"use strict"; return (${expr})`)()
    if (typeof result !== "number" || !isFinite(result)) {
      return {
        value: null,
        error: translate("QMS/FORMULA/ERR_INVALID_RESULT"),
      }
    }
    // Format: strip trailing .0 for clean integers
    const formatted = Number.isInteger(result)
      ? String(result)
      : String(parseFloat(result.toPrecision(10)))
    return { value: formatted, error: null }
  } catch {
    return { value: null, error: translate("QMS/FORMULA/ERR_EVAL_FAILED") }
  }
}

function FormulaColumnHeader({
  prim,
  errorCount,
  totalCount,
  filterActive,
  onToggleFilter,
  primitiveTitleMap,
}: {
  prim: import("../../types").PrimitiveDef
  errorCount: number
  totalCount: number
  filterActive: boolean
  onToggleFilter: () => void
  primitiveTitleMap: Record<string, string>
}) {
  const { translate, currentLanguage } = useTranslation()
  const [open, setOpen] = useState(false)
  const ref = useRef<HTMLDivElement>(null)

  useEffect(() => {
    if (!open) return
    const handleClick = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false)
    }
    document.addEventListener("mousedown", handleClick)
    return () => document.removeEventListener("mousedown", handleClick)
  }, [open])

  const formula = prim.config?.formula
  const display = formula
    ? formatFormulaDisplay(formula, primitiveTitleMap)
    : translate("QMS/PIVOT/FORMULA_NONE")

  return (
    <div className="relative" ref={ref}>
      <button
        onClick={() => setOpen((v) => !v)}
        className="flex items-center gap-1.5 transition-colors hover:text-yellow1"
      >
        <PrimitiveTypeIcon type={prim.type} />
        <span>{getPrimitiveDisplayTitle(prim, currentLanguage)}</span>
        {errorCount > 0 && (
          <span
            className={clsx(
              "rounded-full px-1.5 py-0.5 text-[10px] font-medium leading-none",
              filterActive
                ? "bg-warning text-white"
                : "bg-warning/20 text-warning",
            )}
            title={translate("QMS/PIVOT/FORMULA_ERROR_COUNT_TITLE", {
              errorCount,
              totalCount,
            })}
          >
            {errorCount}
          </span>
        )}
        <svg className="h-3 w-3 text-grey5" viewBox="0 0 12 12" fill="none">
          <path
            d="M3 5l3 3 3-3"
            stroke="currentColor"
            strokeWidth="1.5"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
        </svg>
      </button>
      {open && (
        <div className="absolute left-0 top-full z-20 mt-1 min-w-[220px] rounded-lg border border-grey4 bg-grey2 px-3 py-2.5 shadow-lg">
          <p className="mb-1 text-[10px] uppercase tracking-wide text-grey5">
            {translate("QMS/PIVOT/FORMULA_HEADING")}
          </p>
          <p className="font-mono rounded bg-grey3 px-2 py-1 text-xs text-grey6">
            {display}
          </p>
          {errorCount > 0 && (
            <>
              <div className="my-2 border-t border-grey4/50" />
              <button
                onClick={() => {
                  onToggleFilter()
                  setOpen(false)
                }}
                className={clsx(
                  "flex w-full items-center gap-2 rounded px-2 py-1.5 text-left text-xs transition-colors",
                  filterActive
                    ? "bg-warning/10 font-medium text-warning"
                    : "text-grey5 hover:bg-grey3",
                )}
              >
                <span className="text-warning">⚠</span>
                {filterActive
                  ? translate("QMS/PIVOT/FORMULA_ERRORS", { count: errorCount })
                  : translate("QMS/PIVOT/FORMULA_SHOW_ERRORS", {
                      count: errorCount,
                    })}
              </button>
            </>
          )}
        </div>
      )}
    </div>
  )
}
